// @ts-nocheck
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import api, { getReport, persistEvent, resolveTurnToken, ReportDocument } from './apiService';
import type { GetReportResult, TransformedResponse } from './apiService';
import { CHANGE_PASSWORD_SUCCESS_USER_MESSAGE, SESSION_EXPIRED_USER_MESSAGE, terminalAuthRefreshError } from './apiErrors';
import {
    OIDC_SESSION_MARKER,
    clearKeycloakSession,
    completeKeycloakLogin,
    connectReturnPath,
    currentKeycloakIdToken,
    endKeycloakSession,
    isKeycloakMode,
    isOidcCallbackPath,
    refreshKeycloakTokens,
} from './auth/oidc';
import {
    clearImpersonation,
    isImpersonatingTab,
    isImpersonationToken,
    readAuthToken,
    readImpersonationMeta,
    readUserData,
    storeImpersonation,
    storeUserData,
} from './auth/impersonationTab';
import { SIGNED_OUT_OF_BARK_NOTICE, signInRefusalMessage } from './components/auth/signInErrors';
import ReferenceGraph from './ReferenceGraph'; // Import the new component
import GlobalStyles from './components/ui/GlobalStyles';
import ErrorBoundary from './components/ui/ErrorBoundary';
import MarkdownRenderer from './components/ui/MarkdownRenderer';
import ConfirmDialog from './components/ui/ConfirmDialog';
import { useToast } from './components/ui/Toast';
import LoginPage from './components/auth/LoginPage';
import ForgotPasswordModal from './components/auth/ForgotPasswordModal';
import ChangePasswordModal from './components/auth/ChangePasswordModal';
import AdminPage from './components/admin/AdminPage';
import ReferenceFlowView from './components/references/ReferenceFlowView';
import ReferenceTreeView from './components/references/ReferenceTreeView';
import { RegulatorySettings } from './components/settings/RegulatorySettingsPanel';
import { transformRegulatorySettings } from './components/settings/settingsUtils';
import { ReferenceModal, FullReferenceModal, ServiceSelectionModal } from './components/modals/Modals';
import GeminiChatArea from './components/chat/GeminiChatArea';
import SavedReportView from './components/reports/SavedReportView';
import { reportOpenFailureMessage } from './components/reports/reportOpenFeedback';
import { ContextMenu, MenuItem } from './components/ui/ContextMenu';
import ReactMarkdown from 'react-markdown';
import LogicalChainView from './components/logic/LogicalChainView';
import { ResearchWorkspace, WorkspaceTabs } from './components/workspace';
import ReportView from './components/workspace/ReportView';
import WorkspaceDetailsOverlay, { WorkspaceDetailsType } from './components/workspace/WorkspaceDetailsOverlay';
import UnifiedFindingsPanel, { FindingsToggleButton } from './components/workspace/UnifiedFindingsPanel';
import EditableProjectTitle from './components/workspace/EditableProjectTitle';
// OnlineSupportPanel / OnlineSupportToggleButton no longer mounted —
// online support is now a synthetic group inside UnifiedFindingsPanel
// (BAR-39 UI consolidation).
import type { ReferenceDetails } from './components/workspace/types';
import { SyncStatusIndicator } from './components/projects';
import ShareProjectDialog from './components/projects/ShareProjectDialog';
import UserAccountMenu from './components/sidebar/UserAccountMenu';
import { useProjectStore } from './stores/projectStore';
import { useProcessViewerStore } from './stores/processViewerStore';
import { useQuickSearchStore } from './stores/quickSearchStore';
import ResearchMissionPanel from './components/ResearchMissionPanel';
import PlanTrainTrack from './components/workspace/PlanTrainTrack';
import WorkspaceBrief from './components/workspace/WorkspaceBrief';
import { useResearchProgress, mapServerProgress } from './hooks/useResearchProgress';
import {
    Boxes,
    FileBarChart,
    Landmark,
    Lock,
    MessageSquare,
    PanelRightClose,
    PanelRightOpen,
    Pencil,
    Plug,
    Trash2,
} from 'lucide-react';
import { INSTITUTION_ENABLED, INVENTORY_ENABLED } from './router/ShellRoutes';
import type { ResearchMission, ResearchProgressState, HandoffPackage } from './types';
import { BuildArtifactsView } from './components/process';
import PlanGraphView from './components/process/PlanGraphView';
import { dictToPlanGraph } from './utils/planGraphData';
import { useWorkspaceDraftMode } from './hooks/useWorkspaceDraftMode';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    useActiveView,
    useNavigateToView,
    type NavigateToView,
} from './router/useUrlNavigation';
import { AuthProvider, useAuthActions } from './context/AuthContext';
import { setLookupUsesChatFree } from './lookupApi';
import { hasNoWebAccess, isChatLocked, isMcpOnlyCustomer, unlockModuleDialogCopy, usesChatFree as userUsesChatFree } from './utils/moduleAccess';
import type { ImpersonationMeta } from './context/AuthContext';
import { SessionProvider } from './context/SessionContext';
import { ShellUiProvider } from './context/ShellUiContext';
import AppRoutes from './router/AppRoutes';
import ShellRoutes from './router/ShellRoutes';
import { clearClassificationMutations } from './classification/bridge';
import { useClassificationAccess } from './classification/useClassificationAccess';
import { ROUTE_PATTERNS } from './router/routes';
import { computeIsWelcomeState } from './utils/welcomeState';
import { nextAvailableChatId } from './utils/chatId';
import { formatCountOrFallback } from './utils/plural';
import { describeToolOutcome } from './utils/toolOutcome';
import PhaseStepper from './components/process/PhaseStepper';
import type { BuildPhase, WorkspaceMode } from './types/modeling';
import {
    runLogoutTransition,
    shouldClearAuthAfterRefreshFailure,
} from './authSessionCleanup';

// --- UI COMPONENTS --- //

// BAR-53: shared default so the useState initializer and the project-switch
// reset (resetEphemeralProjectState) stay in sync.
const DEFAULT_TOKEN_USAGE = Object.freeze({ inputTokens: 0, outputTokens: 0, totalTokens: 0 });

function isLegacyReportLinkId(reportId: string): boolean {
    // Current saved-report producers use one of these two prefixes.
    // Anything else predates or falls outside the stored-link ID contract.
    return !reportId.startsWith('report_') && !reportId.startsWith('inventory_report_');
}

function MainApplicationUI({
    activeChat, allChats, onNewQuery, onSelectQuery, onSendMessage, isLoading, setIsLoading, streamingStatus, resetEphemeralProjectState, streamingResponse, setStreamingResponse, streamingThinking, setStreamingThinking, streamingActivityLog, activeToolCall, onCancelWorkflow, onLogout,
    regulatorySettings, onSettingsChange, onServiceSelect,
    currentReport, activeView,
    setActiveView, setSelectedReportId,
    onReportLinkClick,
    isSettingsCollapsed, setIsSettingsCollapsed, onCloseReport, onSwitchToReport, userData, isCanvasMode, setIsCanvasMode,
    openReports, activeReportId, onDeleteChat, onRenameChat, resetChatState,
    highlightedNodeId, setHighlightedNodeId, isSourcePanelOpen, setIsSourcePanelOpen, sourcePanelContent, setSourcePanelContent,
    researchMission,
    onReconnectToStream,
    onRestoreResearch,
    hasReconnectedSession,
    reLayoutTrigger,
    tokenUsage,
    researchProgress,
    getResearchProgress,
    activeResearchTrackingId,
    handoffPackage,
    setHandoffPackage,
    authToken,
    pendingQuestion,
    onAnswerQuestion,
    onOpenWorkspaceDetails,
    onOpenChangePassword,
    onOpenAdmin,
}: any) {
    const [isSummarizing, setIsSummarizing] = useState(false);
    // Two-phase project open (#89) — gate canvas render until both the
    // authoritative workspace snapshot is loaded AND the subscription
    // catchup stream has completed. Prevents the user from seeing a
    // mid-replay flicker when opening a project with prior history.
    const [isProjectOpening, setIsProjectOpening] = useState(false);
    // Which phase of the open we're in: false while loadProject is still
    // fetching the project (the click's only feedback is the overlay), true
    // once it has resolved and we're only waiting on subscription catchup.
    // Drives the safety-net cap below.
    const [openLoadResolved, setOpenLoadResolved] = useState(false);
    const [catchupCompleteForProject, setCatchupCompleteForProject] = useState<string | null>(null);
    // UX-STANDARD S-03 (failure ≠ empty): a project open that doesn't end in
    // the workspace must say so. Toast is the outcome surface for a library
    // click (there is no in-place error region for a row that never opened).
    const { showToast: showShellToast } = useToast();
    // Home Chat Agent prefill state (bark-specific)
    const [prefillChatInput, setPrefillChatInput] = useState<string | null>(null);
    // When true, GeminiChatArea will auto-send `prefillChatInput` once on mount
    // (new-project flow opts in; the existing home-agent action only pre-fills).
    const [autoSendPrefill, setAutoSendPrefill] = useState(false);
    // BAR-40: top-level navigation is URL-driven. `routeView`,
    // `newProjectType`, `projectsTypeFilter` are a READ-ONLY projection of the
    // current route (useActiveView) — used here only for chrome nav
    // highlighting + layout gating, NOT for view dispatch (the route tree in
    // ShellRoutes selects the view). `navigateToView` is the single navigation
    // writer (also exposed on ShellUiContext for the view routes).
    // `routeProjectId` is the project id parsed from the URL, used by the
    // deep-link hydration effect below to load the project on a cold open.
    const {
        view: routeView,
        projectId: routeProjectId,
        projectsFilter: projectsTypeFilter,
        newProjectType,
    } = useActiveView();
    const projectOpenGeneration = useRef(0);
    const projectOpenRoute = useRef(routeView);
    projectOpenRoute.current = routeView;
    const routeNavigateToView = useNavigateToView();
    /* the institution profile is master data with its own plain route —
       the rail navigates to it directly (Kawi, 5 Aug: "in the bark menu") */
    const shellNavigate = useNavigate();
    const shellLocation = useLocation();
    const navigateToView = useCallback<NavigateToView>((view, options) => {
        // Saved reports intentionally open over the current route so report
        // links do not rewrite the user's URL. Any explicit shell navigation
        // closes that overlay before handing control back to the URL router.
        if (activeView === 'report' && currentReport) {
            setSelectedReportId(null);
            setIsCanvasMode(false);
            setActiveView('welcome');
        }
        routeNavigateToView(view, options);
    }, [
        activeView,
        currentReport,
        routeNavigateToView,
        setActiveView,
        setIsCanvasMode,
        setSelectedReportId,
    ]);

    // Collaboration state
    const [showShareDialog, setShowShareDialog] = useState(false);
    // BAR-135 D11: presence carries the TARGET identity during impersonation
    // (token-derived, server-side); an optional server-set `imp` flag marks a
    // collaborator who is being impersonated so it's visible to other viewers.
    const [onlineUsers, setOnlineUsers] = useState<Array<{user_id: string; email: string; imp?: boolean}>>([]);

    // Report tab findings panel state
    const [isReportFindingsPanelOpen, setIsReportFindingsPanelOpen] = useState(false);
    const [selectedReportFindingId, setSelectedReportFindingId] = useState<string | null>(null);
    const [selectedReportOnlineSupportId, setSelectedReportOnlineSupportId] = useState<string | null>(null);
    const [isReportDetailOpen, setIsReportDetailOpen] = useState(false);
    // Tracks the previous detail-open state so we only clear the report
    // selection on a genuine open→closed transition — not the transient
    // `false` the findings panel emits on first open before its detail
    // state catches up (which was de-highlighting the just-clicked chip).
    const prevReportDetailOpenRef = useRef(false);
    const [reportSplitPercent, setReportSplitPercent] = useState(50);
    const [reportListWidth, setReportListWidth] = useState(220);
    // Online-support panel state removed — online support is now a
    // synthetic group inside UnifiedFindingsPanel; OnlineSupportFocusModal
    // handles detail/explore-deeper view.

    // Project store for workspace management
    const {
        activeProject,
        isLoading: projectLoading,
        loadProject,
        loadProjectList,
        projectList,
        updateWorkspace,
        updateNotes,
        clearActiveProject,
        getChatHistory,
        getDisplayHistory,
        applyWorkspaceOperations,
        // Multi-workspace state
        activeWorkspace,
        workspaceMode,
        barkWorkspace,
        pendingWorkspaceSwitch,
        setActiveWorkspace,
        setWorkspaceMode,
        acceptWorkspaceSwitch,
        rejectWorkspaceSwitch,
        productionState,
        setActiveArtifact,
        // Dict-based workspace from event-sourced storage
        dictWorkspace,
        // True while a user-initiated workspace/node drill is fetching (#89)
        workspaceLoading,
    } = useProjectStore();

    // Task 10 (nav-entry-project-list): the Projects view type filter
    // ('all' | 'research' | 'build') is derived from the URL `?type=`
    // search param via useActiveView above (BAR-40). 'all' (the
    // "Project Library" entry) is the default and omitted from the URL.

    // Left-nav handler for the two type-specific entries. Drops the user
    // straight into an EMPTY workspace (research → /research/new, build →
    // /build/new) via a topic-less blank draft — no intermediate picker. The
    // backend project is created lazily on the user's first chat message (see
    // the draft state machine in useWorkspaceDraftMode). The user's own
    // existing projects remain reachable via the "Project Library" left-nav
    // entry; the chat-first new-project page still lives at /projects/new for
    // the home-page Research/Build cards.
    const openTypedProjects = (type: 'research' | 'build') => {
        beginBlankDraft(type);
    };

    // BAR-82: read the disabled-modules list from the login response so the
    // sidebar and home-action handler can gate Build for restricted companies
    // (e.g. Noba). Backend is the authoritative gate — this just hides the UI.
    //
    // BAR-1351: taken from auth context rather than re-derived from the
    // `userData` prop, so the sidebar and the landing cards cannot disagree
    // about who this customer is. They read the same state either way, but
    // `isMcpOnlyCustomer` now branches copy on this predicate and two
    // definitions of it is one too many.
    const { isModuleDisabled, usesChatFree } = useAuthActions();

    // BAR-53: reset MainApplicationUI-local panels that the App-level
    // resetEphemeralProjectState() can't reach — cached report references (read
    // by the global ref-click window handler), the legacy evidence drawer, and
    // the report-view finding / online-support selections. Called on project
    // switch AND on delete-active so neither carries the previous project's UI.
    const resetMainAppLocalPanels = () => {
        referencesRef.current = [];
        setSelectedEvidence(null);
        setSelectedReportFindingId(null);
        setSelectedReportOnlineSupportId(null);
        setIsReportDetailOpen(false);
        setIsReportFindingsPanelOpen(false);
    };

    // Shared project selection handler (used by ProjectLibraryView and LandingPage)
    const handleSelectProject = async (projectId: string) => {
        const generation = ++projectOpenGeneration.current;
        const startedOnClassification = projectOpenRoute.current === 'classification';
        const supersededByClassification = () => {
            if (startedOnClassification || projectOpenRoute.current !== 'classification') return false;
            // Only this open owns its overlay; a later explicit open may own it now.
            if (projectOpenGeneration.current === generation) setIsProjectOpening(false);
            return true;
        };
        console.log('Project selected:', projectId);
        const cleared = await clearActiveProject();
        if (supersededByClassification()) return;
        if (!cleared) {
            // The still-open project has unsaved changes whose save failed; the
            // store keeps it open so its sync error stays actionable — but that
            // indicator lives inside the workspace, and the user is looking at
            // the library. Say so here instead of a dead click (S-03).
            const stuck = useProjectStore.getState().activeProject;
            showShellToast({
                variant: 'warning',
                message: `Couldn't switch projects: "${stuck?.name || 'the open project'}" has unsaved changes that failed to save.`,
                action: stuck ? {
                    label: 'Open it',
                    onClick: () => navigateToView(
                        stuck.project_type === 'build' ? 'build-artifacts' : 'workspace',
                        { projectId: stuck.project_id },
                    ),
                } : undefined,
            });
            return;
        }
        // Clear stale plan reload timer from previous project (#89)
        if ((window as any).__planReloadTimer) {
            clearTimeout((window as any).__planReloadTimer);
            (window as any).__planReloadTimer = null;
        }
        // Reset all streaming state from previous project
        setIsLoading(false);
        if (typeof resetEphemeralProjectState === 'function') resetEphemeralProjectState();
        resetMainAppLocalPanels();
        resetChatState(projectId);
        // Two-phase open (#89): show a loading overlay until both the
        // snapshot is loaded and subscription catchup is complete.
        setIsProjectOpening(true);
        setOpenLoadResolved(false);
        setCatchupCompleteForProject(null);
        if (supersededByClassification()) return;
        const success = await loadProject(projectId, userData?.email || '');
        if (supersededByClassification()) return;
        if (!success) {
            // The one silent exit that is legitimate: the store moved on to a
            // DIFFERENT project because the user clicked another one mid-load
            // (loadProject's stale-load bail). That newer click owns the
            // overlay and the outcome — don't touch either.
            const live = useProjectStore.getState();
            const supersededByAnotherProject = !!live.activeProject
                && live.activeProject.project_id !== projectId;
            if (supersededByAnotherProject) return;
        }
        setOpenLoadResolved(true);
        if (success) {
            const project = useProjectStore.getState().activeProject;
            const isBuildProject = project?.project_type === 'build';
            // routeView based on project type; workspaceMode already restored by loadProject.
            // BAR-40: navigation writes the project into the URL (/research/:id
            // or /build/:id). The idempotency guard in navigateToView makes this
            // a no-op when we arrived here via a deep link already on that path.
            navigateToView(isBuildProject ? 'build-artifacts' : 'workspace', { projectId: project?.project_id });
            // Load latest handoff for build projects
            if (isBuildProject && authToken) {
                try {
                    const handoff = await api.getLatestHandoff(projectId, authToken);
                    if (supersededByClassification()) return;
                    if (handoff) {
                        // Legacy chat-chip context only — the HandoffChecklist /
                        // HandoffPackagePanel UI was retired with the
                        // HandoffPackage path (requirements now ride the
                        // first-class SpecRequirement panel).
                        setHandoffPackage(handoff as HandoffPackage);
                        console.log('[App] Loaded handoff package for build project:', handoff.handoff_id);
                    }
                } catch (e) {
                    console.warn('[App] Failed to load handoff:', e);
                }
            }
            const activeSession = await api.checkActiveSession(projectId);
            if (supersededByClassification()) return;
            await onRestoreResearch(projectId);
            if (supersededByClassification()) return;
            if (activeSession.active) {
                console.log('[App] Active agent session found, calling onReconnectToStream:', activeSession);
                onReconnectToStream(projectId);
            } else {
                console.log('[App] No active session, project data already loaded from Firestore');
                setIsLoading(false);
            }
        } else {
            // Load failed — drop the opening overlay now rather than leaving the
            // user staring at "Opening project…" until the safety net fires.
            setIsProjectOpening(false);
            // S-03 (failure ≠ empty): a failed open must be visible and
            // retryable — before this, the overlay just vanished and the user
            // was left on the library with no message.
            const live = useProjectStore.getState();
            const failedName = projectList.find((p: any) => p.project_id === projectId)?.name;
            const reason = live.syncStatus === 'error' && live.syncError ? ` (${live.syncError})` : '';
            showShellToast({
                variant: 'error',
                message: `Couldn't open ${failedName ? `"${failedName}"` : 'the project'}${reason}.`,
                action: {
                    label: 'Retry',
                    onClick: () => { void handleSelectProject(projectId); },
                },
            });
        }
    };

    // BAR-40: deep-link / refresh / back-forward hydration. When the URL
    // names a project (/research/:id or /build/:id) that isn't the one
    // currently loaded, open it. handleSelectProject loads the project and
    // (via navigateToView) settles on the canonical path. The ref guard
    // stops React 18 StrictMode's double-invoke — and any re-render — from
    // double-loading; handleSelectProject itself reads live store state.
    const hydratedProjectRef = useRef<string | null>(null);
    // BAR-1528: with every web module off, report-library refuses the project
    // (403 `web_access_not_in_plan`). The route gate already sends a project URL
    // home with the unlock dialog; this keeps the doomed load from firing first.
    const webAccessOff = hasNoWebAccess(isModuleDisabled);
    useEffect(() => {
        // Classification resolves its own scoped context; opening the legacy
        // workspace here would replace this URL with Research or Build.
        if (!routeProjectId || routeView === 'classification') {
            hydratedProjectRef.current = null;
            return;
        }
        if (webAccessOff) return;
        if (useProjectStore.getState().activeProject?.project_id === routeProjectId) {
            // Already loaded (e.g. we just navigated here after opening it).
            hydratedProjectRef.current = routeProjectId;
            return;
        }
        if (hydratedProjectRef.current === routeProjectId) return;
        // Wait out an in-flight open. isProjectOpening is in the dep array
        // below so this effect re-fires when the flag clears — otherwise a
        // deep-link arriving during a brief opening window (fast back-forward)
        // would be parsed but silently never loaded.
        if (isProjectOpening) return;
        hydratedProjectRef.current = routeProjectId;
        void handleSelectProject(routeProjectId);
        // handleSelectProject is intentionally omitted from deps: it's
        // recreated each render and reads live store/auth state at call time.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [routeView, routeProjectId, isProjectOpening, webAccessOff]);

    // Close the project-open loading overlay once ALL of (a) the authoritative
    // workspace snapshot is present (dictWorkspace.workspace_id is set for
    // the active project), (b) subscription catchup has completed for that
    // project, and (c) we have actually navigated into the project interior.
    //
    // (c) is load-bearing for large projects: handleSelectProject only flips
    // routeView to the interior AFTER `await loadProject` fully returns (which
    // includes the slow spawned-workspace re-hydration + event load), but the
    // collab-WS `catchup_complete` often arrives earlier. Without the routeView
    // gate the overlay closes mid-load while routeView is still 'landing'/
    // 'projects', so the user is dumped back on the home/library screen for a
    // few seconds before loadProject finishes and navigates them inward — the
    // "it disappeared, then sent me inside" flash. Holding the overlay until
    // routeView is a project view keeps feedback continuous; the destination
    // view then owns its own loading state (ProcessViewer "Loading model…",
    // PlanLoadingPlaceholder, etc.). The safety net below still bounds this.
    const isOnProjectView = routeView === 'workspace'
        || routeView === 'logic-chain'
        || routeView === 'build-artifacts';
    useEffect(() => {
        if (!isProjectOpening) return;
        const pid = activeProject?.project_id;
        if (!pid) return;
        const snapshotReady = !!dictWorkspace?.workspace_id;
        const catchupReady = catchupCompleteForProject === pid;
        if (snapshotReady && catchupReady && isOnProjectView) {
            setIsProjectOpening(false);
        }
    }, [isProjectOpening, activeProject?.project_id, dictWorkspace?.workspace_id, catchupCompleteForProject, isOnProjectView]);

    // Workspace draft-mode state machine (research + build). Owns pendingDraft
    // state + refs, the workspace-exit and project-id-change reset effects,
    // plus the beginBlankDraft / beginDraft / trySendDraft actions used by the
    // sidebar buttons, the Build box-press, and the chat-panel onSendMessage
    // handlers below. Extracted to a separate file
    // (hooks/useWorkspaceDraftMode.ts) so the new code gets full TypeScript
    // coverage despite App.tsx's file-level @ts-nocheck.
    const draftMode = useWorkspaceDraftMode({
        routeView,
        activeProjectId: activeProject?.project_id,
        userData,
        // BAR-40: draft mode navigates instead of setting local state. The
        // begin* actions clear the active project first, so navigateToView
        // with no active project resolves to the /…/new draft sentinel
        // ('build-artifacts' → /build/new, 'workspace' → /research/new).
        navigateToView,
        setWorkspaceMode,
        setPrefillChatInput,
        setAutoSendPrefill,
        clearActiveProject,
        resetChatState: (projectId: string) => resetChatState(projectId),
    });
    const { pendingDraft, draftCreateError, draftRetryAttempt, beginBlankDraft, beginDraft, trySendDraft } = draftMode;
    // True while the user is composing a research / build blank draft with no
    // project yet — drives the empty-canvas + chat render branches below.
    const researchDraftActive = !activeProject && !!pendingDraft && pendingDraft.type === 'research' && routeView === 'workspace';
    const buildDraftActive = !activeProject && !!pendingDraft && pendingDraft.type === 'build' && routeView === 'build-artifacts' && workspaceMode === 'plan';

    // Project-less research/build dead-end → go to the start page. After a hard
    // refresh of a bare /research or /build URL, the in-memory draft is gone, so
    // the route has no project (in the URL or active), nothing loading, and no
    // draft — there is nothing to show. A sent prompt would have stamped the URL
    // with a real project id (see useWorkspaceDraftMode lazy-create), so reaching
    // here means no running work is at stake; navigate home rather than rebuild
    // an empty canvas. (The previous behaviour re-seeded a blank draft here,
    // which fought the draft-cleanup effect in useWorkspaceDraftMode and
    // infinite-looped → React #185; the build branch rendered nothing, so Build
    // went blank.) Navigating away makes this condition false, so it fires once.
    // URLs that DO name a project hydrate via the deep-link effect above and
    // never reach here.
    useEffect(() => {
        const onWorkspaceRoute = routeView === 'workspace' || routeView === 'build-artifacts';
        if (
            onWorkspaceRoute
            && !routeProjectId
            && !activeProject
            && !projectLoading
            && !pendingDraft
        ) {
            navigateToView('landing');
        }
    }, [routeView, routeProjectId, activeProject, projectLoading, pendingDraft, navigateToView]);

    // Safety net: if catchup doesn't arrive within a reasonable window
    // (e.g. backend subscription delayed), close the overlay anyway so
    // the user isn't stuck looking at a spinner.
    //
    // Two-phase cap (S-01): the 8s catchup cap only arms once loadProject has
    // resolved (openLoadResolved). Before that the overlay is the only
    // feedback for the click, and cutting it at 8s while a slow (large /
    // shared) project is still loading drops the user back on the library
    // with nothing shown, then teleports them inward seconds later. While
    // loading, the load's own resolution ends the overlay (success →
    // navigate; failure → toast in handleSelectProject); the long cap only
    // guards a fetch that never returns.
    useEffect(() => {
        if (!isProjectOpening) return;
        const capMs = openLoadResolved ? 8000 : 60000;
        const t = setTimeout(() => setIsProjectOpening(false), capMs);
        return () => clearTimeout(t);
    }, [isProjectOpening, openLoadResolved]);

    // Collaboration subscription WebSocket — connects to /ws/subscribe/{project_id}
    // Receives streaming events from other users and presence updates
    const subscriptionWsRef = useRef<WebSocket | null>(null);

    useEffect(() => {
        if (!activeProject?.project_id) {
            // Clean up when project is deselected
            if (subscriptionWsRef.current) {
                subscriptionWsRef.current.close();
                subscriptionWsRef.current = null;
            }
            setOnlineUsers([]);
            return;
        }

        const projectId = activeProject.project_id;
        const orchestratorUrl = api.getWorkflowOrchestratorUrl();
        if (!orchestratorUrl) return;

        // Convert HTTP URL to WebSocket URL
        const wsUrl = orchestratorUrl.replace(/^http/, 'ws');
        // BAR-750: the token MUST ride in the Sec-WebSocket-Protocol handshake as
        // ["bearer", <token>] (BAR-270) — the backend reads it ONLY from the
        // subprotocols and never from a `?token=` query param. Passing it in the
        // query made this socket anonymous, which `reject_anonymous` now closes
        // with 4001; that silently killed presence AND `catchup_complete`, the
        // only signal that closes the project-open overlay (so every project open
        // hung on the 8s safety-net timeout).
        const fullUrl = `${wsUrl}/ws/subscribe/${projectId}`;

        console.log('[Collab] Connecting subscription WS for project:', projectId);
        const ws = authToken
            ? new WebSocket(fullUrl, ['bearer', authToken])
            : new WebSocket(fullUrl);
        subscriptionWsRef.current = ws;

        ws.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data);

                if (data.type === 'presence_update') {
                    setOnlineUsers(data.data?.online_users || []);
                }

                if (data.type === 'catchup_complete') {
                    setOnlineUsers(data.data?.online_users || []);
                    console.log('[Collab] Catchup complete, online users:', data.data?.online_users?.length || 0);
                    // Mark catchup done for the active project so the open-phase
                    // loading overlay can close (#89).
                    setCatchupCompleteForProject(projectId);
                }

                if (data.type === 'remote_message_start') {
                    const senderName = data.data?.user_name || 'Someone';
                    const query = data.data?.query || '';
                    console.log(`[Collab] ${senderName} sent: "${query.substring(0, 50)}..."`);
                }

                // BARK workspace switch broadcast — update indicator for non-senders
                // (non-sender's viewport stays put, only the BARK tree icon moves)
                if (data.type === 'workspace_switched' || (data.type === 'workspace_update' && data.data?.operation?.type === 'workspace_switch')) {
                    const target = data.data?.workspace || data.data?.operation?.data?.to;
                    const targetNodeName = data.data?.target_node_name || data.data?.operation?.data?.target_node_name || '';
                    if (target) {
                        const validModes = ['plan', 'institution_context', 'research', 'production', 'output_package'];
                        const targetMode = validModes.includes(target) ? target : 'research';
                        console.log('[Collab] BARK workspace moved to:', targetMode, targetNodeName ? `(${targetNodeName})` : '');
                        // BAR-53 (F5): scope to this collab subscription's project so a
                        // late broadcast can't move the indicator on a switched-to project.
                        useProjectStore.getState().setBarkWorkspace(targetMode as any, projectId);
                        useProjectStore.getState().setBarkWorkspaceName(targetNodeName, projectId);
                    }
                }

                // When a remote message completes, reload project state
                if (data.type === 'complete') {
                    console.log('[Collab] Remote message complete — reloading project events');
                    const { reloadProjectEvents, loadWorkspaceState } = useProjectStore.getState();
                    reloadProjectEvents(projectId, userData?.email || '');
                    loadWorkspaceState(projectId, userData?.email || '');
                }
            } catch (err) {
                // Ignore parse errors
            }
        };

        ws.onclose = () => {
            console.log('[Collab] Subscription WS closed for project:', projectId);
        };

        return () => {
            if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
                ws.close();
            }
        };
    }, [activeProject?.project_id, authToken]);

    // Project view mode (research = workspace, report = report content)
    const [projectViewMode, setProjectViewMode] = useState<'research' | 'report'>('research');

    // Linked report for project (fetched when switching to report view)
    const [linkedReport, setLinkedReport] = useState<ReportDocument | null>(null);
    const [linkedReportLoading, setLinkedReportLoading] = useState(false);

    // Report edit mode and markdown content
    const [reportEditMode, setReportEditMode] = useState(false);
    const [editedMarkdown, setEditedMarkdown] = useState('');

    // Evidence detail panel state (for report view) — legacy, not currently rendered
    const [selectedEvidence, setSelectedEvidence] = useState<Record<string, any> | null>(null);

    // Helper to parse the detail field which may be a Python-style dict string
    const parseDetailText = (detail: string | undefined): string | undefined => {
        if (!detail) return undefined;
        // If it looks like a Python dict string, try to extract the text
        if (detail.startsWith('{') && detail.includes("'text':")) {
            try {
                // Convert Python-style single quotes to JSON double quotes
                const jsonStr = detail
                    .replace(/'/g, '"')
                    .replace(/None/g, 'null')
                    .replace(/True/g, 'true')
                    .replace(/False/g, 'false');
                const parsed = JSON.parse(jsonStr);
                return parsed.text || detail;
            } catch {
                // If parsing fails, return the raw detail
                return detail;
            }
        }
        return detail;
    };

    // Store references for window handler access
    const referencesRef = useRef<any[]>([]);

    // Set up window handler for reference clicks (used with dangerouslySetInnerHTML)
    useEffect(() => {
        (window as any).handleProjectReportRefClick = (refId: string, event: Event) => {
            event.preventDefault();
            const ref = referencesRef.current?.find(r => r.id === refId);
            if (ref) {
                setSelectedEvidence({
                    id: ref.id || refId,
                    title: ref.title || refId,
                    type: 'reference',
                    summary: ref.summary,
                    detail: parseDetailText(ref.detail) || ref.summary,
                    source: ref.document,
                    sourceReference: ref.document_reference,
                });
            }
        };

        return () => {
            delete (window as any).handleProjectReportRefClick;
        };
    }, []);

    // Helper to render content with clickable reference marks using dangerouslySetInnerHTML
    const renderContentWithMarks = (content: string, references: any[] | undefined): { __html: string } | string => {
        if (!content) return '';

        // Update the ref for window handler access
        if (references) {
            referencesRef.current = references;
        }

        // First, unescape HTML entities in case the marks are HTML-escaped
        let processedContent = content
            .replace(/&lt;/g, '<')
            .replace(/&gt;/g, '>')
            .replace(/&quot;/g, '"')
            .replace(/&#39;/g, "'")
            .replace(/&amp;/g, '&');

        // Replace mark tags with clickable links
        const htmlContent = processedContent.replace(
            /<mark ref_id=['"]([^'"]*)['"]\s*>(.*?)<\/mark>/g,
            `<a href="#" onclick="window.handleProjectReportRefClick('$1', event); return false;" class="text-accent hover:text-accent-hover hover:underline cursor-pointer">$2</a>`
        );

        return { __html: htmlContent };
    };

    // Convert structured content to markdown
    const convertReportToMarkdown = (report: ReportDocument): string => {
        if (!report?.report_content) return '';
        const lines: string[] = [];

        // Title
        if (report.report_content.title) {
            lines.push(`# ${report.report_content.title}`);
            lines.push('');
        }

        // Executive Summary
        if (report.report_content.executive_summary) {
            lines.push('## Executive Summary');
            lines.push('');
            lines.push(report.report_content.executive_summary);
            lines.push('');
        }

        // Sections
        report.report_content.sections?.forEach((section: any) => {
            if (section.type === 'heading') {
                const prefix = '#'.repeat(Math.min((section.level || 1) + 1, 6));
                lines.push(`${prefix} ${section.content}`);
                lines.push('');
            } else if (section.type === 'paragraph') {
                lines.push(section.content);
                lines.push('');
            } else if (section.type === 'list') {
                const items = section.content as string[];
                items?.forEach((item, i) => {
                    lines.push(section.ordered ? `${i + 1}. ${item}` : `- ${item}`);
                });
                lines.push('');
            } else if (section.type === 'table' && section.content) {
                const tableData = section.content as { headers?: string[]; rows?: string[][] };
                if (tableData.headers) {
                    lines.push('| ' + tableData.headers.join(' | ') + ' |');
                    lines.push('| ' + tableData.headers.map(() => '---').join(' | ') + ' |');
                }
                tableData.rows?.forEach(row => {
                    lines.push('| ' + row.join(' | ') + ' |');
                });
                lines.push('');
            }
        });

        // References
        if (report.references && report.references.length > 0) {
            lines.push('## References');
            lines.push('');
            report.references.forEach((ref: any, index: number) => {
                lines.push(`[${index + 1}] **${ref.title}**`);
                if (ref.document && ref.document_reference) {
                    lines.push(`   ${ref.document} - ${ref.document_reference}`);
                }
                if (ref.summary) {
                    lines.push(`   ${ref.summary}`);
                }
                lines.push('');
            });
        }

        return lines.join('\n');
    };

    // Export to PDF (uses browser print)
    const exportToPDF = () => {
        const printContent = document.getElementById('report-content');
        if (!printContent) return;

        const printWindow = window.open('', '_blank');
        if (!printWindow) return;

        printWindow.document.write(`
            <!DOCTYPE html>
            <html>
            <head>
                <title>${linkedReport?.report_content?.title || 'Report'}</title>
                <style>
                    /* Print-preview popup — opens in its own document, so the
                       app's CSS-variable bridge is not available. Hex values
                       below mirror DESIGN.md tokens at the time of writing
                       (--bark-border, --bark-bg-warm, --bark-blue-light,
                       --bark-surface-sunken). If you change those tokens,
                       update these literals too. */
                    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
                    h1 { font-size: 24px; margin-bottom: 16px; }
                    h2 { font-size: 20px; margin-top: 24px; margin-bottom: 12px; }
                    h3 { font-size: 16px; margin-top: 16px; margin-bottom: 8px; }
                    p { font-size: 14px; line-height: 1.6; margin-bottom: 12px; }
                    ul, ol { font-size: 14px; line-height: 1.6; padding-left: 24px; }
                    table { width: 100%; border-collapse: collapse; margin: 16px 0; font-size: 13px; }
                    th, td { border: 1px solid #e2e8f0; padding: 8px; text-align: left; }
                    th { background: #f0f4f6; }
                    .executive-summary { background: #eff6ff; padding: 16px; border-radius: 8px; margin-bottom: 24px; }
                    .reference { background: #f5f8f9; padding: 12px; border-radius: 6px; margin-bottom: 8px; }
                </style>
            </head>
            <body>${printContent.innerHTML}</body>
            </html>
        `);
        printWindow.document.close();
        printWindow.print();
    };

    // Export to Word (downloads as .doc)
    const exportToWord = () => {
        const printContent = document.getElementById('report-content');
        if (!printContent) return;

        const html = `
            <html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word' xmlns='http://www.w3.org/TR/REC-html40'>
            <head><meta charset='utf-8'><title>${linkedReport?.report_content?.title || 'Report'}</title></head>
            <body>${printContent.innerHTML}</body>
            </html>
        `;

        const blob = new Blob(['\ufeff', html], { type: 'application/msword' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${linkedReport?.report_content?.title || 'report'}.doc`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    // Initialize markdown when entering edit mode
    useEffect(() => {
        if (reportEditMode && linkedReport) {
            setEditedMarkdown(convertReportToMarkdown(linkedReport));
        }
    }, [reportEditMode, linkedReport]);

    // Fetch linked report when project has a linked report (needed for references in both views)
    useEffect(() => {
        // BAR-40: drop a late response if the effect re-runs (project switched)
        // or unmounts while the fetch is in flight. With URL deep-links /
        // back-forward, fast A->B switches are easy to trigger, and without this
        // guard project A's report could overwrite project B's linkedReport.
        let cancelled = false;
        const fetchLinkedReport = async () => {
            if (activeProject?.linked_report_id && userData?.email) {
                // Don't refetch if we already have the same report
                if (linkedReport?.report_id === activeProject.linked_report_id) return;

                setLinkedReportLoading(true);
                const result = await getReport(activeProject.linked_report_id, userData.email);
                if (cancelled) return;
                if (result.success && result.report) {
                    setLinkedReport(result.report);
                } else {
                    setLinkedReport(null);
                }
                setLinkedReportLoading(false);
            } else {
                // BAR-53: project has no linked report — clear any report (and
                // its references) left over from the previous project. The old
                // clear-effect only fired when activeProject became null, so an
                // A->B switch where B has no linked report leaked A's report.
                setLinkedReport(null);
            }
        };

        fetchLinkedReport();
        return () => { cancelled = true; };
    }, [activeProject?.linked_report_id, userData?.email]);

    // Clear linked report when project changes
    useEffect(() => {
        if (!activeProject) {
            setLinkedReport(null);
        }
    }, [activeProject?.project_id]);

    // Default to report view when project has a linked report
    useEffect(() => {
        if (activeProject?.linked_report_id) {
            setProjectViewMode('report');
        } else {
            setProjectViewMode('research');
        }
    }, [activeProject?.project_id]);

    // Canvas width state for resizable divider (default 65% of available space)
    // Chat is secondary (35%), canvas is primary (65%)
    const [canvasWidthPercent, setCanvasWidthPercent] = useState(() => {
        const saved = localStorage.getItem('bark_canvas_width');
        return saved ? parseFloat(saved) : 65;
    });
    const [isResizingCanvas, setIsResizingCanvas] = useState(false);

    // Chat hide/show toggle
    const [isChatHidden, setIsChatHidden] = useState(false);

    // Auto-hide chat when finding detail opens, show when it closes
    useEffect(() => {
        const onOpen = () => setIsChatHidden(true);
        const onClose = () => setIsChatHidden(false);
        window.addEventListener('finding-detail-open', onOpen);
        window.addEventListener('finding-detail-close', onClose);
        return () => {
            window.removeEventListener('finding-detail-open', onOpen);
            window.removeEventListener('finding-detail-close', onClose);
        };
    }, []);

    // Context menu state for chat renaming
    const [showChatContextMenu, setShowChatContextMenu] = useState(false);
    const [contextMenuPosition, setContextMenuPosition] = useState({ x: 0, y: 0 });
    const [selectedChatForMenu, setSelectedChatForMenu] = useState<number | null>(null);
    const [renamingChatId, setRenamingChatId] = useState<number | null>(null);
    const [tempChatTitle, setTempChatTitle] = useState('');

    // Note: Removed buggy useEffect that set routeView to 'report' (invalid value)
    // The routeView state only supports: 'landing' | 'logic-chain' | 'workspace' | 'new-project' | 'projects' | 'quick-research' | 'settings'

    // Chat context menu handlers
    const handleChatContextMenu = (e: React.MouseEvent, chatId: number) => {
        e.preventDefault();
        setContextMenuPosition({ x: e.clientX, y: e.clientY });
        setSelectedChatForMenu(chatId);
        setShowChatContextMenu(true);
    };
    
    const startChatRename = (chatId: number) => {
        const chat = allChats[chatId];
        if (chat) {
            setTempChatTitle(chat.title);
            setRenamingChatId(chatId);
        }
        setShowChatContextMenu(false);
    };
    
    const saveChatRename = () => {
        if (renamingChatId && tempChatTitle.trim()) {
            onRenameChat(renamingChatId, tempChatTitle.trim());
        }
        setRenamingChatId(null);
        setTempChatTitle('');
    };
    
    const cancelChatRename = () => {
        setRenamingChatId(null);
        setTempChatTitle('');
    };
    
    // Canvas resize handlers
    const handleCanvasResizeStart = () => {
        setIsResizingCanvas(true);
    };
    
    const handleCanvasResize = (e: MouseEvent) => {
        if (!isResizingCanvas || !mainPanelRef.current) return;

        const mainPanel = mainPanelRef.current;
        const rect = mainPanel.getBoundingClientRect();
        const mouseX = e.clientX - rect.left;
        // Canvas is on left, chat on right: canvas width = mouse position from left
        const newCanvasWidthPercent = Math.max(40, Math.min(80, (mouseX / rect.width) * 100));

        setCanvasWidthPercent(newCanvasWidthPercent);
    };
    
    const handleCanvasResizeEnd = () => {
        if (isResizingCanvas) {
            setIsResizingCanvas(false);
            localStorage.setItem('bark_canvas_width', canvasWidthPercent.toString());
        }
    };
    
    useEffect(() => {
        if (isResizingCanvas) {
            window.addEventListener('mousemove', handleCanvasResize);
            window.addEventListener('mouseup', handleCanvasResizeEnd);
            return () => {
                window.removeEventListener('mousemove', handleCanvasResize);
                window.removeEventListener('mouseup', handleCanvasResizeEnd);
            };
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isResizingCanvas]);
    
    const [isDocumentPopupOpen, setIsDocumentPopupOpen] = useState(false);
    const [documentPopupUrl, setDocumentPopupUrl] = useState<string>('');
    
    const [sidebarWidth, setSidebarWidth] = useState(240);
    const [chatPanelWidth, setChatPanelWidth] = useState(400);
    const [isSidebarHovered, setIsSidebarHovered] = useState(false);
    // Reporting is a teaser entry — clickable, but opens an "Unlock" dialog
    // instead of navigating (the feature isn't enabled for this account).
    const [unlockModule, setUnlockModule] = useState<string | null>(null);
    const [isAccountMenuOpen, setIsAccountMenuOpen] = useState(false);
    
    // Sidebar dimensions
    const collapsedSidebarWidth = 60;
    const expandedSidebarWidth = 240;
    const currentSidebarWidth = (isSidebarHovered || showChatContextMenu || renamingChatId !== null || isAccountMenuOpen) ? expandedSidebarWidth : collapsedSidebarWidth;
    
    // Use props for report and UI state (passed from parent App component)
    const sidebarRef = useRef<HTMLDivElement>(null);
    const mainPanelRef = useRef<HTMLDivElement>(null);

    const startResizeSidebar = (e: React.MouseEvent) => {
        e.preventDefault();
        if (!sidebarRef.current) return;
        const startX = e.clientX; const startWidth = sidebarRef.current.offsetWidth;
        const onMouseMove = (moveEvent: MouseEvent) => { const newWidth = startWidth + moveEvent.clientX - startX; if (newWidth >= 220 && newWidth <= 500) setSidebarWidth(newWidth); };
        const onMouseUp = () => { window.removeEventListener('mousemove', onMouseMove); window.removeEventListener('mouseup', onMouseUp); };
        window.addEventListener('mousemove', onMouseMove); window.addEventListener('mouseup', onMouseUp);
    };

    const startResizeChatPanel = (e: React.MouseEvent) => {
        e.preventDefault();
        const startX = e.clientX;
        const startWidth = chatPanelWidth;
        const onMouseMove = (moveEvent: MouseEvent) => {
            const newWidth = startWidth - (moveEvent.clientX - startX);
            if (newWidth >= 300 && newWidth <= 600) {
                setChatPanelWidth(newWidth);
            }
        };
        const onMouseUp = () => {
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('mouseup', onMouseUp);
        };
        window.addEventListener('mousemove', onMouseMove);
        window.addEventListener('mouseup', onMouseUp);
    };

    // PURE EVENT-BASED ARCHITECTURE: Use Firestore events subcollection
    // Events are stored chronologically and converted to display format by getDisplayHistory()
    // This replaces the old chat_history[].metadata.activityLog nested structure
    const projectHistory = activeProject
        ? getDisplayHistory().filter(msg => {
            // Filter out WebSocket timeout errors from old sessions
            if (msg.sender === 'bark' && msg.message) {
                const isTimeoutError = msg.message.includes('WebSocket timeout after 30 minutes');
                if (isTimeoutError) {
                    console.log('[Chat History] Filtered out old timeout error from events');
                    return false;
                }
            }
            return true;
        })
        : null;

    // HYBRID APPROACH: WebSocket for real-time streaming + Firestore for persistence
    // During streaming: Display growing local message with real-time activityLog
    // After streaming: Display ONLY from Firestore (saved once at completion)
    const chatHistory = activeProject
        ? (() => {
            const localHistory = activeChat?.chatHistory || [];
            // Find the streaming message in local state (has _isStreaming: true)
            const streamingMsg = localHistory.find((msg: any) => msg._isStreaming === true);

            if (streamingMsg && isLoading) {
                // While streaming: show Firestore history + the live streaming message.
                // Filter out phantom bark messages from current turn (no response content yet).
                const filtered = projectHistory!.filter((msg, i) => {
                    if (msg.sender === 'bark' && !msg.message) {
                        const hasUserMsgAfter = projectHistory!.slice(i + 1).some(m => m.sender === 'user');
                        if (!hasUserMsgAfter) return false;
                    }
                    return true;
                });
                return [...filtered, streamingMsg];
            }
            // After streaming completes: show ONLY Firestore
            return projectHistory!;
        })()
        : (activeChat?.chatHistory || []);
    const report = currentReport || activeChat?.report || { title: '', content: '' };

    const handleSummarize = async () => { 
        setIsSummarizing(true);
        // Placeholder for future implementation
        await new Promise(resolve => setTimeout(resolve, 1000));
        setIsSummarizing(false);
    };

    // Now using Gemini-style interface for all states

    // Always show sidebar
    const showSidebar = true;
    const navItemBase = `w-full flex items-center p-2 rounded-md border text-sm transition-colors duration-150 ${isSidebarHovered ? 'gap-3' : 'justify-center'}`;
    const navItemActive = "bg-primary text-on-primary border-primary font-semibold shadow-bark-xs hover:bg-primary-hover hover:border-primary-hover";
    const navItemIdle = "border-transparent text-fg-secondary hover:bg-primary hover:text-on-primary hover:border-primary";
    const navItemDisabled = `${navItemBase} border-transparent text-fg-tertiary cursor-not-allowed`;
    const navItemLocked = `${navItemBase} border-border-default bg-surface-sunken text-fg-tertiary hover:bg-bg-warm hover:text-fg-secondary`;
    const navSoonBadge = "text-[9px] font-semibold bg-bg-warm text-fg-tertiary px-1 py-px rounded-full uppercase tracking-wider";
    const navLockedBadge = "text-[9px] font-semibold bg-surface text-fg-tertiary px-1 py-px rounded-full uppercase tracking-wider";
    const researchLocked = isModuleDisabled('regulatory_research');
    const buildLocked = isModuleDisabled('build_artifact');
    const projectLibraryLocked = isModuleDisabled('project_library');
    const reportingLocked = isModuleDisabled('reporting');
    // Retained command IDs contain no payloads/tokens; discard only this app's
    // retry metadata when the signed-in shell identity changes or signs out.
    useEffect(() => () => clearClassificationMutations(), [userData?.uid, userData?.id, userData?.email]);
    const classificationAccess = useClassificationAccess();
    const [classificationOpenVersion, reopenClassification] = React.useReducer(value => value + 1, 0);
    const classificationLocked = !classificationAccess.enabled;
    // BAR-1528: Chat belongs to no single module, so it locks on the
    // server's own condition: with every web module off the orchestrator
    // refuses its turns (403 `web_access_not_in_plan`). Same as the route gate.
    const chatLocked = isChatLocked(isModuleDisabled, usesChatFree);
    // BAR-1351: a customer who bought BARK over MCP has every web module off
    // and `mcp_access` on. Telling them to "contact your BARK representative"
    // is wrong twice over: self-serve has no representative, and the product
    // they paid for runs in Claude, not here. Branch the copy instead.
    const mcpOnlyCustomer = isMcpOnlyCustomer(isModuleDisabled);
    // The MCP-only copy links the connect page, which opens in place and
    // closes the dialog behind it.
    const unlockCopy = unlockModuleDialogCopy(unlockModule ?? '', mcpOnlyCustomer, () => {
        setUnlockModule(null);
        navigateToView('connect');
    });

    // BAR-40 S4: MainApplicationUI is the persistent shell. It provides ShellUi
    // context to its descendant view routes (rendered by the in-place <Routes>
    // below) so extracted views read the shell's layout/panel state + the
    // MainApplicationUI-owned orchestration handlers (prefill setters, beginDraft,
    // handleSelectProject) WITHOUT prop-drilling — and the prefill setters stay
    // bound to the same local state GeminiChatArea reads (seed contract intact).
    const __shellUiState = {
        canvasWidthPercent, isChatHidden, sidebarWidth, chatPanelWidth,
        isSidebarHovered, isAccountMenuOpen, isResizingCanvas,
        showShareDialog, onlineUsers,
        isReportFindingsPanelOpen, selectedReportFindingId, selectedReportOnlineSupportId,
        isReportDetailOpen, reportSplitPercent, reportListWidth,
        isSourcePanelOpen, sourcePanelContent, highlightedNodeId, reLayoutTrigger,
    };
    const __shellUiActions = {
        navigateToView,
        setIsChatHidden, setIsSidebarHovered, setIsAccountMenuOpen, setShowShareDialog,
        setIsReportFindingsPanelOpen, setSelectedReportFindingId, setSelectedReportOnlineSupportId,
        setIsReportDetailOpen, setIsSourcePanelOpen, setSourcePanelContent, setHighlightedNodeId,
        resetMainAppLocalPanels, onOpenWorkspaceDetails, onOpenChangePassword,
        onOpenUnlockModule: (label: string) => setUnlockModule(label),
        // S4: MainApplicationUI-owned orchestration handlers exposed to view routes.
        setPrefillChatInput, setAutoSendPrefill, beginDraft, beginBlankDraft, handleSelectProject,
    };

    return (
        <ShellUiProvider state={__shellUiState} actions={__shellUiActions}>
        {/* h-full (not h-screen): ShellLayout owns viewport height so the BAR-135
            impersonation banner can reduce the shell's space without clipping. */}
        <div className="flex h-full bg-bg overflow-hidden font-body">
            {showSidebar && (
            <nav
                ref={sidebarRef}
                style={{ width: `${currentSidebarWidth}px` }}
                className="flex flex-col bg-surface text-fg font-brand p-3 border-r border-border-default shadow-bark-xs transition-all duration-300 ease-in-out"
                onMouseEnter={() => setIsSidebarHovered(true)}
                onMouseLeave={() => setIsSidebarHovered(false)}
            >
                <div className={`mb-4 shrink-0 p-2 border border-transparent flex items-center ${isSidebarHovered ? 'gap-3 border-b-border-default pb-3' : 'justify-center'}`}>
                    <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center">
                        <img
                            className="w-9 h-9 object-contain max-w-none"
                            src="/Data/images/BARK_tree_2_navy.png"
                            alt="BARK"
                        />
                    </div>
                    <span className={`font-brand text-[15px] font-medium tracking-[0.08em] text-primary whitespace-nowrap transition-all duration-300 ${isSidebarHovered ? 'opacity-100' : 'opacity-0 w-0 overflow-hidden'}`}>
                        BARK
                    </span>
                </div>

                {/* Navigation Menu */}
                <div className="flex-1 overflow-y-auto space-y-1">
                    {/* Home. Not for an MCP-only account: its home is the
                        connect page (LandingRoute), so the entry would only
                        reopen the page below. */}
                    {!mcpOnlyCustomer && (
                    <button
                        onClick={() => navigateToView('landing')}
                        className={`${navItemBase} ${
                            routeView === 'landing'
                                ? navItemActive
                                : navItemIdle
                        }`}
                    >
                        <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                            </svg>
                        </div>
                        <span className={`whitespace-nowrap transition-all duration-300 ${isSidebarHovered ? 'opacity-100' : 'opacity-0 w-0 overflow-hidden'}`}>
                            Home
                        </span>
                    </button>
                    )}

                    {/* A free (MCP-only) account works in its AI assistant, so
                        the page that connects one is its only entry. */}
                    {mcpOnlyCustomer && (
                        <button
                            onClick={() => navigateToView('connect')}
                            aria-current={routeView === 'connect' ? 'page' : undefined}
                            className={`${navItemBase} ${routeView === 'connect' ? navItemActive : navItemIdle} focus-visible:shadow-bark-focus`}
                        >
                            <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center">
                                <Plug className="w-5 h-5" strokeWidth={1.5} aria-hidden="true" />
                            </div>
                            <span className={`whitespace-nowrap transition-all duration-300 ${isSidebarHovered ? 'opacity-100' : 'opacity-0 w-0 overflow-hidden'}`}>
                                Connect assistant
                            </span>
                        </button>
                    )}

                    {/* Quick Lookup entry temporarily hidden — see docs/ui-polish-batch-1.md task 1 */}
                    {/* Chat: an MCP-only account sees it only where chat-free serves it. */}
                    {(!mcpOnlyCustomer || usesChatFree) && (
                    <button
                        onClick={() => chatLocked
                            ? setUnlockModule('Chat')
                            : navigateToView('lookup')}
                        aria-label={chatLocked ? 'Chat — not enabled for your organization' : 'Chat'}
                        aria-current={!chatLocked && routeView === 'lookup' ? 'page' : undefined}
                        title={chatLocked ? 'Not enabled for your organization' : 'Chat'}
                        className={chatLocked
                            ? `${navItemLocked} focus-visible:shadow-bark-focus`
                            : `${navItemBase} ${routeView === 'lookup' ? navItemActive : navItemIdle} focus-visible:shadow-bark-focus`}
                    >
                        <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center relative">
                            <MessageSquare size={20} strokeWidth={1.5} className="flex-shrink-0" aria-hidden="true" />
                            {chatLocked && !isSidebarHovered && (
                                <Lock className="absolute -top-1 -right-1 w-3 h-3" strokeWidth={2} aria-hidden="true" />
                            )}
                        </div>
                        <span className={`whitespace-nowrap transition-all duration-300 flex items-center gap-2 ${isSidebarHovered ? 'opacity-100' : 'opacity-0 w-0 overflow-hidden'}`}>
                            <span>Chat</span>
                            {chatLocked && <span className={navLockedBadge}>Locked</span>}
                        </span>
                    </button>
                    )}

                    {!mcpOnlyCustomer && (
                        <>
                    {/* Regulatory Research */}
                    <button
                        onClick={() => researchLocked
                            ? setUnlockModule('Regulatory research')
                            : openTypedProjects('research')}
                        className={researchLocked
                            ? navItemLocked
                            : `${navItemBase} ${
                                (routeView === 'new-project' && newProjectType === 'research') ||
                                (routeView === 'projects' && projectsTypeFilter === 'research') ||
                                ['quick-research', 'workspace', 'logic-chain'].includes(routeView)
                                    ? navItemActive
                                    : navItemIdle
                            }`}
                        aria-label={researchLocked ? 'Regulatory research — not enabled for your organization' : undefined}
                        title={researchLocked ? 'Not enabled for your organization' : undefined}
                    >
                        <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center relative">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                            {researchLocked && !isSidebarHovered && (
                                <Lock className="absolute -top-1 -right-1 w-3 h-3" strokeWidth={2} aria-hidden="true" />
                            )}
                        </div>
                        <span className={`whitespace-nowrap transition-all duration-300 flex items-center gap-2 ${isSidebarHovered ? 'opacity-100' : 'opacity-0 w-0 overflow-hidden'}`}>
                            Regulatory research
                            {researchLocked && <span className={navLockedBadge}>Locked</span>}
                        </span>
                    </button>

                    {/* Build & Artifact — unavailable orgs see the shared locked
                        state instead of having the module silently disappear. */}
                    <button
                        onClick={() => buildLocked
                            ? setUnlockModule('Build')
                            : openTypedProjects('build')}
                        className={buildLocked
                            ? navItemLocked
                            : `${navItemBase} ${
                                (routeView === 'new-project' && newProjectType === 'build') ||
                                (routeView === 'projects' && projectsTypeFilter === 'build') ||
                                routeView === 'build-artifacts'
                                    ? navItemActive
                                    : navItemIdle
                            }`}
                        aria-label={buildLocked ? 'Build and artifacts — not enabled for your organization' : undefined}
                        title={buildLocked ? 'Not enabled for your organization' : undefined}
                    >
                        <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center relative">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                            </svg>
                            {buildLocked && !isSidebarHovered && (
                                <Lock className="absolute -top-1 -right-1 w-3 h-3" strokeWidth={2} aria-hidden="true" />
                            )}
                        </div>
                        <span className={`whitespace-nowrap transition-all duration-300 flex items-center gap-2 ${isSidebarHovered ? 'opacity-100' : 'opacity-0 w-0 overflow-hidden'}`}>
                            Build & artifacts
                            {buildLocked && <span className={navLockedBadge}>Locked</span>}
                        </span>
                    </button>

                    <button
                        onClick={() => { reopenClassification(); navigateToView('classification'); }}
                        aria-current={routeView === 'classification' ? 'page' : undefined}
                        className={classificationLocked ? navItemLocked : `${navItemBase} ${routeView === 'classification' ? navItemActive : navItemIdle}`}
                        title={classificationAccess.loading ? 'Checking Classification access' : classificationLocked ? 'Classification access is checked when opened' : undefined}
                    >
                        <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center relative">
                            <Boxes className="w-5 h-5" strokeWidth={1.5} />
                            {classificationLocked && !isSidebarHovered && <Lock className="absolute -top-1 -right-1 w-3 h-3" strokeWidth={2} aria-hidden="true" />}
                        </div>
                        <span className={`whitespace-nowrap transition-all duration-300 flex items-center gap-2 ${isSidebarHovered ? 'opacity-100' : 'opacity-0 w-0 overflow-hidden'}`}>
                            Classification
                            {classificationLocked && <span className={navLockedBadge}>{classificationAccess.loading ? 'Checking' : 'Locked'}</span>}
                        </span>
                    </button>


                    {/* Reporting — gated by the `reporting` module (admin panel,
                        default OFF). Without it the entry stays the teaser: it
                        looks live but opens the "Unlock" dialog. */}
                    <button
                        onClick={() => reportingLocked
                            ? setUnlockModule('Reporting')
                            : navigateToView('reporting-preview')}
                        aria-current={!reportingLocked && routeView === 'reporting-preview' ? 'page' : undefined}
                        className={`${navItemBase} ${!reportingLocked && routeView === 'reporting-preview' ? navItemActive : navItemIdle}`}
                    >
                        <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center">
                            <FileBarChart className="w-5 h-5" strokeWidth={1.5} />
                        </div>
                        <span className={`whitespace-nowrap transition-all duration-300 ${isSidebarHovered ? 'opacity-100' : 'opacity-0 w-0 overflow-hidden'}`}>
                            Reporting
                        </span>
                    </button>

                    {/* Inventory — ONE entry, two faces (Kawi, 5 Aug): where
                        the /inventory route exists (local dev; a build that
                        sets the flags) this IS the door; everywhere else it
                        stays the teaser that opens the "Unlock" dialog
                        (BAR-538; matches the start-page card). The build flag
                        remains the real gate — never the menu. */}
                    <button
                        onClick={() => INVENTORY_ENABLED
                            ? shellNavigate('/inventory')
                            : setUnlockModule('Inventory')}
                        className={`${navItemBase} ${INVENTORY_ENABLED && shellLocation.pathname === '/inventory' ? navItemActive : navItemIdle}`}
                    >
                        <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center">
                            <Boxes className="w-5 h-5" strokeWidth={1.5} />
                        </div>
                        <span className={`whitespace-nowrap transition-all duration-300 ${isSidebarHovered ? 'opacity-100' : 'opacity-0 w-0 overflow-hidden'}`}>
                            Inventory
                        </span>
                    </button>

                    {/* Project Library */}
                    <button
                        onClick={() => projectLibraryLocked
                            ? setUnlockModule('Project library')
                            : navigateToView('projects', { projectsFilter: 'all' })}
                        className={projectLibraryLocked
                            ? navItemLocked
                            : `${navItemBase} ${
                                routeView === 'projects' && projectsTypeFilter === 'all'
                                    ? navItemActive
                                    : navItemIdle
                            }`}
                        aria-label={projectLibraryLocked ? 'Project library — not enabled for your organization' : undefined}
                        title={projectLibraryLocked ? 'Not enabled for your organization' : undefined}
                    >
                        <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center relative">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                            </svg>
                            {projectLibraryLocked && !isSidebarHovered && (
                                <Lock className="absolute -top-1 -right-1 w-3 h-3" strokeWidth={2} aria-hidden="true" />
                            )}
                        </div>
                        <span className={`whitespace-nowrap transition-all duration-300 flex items-center gap-2 ${isSidebarHovered ? 'opacity-100' : 'opacity-0 w-0 overflow-hidden'}`}>
                            Project library
                            {projectLibraryLocked && <span className={navLockedBadge}>Locked</span>}
                        </span>
                    </button>

                    {/* Separator */}
                    <div className="my-2 border-t border-divider" />

                    {/* Institution profile — the bank's master data, its own
                        page OUTSIDE the inventory (Kawi, 5 Aug: "not in the
                        inventory panel, in the bark menu"). Appears only
                        where the /institution route exists — same gate. */}
                    {INSTITUTION_ENABLED && (
                        <button
                            onClick={() => shellNavigate('/institution')}
                            className={`${navItemBase} ${shellLocation.pathname === '/institution' ? navItemActive : navItemIdle}`}
                        >
                            <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center">
                                <Landmark className="w-5 h-5" strokeWidth={1.5} />
                            </div>
                            <span className={`whitespace-nowrap transition-all duration-300 ${isSidebarHovered ? 'opacity-100' : 'opacity-0 w-0 overflow-hidden'}`}>
                                Institution profile
                            </span>
                        </button>
                    )}

                    {/* Institution profile placeholder (took the old "Settings,
                        soon" slot on 2026-10-17). Where the real entry above is
                        built in (INSTITUTION_ENABLED), it alone shows. */}
                    {!INSTITUTION_ENABLED && (
                    <button
                        className={navItemDisabled}
                        disabled
                        aria-disabled="true"
                        title="Coming soon"
                    >
                        <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center relative opacity-50">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            <span
                                aria-hidden="true"
                                className={`absolute -top-0.5 -right-0.5 w-1.5 h-1.5 rounded-full bg-border-strong ring-2 ring-surface transition-opacity duration-300 ${isSidebarHovered ? 'opacity-0' : 'opacity-100'}`}
                            />
                        </div>
                        <span className={`whitespace-nowrap transition-all duration-300 flex items-center gap-2 ${isSidebarHovered ? 'opacity-100' : 'opacity-0 w-0 overflow-hidden'}`}>
                            <span className="opacity-60">Institution profile</span>
                            <span className={navSoonBadge}>soon</span>
                        </span>
                    </button>
                    )}
                        </>
                    )}
                </div>

                <div className="mt-auto shrink-0">
                    <UserAccountMenu
                        userData={userData}
                        isExpanded={isSidebarHovered || isAccountMenuOpen}
                        onOpenChange={setIsAccountMenuOpen}
                        onLogout={onLogout}
                        onChangePassword={onOpenChangePassword}
                        onOpenAdmin={() => navigateToView('admin')}
                        onOpenConnect={() => navigateToView('connect')}
                    />
                </div>
            </nav>
            )}

            {showSidebar && (
                <div onMouseDown={startResizeSidebar} className="w-px cursor-col-resize flex-shrink-0 bg-border-default hover:w-1 hover:bg-primary transition-all duration-150 z-10"></div>
            )}

            {/* Main Content Area - Resizable Split Layout */}
            {/* h-full (not h-screen) so the impersonation banner (BAR-135) can
                shorten the shell without this panel overflowing the viewport. */}
            <div ref={mainPanelRef} className="flex-1 flex flex-col h-full min-w-0 bg-surface">
                {/* Unified Project Header — same for research and build projects */}
                {activeProject && (routeView === 'workspace' || routeView === 'logic-chain' || routeView === 'build-artifacts') && (
                    <div className="flex-shrink-0 bg-surface border-b border-border-default px-4 py-2 z-10">
                        <div className="flex items-center justify-between gap-4">
                            {/* Left: back-to-plan button + project name */}
                            <div className="flex-shrink-0 min-w-0 flex items-center gap-2">
                                {routeView === 'build-artifacts' && workspaceMode !== 'plan' && (
                                    <button
                                        onClick={() => {
                                            setWorkspaceMode('plan', 'user');
                                            // Reload plan workspace data
                                            if (activeProject?.project_id && userData?.email) {
                                                useProjectStore.getState().loadWorkspaceState(activeProject.project_id, userData.email);
                                            }
                                        }}
                                        className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-fg-tertiary hover:text-fg-secondary hover:bg-bg-warm rounded transition-colors"
                                        title="Back to project plan"
                                    >
                                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                                        </svg>
                                        Plan
                                    </button>
                                )}
                                <EditableProjectTitle
                                    projectId={activeProject.project_id}
                                    projectName={activeProject.name}
                                    userEmail={userData?.email || ''}
                                    onRenamed={() => loadProjectList(userData?.email || '')}
                                />
                            </div>

                            {/* Plan train track — shows plan nodes as a DAG in the header */}
                            {activeProject?.project_type === 'build' && workspaceMode !== 'plan' && (
                                <div className="flex-1 flex justify-center overflow-hidden">
                                    <PlanTrainTrack />
                                </div>
                            )}

                            {/* Right: presence + share + sync */}
                            <div className="flex items-center gap-3 flex-shrink-0">
                                {/* Online presence avatars */}
                                {onlineUsers.length > 1 && (
                                    <div className="flex items-center -space-x-1.5">
                                        {onlineUsers.slice(0, 4).map(u => (
                                            <div
                                                key={u.user_id}
                                                className={
                                                    'w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-medium border-2 ' +
                                                    (u.imp
                                                        ? 'bg-warning-bg text-warning border-warning'
                                                        : 'bg-success-bg text-success border-white')
                                                }
                                                title={u.imp ? `${u.email} (impersonated)` : u.email}
                                            >
                                                {(u.email || '?').charAt(0).toUpperCase()}
                                            </div>
                                        ))}
                                        {onlineUsers.length > 4 && (
                                            <div className="w-6 h-6 rounded-full bg-bg-warm text-fg-tertiary border-2 border-white flex items-center justify-center text-[10px] font-medium">
                                                +{onlineUsers.length - 4}
                                            </div>
                                        )}
                                    </div>
                                )}
                                {/* Share button */}
                                <button
                                    onClick={() => setShowShareDialog(true)}
                                    className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-fg-secondary bg-bg-warm rounded-md hover:bg-bg-warm transition-colors"
                                >
                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                                    </svg>
                                    Share
                                </button>
                                <SyncStatusIndicator />
                            </div>
                        </div>
                    </div>
                )}
                {/* Share Dialog */}
                {showShareDialog && activeProject && routeView !== 'classification' && (
                    <ShareProjectDialog
                        projectId={activeProject.project_id}
                        userId={userData?.email || ''}
                        isOwner={activeProject.user_id === (userData?.email || '')}
                        onClose={() => setShowShareDialog(false)}
                    />
                )}

                {/* Main content row - Canvas + Chat (chat on right) */}
                <div className="flex-1 flex min-h-0">
                    {/* Canvas/Workspace Area - Full width when in library/new-project/projects, resizable when viewing project */}
                    <div
                        className={`h-full bg-surface flex flex-col overflow-hidden transition-all duration-300 ease-in-out`}
                        style={{ width: (routeView === 'lookup' || routeView === 'new-project' || routeView === 'projects' || routeView === 'landing' || routeView === 'quick-research' || routeView === 'settings' || routeView === 'reporting-preview' || routeView === 'classification' || routeView === 'connect' || !activeProject || isChatHidden) ? '100%' : `${canvasWidthPercent}%` }}
                    >
                        {/* Content Area - Full Height */}
                        <div className="flex-1 overflow-hidden bg-surface relative">
                        {/* Project-open loading overlay (#89). Covers the canvas
                            from the moment a project is clicked until the
                            workspace snapshot + subscription catchup are ready,
                            so the user doesn't see a frozen/dead click or
                            mid-replay flicker. NOTE: this is intentionally NOT
                            gated on `activeProject` — during the slow initial
                            fetch activeProject is null (cleared by
                            clearActiveProject), which is exactly the window the
                            overlay needs to cover. The backdrop also captures
                            clicks, preventing double-open of the same project. */}
                        {isProjectOpening && routeView !== 'classification' && (
                            <div className="absolute inset-0 z-30 flex items-center justify-center bg-surface/80 backdrop-blur-sm">
                                <div className="flex flex-col items-center gap-3">
                                    <div className="h-8 w-8 animate-spin rounded-full border-2 border-border-default border-t-slate-700" />
                                    <div className="text-sm text-fg-secondary">Opening project…</div>
                                </div>
                            </div>
                        )}
                        {/* Node/compartment-open loading overlay (#89). Shown when
                            a plan-node drill or "Open node" navigation is fetching
                            its workspace (loadSpecificWorkspace). Suppressed during
                            project open so the two overlays never stack. */}
                        {workspaceLoading && !isProjectOpening && routeView !== 'classification' && (
                            <div className="absolute inset-0 z-30 flex items-center justify-center bg-surface/80 backdrop-blur-sm">
                                <div className="flex flex-col items-center gap-3">
                                    <div className="h-8 w-8 animate-spin rounded-full border-2 border-border-default border-t-slate-700" />
                                    <div className="text-sm text-fg-secondary">Loading workspace…</div>
                                </div>
                            </div>
                        )}
                        {/* Content */}
                        {/* BAR-40: the view route table lives in <ShellRoutes> (the single
                            source of truth for URL→view). The project-scoped tail
                            (workspace / logic-chain / build-artifacts + welcome fallback) is
                            still selected by the URL-derived routeView and passed as the
                            catch-all `legacyView` until S5 extracts it into real routes. The
                            chrome around this block never remounts on navigation. */}
                        {activeView === 'report' && currentReport && routeView !== 'classification' ? (
                            <SavedReportView
                                report={currentReport}
                                backLabel="Back to workspace"
                                onBack={() => {
                                    setSelectedReportId(null);
                                    setIsCanvasMode(false);
                                    setActiveView('welcome');
                                }}
                            />
                        ) : (
                        <ShellRoutes
                            classificationOpenVersion={classificationOpenVersion}
                            workspaceView={(
                            activeProject ? (
                                <div className="h-full flex flex-col">
                                    {/* Workspace brief (build projects only, self-gating) — the
                                        plan node's what/inputs/output exactly as the plan view
                                        presents it, docked top + collapsible. */}
                                    <WorkspaceBrief workspaceId={dictWorkspace?.workspace_id} />
                                    {/* Content Area — workspace tabs render inside each view's
                                        primary column (not at canvas level) so the floating pill's
                                        `left-1/2` centering is bounded to the view column and
                                        never overlaps the findings panel sibling. */}
                                    <div className="flex-1 overflow-hidden relative">
                                    {(() => {
                                        // Welcome-to-Research empty state — shared with
                                        // BuildArtifactsView's RequirementsPhase via
                                        // utils/welcomeState.ts so both mount points see the
                                        // same cliff.
                                        const isWelcomeState = computeIsWelcomeState(dictWorkspace, activeProject);

                                        const workspaceTabsEl = (
                                            <WorkspaceTabs
                                                activeWorkspace={activeWorkspace}
                                                onWorkspaceChange={setActiveWorkspace}
                                                pendingSwitch={pendingWorkspaceSwitch}
                                                onAcceptSwitch={() => {
                                                    const proposal = pendingWorkspaceSwitch;
                                                    acceptWorkspaceSwitch();
                                                    if (proposal) {
                                                        const target = proposal.to_workspace as string;
                                                        const from = proposal.from_workspace as string;
                                                        navigateToView(target === 'production' ? 'build-artifacts' : 'workspace', { projectId: activeProject?.project_id });
                                                        const validModes = ['plan', 'institution_context', 'research', 'production', 'output_package'];
                                                        setWorkspaceMode(validModes.includes(target) ? target as any : 'research', 'user');
                                                        useProjectStore.getState().setMainWorkspaceView(target);
                                                    }
                                                }}
                                                onRejectSwitch={rejectWorkspaceSwitch}
                                                isAgentActive={isLoading}
                                                disabled={isWelcomeState}
                                            />
                                        );
                                        if (activeWorkspace === 'graph') {
                                            // The plan workspace renders as the 2-D grid
                                            // (PlanGraphView), not the evidence board — same
                                            // grid as the build-artifacts plan phase, so the
                                            // plan looks identical wherever it is shown.
                                            if (workspaceMode === 'plan') {
                                                const planGraph = dictWorkspace
                                                    ? dictToPlanGraph(dictWorkspace)
                                                    : { nodes: [], edges: [] };
                                                return (
                                                    <div className="h-full flex flex-col">
                                                        {workspaceTabsEl}
                                                        <div className="flex-1 min-h-0">
                                                            <PlanGraphView
                                                                key={`plan-${dictWorkspace?.workspace_id}`}
                                                                nodes={planGraph.nodes}
                                                                edges={planGraph.edges}
                                                                clusters={dictWorkspace?.clusters || {}}
                                                                objective={(dictWorkspace as any)?.plan_objective || (activeProject as any)?.objective || ''}
                                                                onNodeClick={async (nodeId) => {
                                                                    // Drill into a plan node's workspace.
                                                                    // Mirror PlanTrainTrack.drillIntoNode:
                                                                    // await load before switching mode, and
                                                                    // route to build-artifacts so each phase
                                                                    // gets its proper view.
                                                                    const node = dictWorkspace?.nodes?.[nodeId] as any;
                                                                    const wsId = node?.workspace_id;
                                                                    const wsType = node?.workspace_type;
                                                                    const uid = userData?.email || activeProject?.user_id;
                                                                    if (!wsId || !wsType || !uid) return;
                                                                    const loaded = await useProjectStore.getState().loadSpecificWorkspace(wsId, uid);
                                                                    if (!loaded) {
                                                                        console.warn('[App] loadSpecificWorkspace returned false for', wsId, '— aborting navigation.');
                                                                        return;
                                                                    }
                                                                    navigateToView('build-artifacts', { projectId: activeProject?.project_id });
                                                                    setWorkspaceMode(wsType, 'user');
                                                                }}
                                                            />
                                                        </div>
                                                    </div>
                                                );
                                            }
                                            return (
                                                <ResearchWorkspace
                                                    graph={dictWorkspace}
                                                    references={linkedReport?.references}
                                                    findings={dictWorkspace?.findings || activeProject.findings}
                                                    markers={dictWorkspace?.markers || {}}
                                                    onlineSupport={dictWorkspace?.online_support || {}}
                                                    workspaceId={dictWorkspace?.workspace_id}
                                                    compartmentId={dictWorkspace?.compartment_id || 'default'}
                                                    editable={true}
                                                    initialPositions={activeProject.workspace_ui_state?.node_positions}
                                                    onPositionsChange={(positions) => {
                                                        updateWorkspace(activeProject.workspace, {
                                                            ...activeProject.workspace_ui_state,
                                                            node_positions: positions
                                                        });
                                                    }}
                                                    notes={activeProject.notes || ''}
                                                    onNotesChange={updateNotes}
                                                    highlightedNodeId={highlightedNodeId}
                                                    isSourcePanelOpen={isSourcePanelOpen}
                                                    sourcePanelContent={sourcePanelContent}
                                                    onCloseSourcePanel={() => {
                                                        setIsSourcePanelOpen(false);
                                                        setSourcePanelContent(null);
                                                    }}
                                                    onNavigateToNode={(nodeId) => {
                                                        setHighlightedNodeId(nodeId);
                                                        setTimeout(() => setHighlightedNodeId(null), 3000);
                                                    }}
                                                    triggerReLayout={reLayoutTrigger}
                                                    onValidateClick={() => onOpenWorkspaceDetails?.()}
                                                    headerSlot={workspaceTabsEl}
                                                    isWelcomeState={isWelcomeState}
                                                />
                                            );
                                        }
                                        /* Report tab (default) — flex row so findings panel compresses report */
                                        return (
                                        <div className="flex h-full bg-bg-warm">

                                            <div className={`min-w-0 relative ${isReportDetailOpen ? '' : 'flex-1'}`}
                                                style={isReportDetailOpen ? { width: `${reportSplitPercent}%` } : undefined}
                                            >
                                                {/* Pill bound to this column only — never crosses into findings */}
                                                {workspaceTabsEl}
                                                {/* Report frame: ReportView renders the framed card and owns
                                                    its own vertical scroll inside the grey workspace shell. */}
                                                <div className="h-full">
                                                    <ReportView
                                                        report={dictWorkspace?.reports ? Object.values(dictWorkspace.reports)[0] || null : null}
                                                        draftReport={activeProject.draft_report || null}
                                                        markers={dictWorkspace?.markers || {}}
                                                        findings={dictWorkspace?.findings || {}}
                                                        onlineSupport={dictWorkspace?.online_support || {}}
                                                        selectedFindingId={selectedReportFindingId}
                                                        selectedOnlineSupportId={selectedReportOnlineSupportId}
                                                        workspaceId={dictWorkspace?.workspace_id}
                                                        userId={userData?.email || ''}
                                                        authToken={authToken || undefined}
                                                        onDispatchChatMessage={(text, opts) => {
                                                            // Phase E Fix button: a pre-filled instruction enters the
                                                            // normal chat path — same as if the user typed it.
                                                            // onSendMessage is App's handleSendMessage passed into
                                                            // MainApplicationUI (the definition itself is out of
                                                            // scope here — that exact mistake shipped once).
                                                            // BAR-192: forward the structured repair flag.
                                                            onSendMessage(text, 'standard', false, { repair: opts?.repair });
                                                        }}
                                                        isWelcomeState={isWelcomeState}
                                                        onCitationClick={(findingId, markerId) => {
                                                            setSelectedReportFindingId(findingId);
                                                            setSelectedReportOnlineSupportId(null); // cross-type mutual exclusion: only one chip highlighted
                                                            setIsReportFindingsPanelOpen(true);
                                                        }}
                                                        onOnlineRefClick={(onlineSupportId) => {
                                                            setSelectedReportOnlineSupportId(onlineSupportId);
                                                            setSelectedReportFindingId(null); // cross-type mutual exclusion: only one chip highlighted
                                                            setIsReportFindingsPanelOpen(true);
                                                        }}
                                                    />
                                                </div>
                                                {/* Toggle button overlays report — outside scroll container so it stays fixed */}
                                                {(() => {
                                                    const findingsCount = Object.keys(dictWorkspace?.findings || activeProject?.findings || {}).length;
                                                    const onlineSupportCount = Object.keys(dictWorkspace?.online_support || {}).length;
                                                    const uploadedFileCount = Object.keys(dictWorkspace?.uploaded_files || {}).length;
                                                    return !isReportFindingsPanelOpen && (findingsCount + onlineSupportCount + uploadedFileCount > 0) ? (
                                                        <FindingsToggleButton
                                                            findingsCount={findingsCount + onlineSupportCount + uploadedFileCount}
                                                            onToggle={() => setIsReportFindingsPanelOpen(true)}
                                                        />
                                                    ) : null;
                                                })()}
                                            </div>
                                            {/* Findings panel — flex sibling, compresses report */}
                                                <div
                                                    className={`h-full flex-shrink-0 transition-all duration-300 ease-in-out overflow-hidden`}
                                                    style={{
                                                        width: isReportFindingsPanelOpen
                                                            ? (isReportDetailOpen ? `${100 - reportSplitPercent}%` : reportListWidth)
                                                            : 0
                                                    }}
                                                >
                                                    <UnifiedFindingsPanel
                                                        allFindings={dictWorkspace?.findings || activeProject.findings || {}}
                                                        allMarkers={dictWorkspace?.markers || {}}
                                                        allOnlineSupport={dictWorkspace?.online_support || {}}
                                                        allUploadedFiles={dictWorkspace?.uploaded_files || {}}
                                                        linkedOutputs={dictWorkspace?.linked_outputs || []}
                                                        selectedFindingId={selectedReportFindingId}
                                                        selectedOnlineSupportId={selectedReportOnlineSupportId}
                                                        isOpen={isReportFindingsPanelOpen}
                                                        workspaceId={dictWorkspace?.workspace_id}
                                                        compartmentId={dictWorkspace?.compartment_id || 'default'}
                                                        onToggle={() => {
                                                            setIsReportFindingsPanelOpen(!isReportFindingsPanelOpen);
                                                        }}
                                                        onFindingSelect={(id) => {
                                                            setSelectedReportFindingId(id);
                                                            setSelectedReportOnlineSupportId(null); // cross-type mutual exclusion: picking a finding clears any online highlight
                                                            setIsReportFindingsPanelOpen(true);
                                                        }}
                                                        onClose={() => {
                                                            setSelectedReportFindingId(null);
                                                            setIsReportDetailOpen(false);
                                                            setIsReportFindingsPanelOpen(false);
                                                        }}
                                                        onNavigateToNode={(nodeId) => {
                                                            setActiveWorkspace('graph');
                                                            setHighlightedNodeId(nodeId);
                                                            setTimeout(() => setHighlightedNodeId(null), 3000);
                                                        }}
                                                        onDetailOpenChange={(open) => {
                                                            setIsReportDetailOpen(open);
                                                            // Clear the report-driven selection only on a genuine
                                                            // open→closed transition, so re-clicking the same chip
                                                            // re-fires the panel's effect and reopens it (props are
                                                            // value-gated; a repeat click of the same id is a dead
                                                            // click otherwise). Guarding on the previous state avoids
                                                            // the transient `false` the panel emits on first open —
                                                            // before detailFindingId is set — from wiping the just-
                                                            // clicked selection and de-highlighting the report chip.
                                                            if (prevReportDetailOpenRef.current && !open) {
                                                                setSelectedReportFindingId(null);
                                                                setSelectedReportOnlineSupportId(null);
                                                            }
                                                            prevReportDetailOpenRef.current = open;
                                                        }}
                                                        onPanelResize={(delta) => {
                                                            if (isReportDetailOpen) {
                                                                const parentEl = document.querySelector('.flex.h-full') as HTMLElement;
                                                                const containerWidth = parentEl?.offsetWidth || window.innerWidth;
                                                                const pctDelta = (delta / containerWidth) * 100;
                                                                setReportSplitPercent(p => Math.max(25, Math.min(75, p + pctDelta)));
                                                            } else {
                                                                setReportListWidth(w => Math.max(160, Math.min(500, w - delta)));
                                                            }
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        );
                                    })()}
                                </div>
                            </div>
                        ) : researchDraftActive ? (
                            // Research draft: no project yet — the sidebar "Regulatory
                            // Research" button landed here via a topic-less blank draft
                            // (/research/new). Render the canonical fresh-research empty
                            // state (the same Welcome-to-Research panel a brand-new
                            // project shows) so the user sees "empty workspace + chat"
                            // rather than a blank shell. The project is created lazily on
                            // the first chat message (useWorkspaceDraftMode.trySendDraft),
                            // after which the activeProject branch above takes over.
                            <div className="h-full flex flex-col">
                                <div className="flex-1 overflow-hidden relative">
                                    <ResearchWorkspace graph={undefined} isWelcomeState={true} editable={false} />
                                </div>
                            </div>
                        ) : (projectLoading || routeProjectId) ? (
                            // `routeProjectId` covers the cold-load window: on a hard
                            // refresh of /research/:id the URL names a project before
                            // auth resolves and the deep-link effect flips projectLoading.
                            // Without it, every guard sits at its cold-start default and
                            // the legacy "Quick Research" fallback below would flash/stick.
                            <div className="h-full flex items-center justify-center bg-bg-warm">
                                <div className="text-center">
                                    <div className="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
                                    <p className="text-fg-tertiary text-sm">Loading project...</p>
                                </div>
                            </div>
                        ) : (
                            // Project-less workspace dead-end. The effect above
                            // navigates to the start page; render a neutral spinner
                            // for the single paint before that commits so the old
                            // "Quick Research" panel never flashes.
                            <div className="h-full flex items-center justify-center bg-bg-warm">
                                <div className="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin"></div>
                            </div>
                        )
                        )}
                            buildArtifactsView={(
                        activeProject ? (
                            <BuildArtifactsView
                                projectId={activeProject.project_id}
                                authToken={authToken}
                                userId={userData?.email || ''}
                                linkedReport={linkedReport}
                                isAgentActive={isLoading}
                                onValidateClick={() => onOpenWorkspaceDetails?.()}
                                onDispatchChatMessage={(text, opts) => {
                                    // Phase E Fix button (this shell renders the report
                                    // view in research/build modes — NOT the legacy
                                    // ReportView site below, which cost a soak round to
                                    // discover). onSendMessage is App's handleSendMessage.
                                    // BAR-192: forward the structured repair flag.
                                    onSendMessage(text, 'standard', false, { repair: opts?.repair });
                                }}
                            />
                        ) : buildDraftActive ? (
                            // Draft mode: no project yet, but we render the plan canvas
                            // so the user sees "empty plan + chat" rather than a blank
                            // app shell. Entered from a Build topic-box press (seeded
                            // with a topic) OR the sidebar "Build & Artifacts" button
                            // (topic-less blank draft). BuildArtifactsView's plan phase
                            // falls through to <PlanGraphView nodes={[]} edges={[]} /> when there is
                            // no dictWorkspace (which is the case here — no project,
                            // no workspace fetched). projectId={null} is the explicit
                            // "no project loaded" signal — phases that need a real id
                            // (e.g. build) guard against null inside BuildArtifactsView.
                            <BuildArtifactsView
                                projectId={null}
                                authToken={authToken}
                                userId={userData?.email || ''}
                                linkedReport={linkedReport}
                                isAgentActive={false}
                                onValidateClick={() => {}}
                            />
                        ) : (
                            // Project-less build dead-end. The effect above
                            // navigates to the start page; render a neutral spinner
                            // for the single paint before that commits (rather than
                            // a blank screen).
                            <div className="h-full flex items-center justify-center bg-bg-warm">
                                <div className="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin"></div>
                            </div>
                        )
                            )}
                        />
                        )}
                    </div>
                </div> {/* Close canvas container */}

                    {/* Chat panel + toggle button.
                        Gate: render when an active project exists in any workspace
                        view (existing behavior), OR in draft mode — research (sidebar
                        "Regulatory Research") or build (sidebar "Build & Artifacts" /
                        a Build topic-box press). The draft-mode branches let us show
                        the chat composer (and, for box presses, the topic heading)
                        before any project exists; the project is created lazily when
                        the user submits their first message (see the onSendMessage
                        interceptor below). */}
                    {(activeProject || buildDraftActive || researchDraftActive) && (routeView === 'workspace' || routeView === 'logic-chain' || routeView === 'build-artifacts') && (
                        <>
                            {/* Resizable Divider - only interactive when chat visible */}
                            {!isChatHidden && (
                                <div
                                    onMouseDown={handleCanvasResizeStart}
                                    className={`h-full cursor-col-resize flex-shrink-0 transition-all duration-150 z-10 ${
                                        isResizingCanvas ? 'w-1 bg-accent' : 'w-px bg-bg-warm hover:w-1 hover:bg-accent-tint'
                                    }`}
                                    style={{ userSelect: 'none', cursor: 'col-resize' }}
                                />
                            )}

                            {/* Always-rendered chat panel with animated width */}
                            <div
                                className="h-full overflow-hidden transition-all duration-300 ease-in-out flex-shrink-0"
                                style={{ width: isChatHidden ? 0 : `${100 - canvasWidthPercent}%` }}
                            >
                                <div className="h-full bg-surface border-l border-border-default flex flex-col" style={{ minWidth: '300px' }}>
                                    {/* Chat header with hide button */}
                                    <div className="flex-shrink-0 flex items-center justify-between px-3 py-2 border-b border-divider">
                                        <span className="text-xs font-medium text-fg-tertiary uppercase tracking-wide">Chat</span>
                                        <button
                                            onClick={() => setIsChatHidden(true)}
                                            className="p-1.5 text-fg-tertiary hover:text-accent hover:bg-accent-tint rounded-md transition-colors"
                                            title="Hide chat"
                                        >
                                            <PanelRightClose className="w-4 h-4" />
                                        </button>
                                    </div>
                                    {/* Draft-mode status strip — shown only while a draft has
                                        no project yet. Two independent pieces:
                                          • Topic heading: Build topic-box presses seed a topic;
                                            sidebar blank drafts (research or build) carry none,
                                            so the heading stays hidden for them. <p> rather than
                                            <h2> because this subtree has no <h1> ancestor.
                                          • Lazy-create error: surfaced for ANY draft type so a
                                            failed first-send retry prompt isn't swallowed on the
                                            (topic-less) research path.
                                        aria-live="polite" announces both to SR users on landing
                                        without us managing focus. */}
                                    {(buildDraftActive || researchDraftActive) && (pendingDraft?.topic || draftCreateError) && (
                                        <div className="flex-shrink-0 px-4 py-3 border-b border-divider">
                                            {pendingDraft?.topic && (
                                                <p
                                                    className="font-body text-[15px] font-semibold text-fg"
                                                    aria-live="polite"
                                                >
                                                    {pendingDraft.topic.prompt}
                                                </p>
                                            )}
                                            {draftCreateError && (
                                                <div
                                                    className={`${pendingDraft?.topic ? 'mt-2 ' : ''}px-3 py-2 bg-danger-bg border border-danger rounded-md`}
                                                    role="alert"
                                                    aria-live="polite"
                                                >
                                                    <p className="text-xs text-danger">{draftCreateError}</p>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    {/* Chat content */}
                                    <div className="flex-1 min-h-0 overflow-hidden">
                                        {/* key forces GeminiChatArea to remount fresh per project.
                                            That's what lets useState(initialInput) capture the
                                            correct seed on each project switch, and what makes
                                            the auto-send effect a true one-shot per project
                                            mount — no ref-machinery to track "did we already
                                            fire for this seed" across instance reuse. The
                                            'draft-N' key is used while a draft is active
                                            with no project; N (draftRetryAttempt) bumps on each
                                            lazy-create failure so the component remounts and
                                            re-applies prefillChatInput=msg as initialInput,
                                            restoring the user's typed message in the textarea
                                            for retry. Once lazy-create completes, the key
                                            switches to the real project_id and the component
                                            remounts to auto-send the user's first message. */}
                                        <GeminiChatArea
                                            key={activeProject?.project_id ?? `draft-${draftRetryAttempt}`}
                                            history={chatHistory}
                                            onSendMessage={(msg, depth, multi, options) => {
                                                // Delegate the draft-mode branch to the
                                                // useWorkspaceDraftMode hook. If the user is in
                                                // draft mode (no active project + a pendingDraft
                                                // set), trySendDraft returns { handled: true }
                                                // and owns the lazy-create + state coordination.
                                                // Otherwise we fall through to the default
                                                // forward-to-store send path below.
                                                const draftResult = trySendDraft(msg);
                                                if (draftResult.handled) return;
                                                setPrefillChatInput(null);
                                                setAutoSendPrefill(false);
                                                onSendMessage(msg, depth, multi, options);
                                            }}
                                            initialInput={prefillChatInput || undefined}
                                            autoSendOnMount={autoSendPrefill}
                                            isLoading={isLoading}
                                            streamingStatus={streamingStatus}
                                            streamingResponse={streamingResponse}
                                            streamingThinking={streamingThinking}
                                            streamingActivityLog={streamingActivityLog}
                                            activeToolCall={activeToolCall}
                                            onCancelWorkflow={onCancelWorkflow}
                                            activeChat={activeChat}
                                            compact={isCanvasMode}
                                            onServiceSelect={onServiceSelect}
                                            currentReport={currentReport}
                                            onCloseReport={onCloseReport}
                                            setActiveView={setActiveView}
                                            userData={userData}
                                            isCanvasMode={isCanvasMode}
                                            setIsCanvasMode={setIsCanvasMode}
                                            researchMission={researchMission}
                                            hasReconnectedSession={hasReconnectedSession}
                                            tokenUsage={tokenUsage}
                                            researchProgress={researchProgress}
                                            getResearchProgress={getResearchProgress}
                                            activeResearchTrackingId={activeResearchTrackingId}
                                            handoffPackage={handoffPackage}
                                            pendingQuestion={pendingQuestion}
                                            onAnswerQuestion={onAnswerQuestion}
                                            onReportClick={onReportLinkClick}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Show chat button when hidden */}
                            {isChatHidden && (
                                <div className="h-full flex-shrink-0 border-l border-border-default bg-bg-warm">
                                    <div className="flex items-center justify-end px-3 py-2 border-b border-divider">
                                        <button
                                            onClick={() => setIsChatHidden(false)}
                                            className="p-1.5 text-fg-tertiary hover:text-accent hover:bg-accent-tint rounded-md transition-colors"
                                            title="Show chat"
                                        >
                                            <PanelRightOpen className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
            </div> {/* Close main content row */}
            </div> {/* Close main panel */}

            {/* Document Link Confirmation Modal */}
            {isDocumentPopupOpen && (
                <div className="fixed inset-0 z-50 overflow-hidden">
                    {/* Backdrop */}
                    <div 
                        className="absolute inset-0 bg-black bg-opacity-50"
                        onClick={() => setIsDocumentPopupOpen(false)}
                    />
                    
                    {/* Modal */}
                    <div className="relative w-full h-full flex items-center justify-center p-4">
                        <div className="bg-surface rounded-lg shadow-xl w-full max-w-md flex flex-col">
                            {/* Header */}
                            <div className="flex items-center justify-between p-6 border-b border-border-default">
                                <h3 className="text-lg font-semibold text-fg">Open legal document</h3>
                                <button 
                                    onClick={() => setIsDocumentPopupOpen(false)}
                                    className="text-fg-tertiary hover:text-fg-secondary text-xl leading-none"
                                >
                                    ×
                    </button>
                </div>
                            
                            {/* Content */}
                <div className="p-6">
                                <p className="text-fg-secondary mb-4">
                                    This will open the official EUR-Lex document in a new tab.
                                </p>
                                <div className="text-sm text-fg-tertiary mb-6 p-3 bg-bg-warm rounded">
                                    <strong>URL:</strong> {documentPopupUrl}
                                </div>
                                
                                <div className="flex space-x-3">
                        <button 
                                        onClick={() => {
                                            window.open(documentPopupUrl, '_blank', 'noopener,noreferrer');
                                            setIsDocumentPopupOpen(false);
                                        }}
                                        className="flex-1 inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-accent hover:bg-accent-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent"
                                    >
                                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                    </svg>
                                        Open document
                        </button>
                        <button 
                                        onClick={() => setIsDocumentPopupOpen(false)}
                                        className="px-4 py-2 border border-border-default rounded-md shadow-sm text-sm font-medium text-fg-secondary bg-surface hover:bg-bg-warm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent"
                                    >
                                        Cancel
                                    </button>
                                </div>
                                </div>
                            </div>
                    </div>
                </div>
            )}
            
            {/* Chat Context Menu */}
            {showChatContextMenu && (
                <ContextMenu 
                    position={contextMenuPosition}
                    onClose={() => setShowChatContextMenu(false)}
                >
                    <MenuItem onClick={() => startChatRename(selectedChatForMenu!)}>
                        <span className="flex items-center gap-2">
                            <Pencil size={16} strokeWidth={1.5} aria-hidden="true" />
                            Rename chat
                        </span>
                    </MenuItem>
                    <MenuItem onClick={() => {
                        if (selectedChatForMenu) onDeleteChat(selectedChatForMenu);
                        setShowChatContextMenu(false);
                    }}>
                        <span className="flex items-center gap-2">
                            <Trash2 size={16} strokeWidth={1.5} aria-hidden="true" />
                            Delete chat
                        </span>
                    </MenuItem>
                </ContextMenu>
            )}

            {/* Module teaser — single-action "Unlock" dialog, shared by the
                sidebar Reporting item and the start-page teaser cards. */}
            <ConfirmDialog
                open={unlockModule !== null}
                title={unlockCopy.title}
                description={unlockCopy.description}
                confirmLabel="Got it"
                hideCancel
                onConfirm={() => setUnlockModule(null)}
                onCancel={() => setUnlockModule(null)}
            />
        </div>
        </ShellUiProvider>
    );
}



















// Structural event types that both the streaming callback and Redis subscription process.
// Used for deduplication — declared at module level to avoid re-creation per event.
const STRUCTURAL_EVENTS = new Set([
    'tool_call', 'tool_result', 'phase_change', 'finding', 'complete',
    'workspace_update', 'workspace_switched', 'workspace_switch_proposal',
    'handoff_sync', 'research_started', 'research_mission',
    'research_wave_start', 'coverage_metrics', 'research_gap_analysis',
    'premise_result', 'worker_update', 'progress', 'error', 'user_question',
]);

// Upper bound on how long the watchdog stays suppressed for an in-flight command
// request, so a stuck activeWorkflowRequestRef can't disable health monitoring.
const MAX_WORKFLOW_REQUEST_DURATION_MS = 5 * 60 * 1000;

// BAR-1564: "the next Keycloak sign-in asks for credentials" outlives the
// page. After a Keycloak sign-out that could not start or has not finished
// (Keycloak may still hold the session) or an account BARK refused, a
// reload or a new tab must not let Keycloak sign the same account straight
// back in. Kept in localStorage like the Keycloak session itself; the next
// sign-in BARK accepts clears it.
const FORCE_LOGIN_PROMPT_KEY = 'bark_kc_force_login';

function readForceLoginPrompt(): boolean {
    if (typeof window === 'undefined') return false;
    try {
        return localStorage.getItem(FORCE_LOGIN_PROMPT_KEY) === '1';
    } catch {
        return false;
    }
}

function writeForceLoginPrompt(value: boolean): void {
    try {
        if (value) localStorage.setItem(FORCE_LOGIN_PROMPT_KEY, '1');
        else localStorage.removeItem(FORCE_LOGIN_PROMPT_KEY);
    } catch {
        // Storage unavailable: the in-page state still applies to this mount.
    }
}

export default function App() {
    const { showToast } = useToast();
    // BAR-40: App sits inside <BrowserRouter> (see index.tsx), so it can
    // navigate. Used to return to the home URL on logout so the next user
    // on this browser doesn't inherit the previous deep-link URL.
    const navigate = useNavigate();
    const location = useLocation();
    const reportOpenRequestRef = useRef(0);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [authToken, setAuthToken] = useState<string | null>(null);
    const refreshTokenRef = useRef<string | null>(null);
    // BAR-135: super-admin impersonation. Non-null while impersonating a target.
    // Kept in this tab's sessionStorage with the target's token and user, so
    // the banner + refresh-suppression survive a reload; localStorage keeps the
    // admin's session (BAR-1564, auth/impersonationTab.ts).
    const [impersonation, setImpersonation] = useState<ImpersonationMeta | null>(null);
    // Ref mirror so the WS-4001 / auth-failure callbacks and the expiry timer
    // can read live impersonation state without re-subscribing on every change.
    const impersonationRef = useRef<ImpersonationMeta | null>(null);
    useEffect(() => { impersonationRef.current = impersonation; }, [impersonation]);
    // Late-bound ref to exitImpersonation (defined below) so the auth effects
    // above its declaration can trigger auto-exit without ordering hazards.
    const exitImpersonationRef = useRef<((reason?: string) => Promise<void>) | null>(null);
    // Guards against a re-entrant / double exit (expiry timer + WS-4001 +
    // user-click could otherwise all fire near-simultaneously).
    const impersonationExitInFlightRef = useRef(false);
    // Ordinary logout can also be reached by a user click and a terminal
    // change-password response at nearly the same time. One cleanup is enough.
    const logoutInFlightRef = useRef(false);
    // BAR-1564: who signs people in, from config.json (`pending` until read).
    const [authMode, setAuthMode] = useState<'pending' | 'firebase' | 'keycloak'>('pending');
    // BAR-1564: Keycloak sent the browser back to /auth/callback. The sign-in is
    // finished there (initializeApp) before the app renders anything else.
    const [oidcCallbackPending, setOidcCallbackPending] = useState(() => (
        typeof window !== 'undefined' && isOidcCallbackPath(window.location.pathname)
    ));
    // Why the last Keycloak sign-in did not finish, for the sign-in page. The
    // tone travels with the words: 'info' (a failed sign-out is news, not a
    // fault) shows in neutral text, 'error' in danger.
    const [loginNotice, setLoginNotice] = useState<{ text: string; tone: 'error' | 'info' } | null>(null);
    // BAR-1564: after BARK refused an account, or a sign-out Keycloak could
    // not finish, the next sign-in asks for credentials. Kept in
    // localStorage, so it survives a reload.
    const [forceLoginPrompt, setForceLoginPromptState] = useState(readForceLoginPrompt);
    const setForceLoginPrompt = useCallback((value: boolean) => {
        writeForceLoginPrompt(value);
        setForceLoginPromptState(value);
    }, []);
    // The sign-in page's sign-out view ran out while Keycloak's sign-out is
    // still on its way: it may never arrive.
    const handleSignOutLate = useCallback(() => setForceLoginPrompt(true), [setForceLoginPrompt]);
    // BAR-1564: true until the startup restore below (including the
    // /auth/callback path) has settled. The automatic Keycloak redirect on the
    // sign-in page waits for it, so a session that can still be restored is
    // never sent to sign in again.
    const [sessionRestorePending, setSessionRestorePending] = useState(true);
    // BAR-1564: set when a person signs out in keycloak mode, never cleared:
    // the page is leaving, and on a failed Keycloak sign-out the notice holds
    // the page.
    const [signingOut, setSigningOut] = useState(false);
    // Password-reset / invite deep link: read once on mount and strip from URL so a
    // refresh doesn't re-open the modal.
    const [resetToken, setResetToken] = useState<string | null>(() => {
        if (typeof window === 'undefined') return null;
        const params = new URLSearchParams(window.location.search);
        const t = params.get('reset_token');
        if (t) {
            params.delete('reset_token');
            const remaining = params.toString();
            const cleanUrl = window.location.pathname + (remaining ? `?${remaining}` : '') + window.location.hash;
            // Only the URL changes: React Router's state (its entry index) stays.
            window.history.replaceState(window.history.state, '', cleanUrl);
        }
        return t;
    });
    // BAR-126: invite token — same shape as reset_token but renders different copy.
    const [inviteToken, setInviteToken] = useState<string | null>(() => {
        if (typeof window === 'undefined') return null;
        const params = new URLSearchParams(window.location.search);
        const t = params.get('invite_token');
        if (t) {
            params.delete('invite_token');
            const remaining = params.toString();
            const cleanUrl = window.location.pathname + (remaining ? `?${remaining}` : '') + window.location.hash;
            window.history.replaceState(window.history.state, '', cleanUrl);
        }
        return t;
    });
    // BAR-1528: free-signup activation link (`/?signup_token=…`), handled like
    // invite_token: read once on mount, stripped from the URL, opens the
    // password modal in its own mode. Captured here and stripped in an effect
    // below, as `connect` is: React StrictMode runs state initializers twice in
    // development, and stripping inside the initializer would leave the run
    // React keeps with nothing.
    const [signupToken, setSignupToken] = useState<string | null>(() => {
        if (typeof window === 'undefined') return null;
        return new URLSearchParams(window.location.search).get('signup_token');
    });
    useEffect(() => {
        if (typeof window === 'undefined') return;
        const params = new URLSearchParams(window.location.search);
        if (!params.has('signup_token')) return;
        params.delete('signup_token');
        const remaining = params.toString();
        const cleanUrl = window.location.pathname + (remaining ? `?${remaining}` : '') + window.location.hash;
        window.history.replaceState(window.history.state, '', cleanUrl);
    }, []);
    // BAR-962: MCP connector hand-off. A connector (Claude, Codex, …) sends the
    // person here with ?connect=<oauth state>; they sign in exactly as usual
    // (same page, same methods); once authenticated the app hands its existing
    // session to the connector gateway and follows the connector's redirect.
    // Read once on mount, then stripped from the URL. (Capture and strip are
    // deliberately separate: React StrictMode runs state initializers twice
    // in development, so stripping inside the initializer would leave the
    // second run — the one React keeps — with nothing.)
    const [connectState, setConnectState] = useState<string | null>(() => {
        if (typeof window === 'undefined') return null;
        return new URLSearchParams(window.location.search).get('connect');
    });
    // BAR-1264: which gateway issued this state. A PR preview gateway is not
    // the one gateway this app has configured, and only the issuer can
    // validate the state — so it names itself here. apiService allowlists the
    // value to this environment before using it, and falls back otherwise.
    const [connectGatewayOrigin, setConnectGatewayOrigin] = useState<string | null>(() => {
        if (typeof window === 'undefined') return null;
        return new URLSearchParams(window.location.search).get('gateway_origin');
    });
    const [connectContext, setConnectContext] = useState<{ client_name: string; scopes: string[]; redirect_origin: string; preview_gateway?: string | null } | null>(null);
    const [connectSubmitting, setConnectSubmitting] = useState(false);
    // "Use another account" is signing out: the consent stays up until the
    // logout finishes, and must not hand over the account it is leaving.
    const [connectSwitching, setConnectSwitching] = useState(false);
    const [connectError, setConnectError] = useState<string | null>(null);
    const connectInFlightRef = useRef(false);
    useEffect(() => {
        if (typeof window === 'undefined') return;
        const params = new URLSearchParams(window.location.search);
        if (!params.has('connect') && !params.has('gateway_origin')) return;
        params.delete('connect');
        params.delete('gateway_origin');
        const remaining = params.toString();
        const cleanUrl = window.location.pathname + (remaining ? `?${remaining}` : '') + window.location.hash;
        window.history.replaceState(window.history.state, '', cleanUrl);
    }, []);
    const [isLoading, setIsLoading] = useState(false);
    const [streamingStatus, setStreamingStatus] = useState<string | null>(null);  // Streaming progress messages
    const [streamingResponse, setStreamingResponse] = useState<string>('');  // Accumulated streaming tokens
    const [streamingThinking, setStreamingThinking] = useState<string>('');  // Accumulated thinking tokens
    const [activityLog, setActivityLog] = useState<Array<{type: string; content: string; timestamp: number}>>([]);  // Activity timeline events
    // Refs to track accumulated values in real-time (React state updates are batched/async)
    const streamingThinkingRef = useRef<string>('');
    const streamingResponseRef = useRef<string>('');  // Track response for unified timeline
    const activityLogRef = useRef<Array<{type: string; content: string; timestamp: number}>>([]);
    const activitySequenceRef = useRef<number>(0);  // Sequence counter for guaranteed chronological ordering
    const activityLogUpdateTimeoutRef = useRef<NodeJS.Timeout | null>(null);  // Debounce activity log UI updates (prevents "drunk text")
    const rafScheduledRef = useRef<boolean>(false);  // Track if requestAnimationFrame is scheduled
    // reload_project triggers loadWorkspaceState from Report Library as a safety net
    // when incremental workspace_operations may not have been received (e.g. background research).
    const isReconnectingRef = useRef<boolean>(false);  // Track if we're handling a reconnection (to prevent useEffect subscription interference)
    // BAR-53: which project the in-flight reconnection belongs to (lets the
    // subscription effect clear a stale reconnect flag after a mid-reconnect switch).
    const reconnectProjectIdRef = useRef<string | null>(null);
    // BAR-40: holds the active reconnect subscription's unsubscribe so a new
    // reconnect (or teardown) can proactively close a prior one instead of
    // leaking an idle subscription under fast project switching.
    const reconnectUnsubscribeRef = useRef<(() => void) | null>(null);
    // Tracks whether the subscription-channel live-stream bootstrap has already
    // created a streaming chat placeholder for the current subscription. Reset
    // when the subscription tears down or when `complete` arrives post-catchup.
    const liveStreamPlaceholderRef = useRef<boolean>(false);
    const processedEventIdsRef = useRef<Set<string>>(new Set());  // Track processed event IDs to prevent duplicate processing
    const lastSubscriptionEventRef = useRef<number>(0);  // Timestamp of last subscription event — used by watchdog to detect activity
    const activeWorkflowRequestRef = useRef<boolean>(false);  // True while command WS request is awaiting completion
    const activeWorkflowRequestStartRef = useRef<number>(0);  // When it was marked active — bounds the watchdog suppression. Single-flight (one request at a time), so overwrite-on-next-start is fine; the ref flag, not this timestamp, is the gate.
    const [hasReconnectedSession, setHasReconnectedSession] = useState(false);  // True when reconnected to an active agent session
    const [activeToolCall, setActiveToolCall] = useState<string | null>(null);  // Currently executing tool
    const [pendingQuestion, setPendingQuestion] = useState<{question: string; context?: string; options?: string[]; question_type?: string} | null>(null);  // Pending ask_user_question
    const [activeWorkers, setActiveWorkers] = useState<{[key: string]: {status: string; area?: string; progress?: number}}>({});  // Active research workers
    const [workflowPhase, setWorkflowPhase] = useState<string | null>(null);  // Current workflow phase
    const [researchFindings, setResearchFindings] = useState<Array<{id: string; summary: string; source?: string; confidence?: number; timestamp: number}>>([]);  // Accumulated findings during research
    const [workflowError, setWorkflowError] = useState<string | null>(null);  // Error from workflow
    const [researchMission, setResearchMission] = useState<ResearchMission | null>(null);  // Research mission (question, plan, progress)
    // Legacy handoff package — retained only as chat-chip context for
    // GeminiChatArea; the HandoffChecklist / HandoffPackagePanel UI was
    // retired with the HandoffPackage path (D7).
    const [handoffPackage, setHandoffPackage] = useState<HandoffPackage | null>(null);
    // Workspace-details floating overlay (#89) — opened by clicking the
    // top-left validation pill. Shows deliverable shape + requirements +
    // validation; hosts the Re-validate button.
    const [isWorkspaceDetailsOpen, setIsWorkspaceDetailsOpen] = useState(false);
    const [workspaceDetailsRefreshSignal, setWorkspaceDetailsRefreshSignal] = useState(0);
    // BAR-126: change-password modal — opened from the user account menu.
    const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);
    const { researchProgressMap, getProgress, hasActiveResearch, dispatch: dispatchResearch, handleResearchEvent } = useResearchProgress();
    const researchProgressMapRef = useRef(researchProgressMap);
    // Keep ref in sync so event handlers can snapshot current state
    useEffect(() => { researchProgressMapRef.current = researchProgressMap; }, [researchProgressMap]);
    // Track which trackingId the current streaming session's research belongs to
    const currentResearchTrackingIdRef = useRef<string | null>(null);
    // Backward compat: compute single researchProgress for streaming timeline (first active task)
    const researchProgress = useMemo(() => {
        return Array.from(researchProgressMap.values()).find(task => task.isActive) || null;
    }, [researchProgressMap]);
    const [tokenUsage, setTokenUsage] = useState<{inputTokens: number; outputTokens: number; totalTokens: number}>({ ...DEFAULT_TOKEN_USAGE });  // Actual API token usage for context window display
    const [activeWebSocket, setActiveWebSocket] = useState<WebSocket | null>(null);  // Active WebSocket for cancel
    const [chats, setChats] = useState<{ [key: string]: any }>({});
    const chatsRef = useRef(chats);
    const lastReservedChatIdRef = useRef(0);
    useEffect(() => { chatsRef.current = chats; }, [chats]);
    const reserveChatId = useCallback(() => {
        const chatId = nextAvailableChatId(chatsRef.current, lastReservedChatIdRef.current);
        lastReservedChatIdRef.current = chatId;
        return chatId;
    }, []);
    const [activeChatId, setActiveChatId] = useState<number | null>(null);
    const activeChatIdRef = useRef(activeChatId);
    useEffect(() => { activeChatIdRef.current = activeChatId; }, [activeChatId]);
    const [regulatorySettings, setRegulatorySettings] = useState<RegulatorySettings>({});
    const [selectedReportId, setSelectedReportId] = useState<string | null>(null);
    const [isSettingsCollapsed, setIsSettingsCollapsed] = useState(true);
    const [userData, setUserData] = useState<any>(null);
    // BAR-53: live mirror of userData so frozen-closure callbacks (e.g.
    // handlePlanningWorkspaceUpdate, a useCallback([])) can read the current
    // email at call time instead of a stale first-render value (null).
    const userDataRef = useRef<any>(null);
    useEffect(() => { userDataRef.current = userData; }, [userData]);
    const [reportLibrary, setReportLibrary] = useState<{ [key: string]: any }>({});  // Cache for fetched reports
    
    // Report and UI state
    // Canvas mode state for report/workspace layout.
    const [isCanvasMode, setIsCanvasMode] = useState(true);
    const [activeView, setActiveView] = useState<string>('welcome');
    const previousReportPathRef = useRef(location.pathname);
    useEffect(() => {
        if (previousReportPathRef.current === location.pathname) return;
        previousReportPathRef.current = location.pathname;

        // A different path leaves the report surface and invalidates any
        // pending open. Query or fragment changes remain within the route and
        // must not close an open report. Report-opening routes therefore must
        // remain path-stable while their request is pending; any future
        // pathname-level report sub-route must revisit this cancellation rule.
        reportOpenRequestRef.current += 1;
        setSelectedReportId(null);
        setIsCanvasMode(false);
        setActiveView((view) => view === 'report' ? 'welcome' : view);
    }, [location.pathname]);

    // Chat-specific computed values
    const activeChat = activeChatId ? chats[activeChatId] : null;
    const openReports = activeChat?.openReports || {};
    const activeReportId = activeChat?.activeReportId || null;
    const currentReport = activeReportId ? openReports[activeReportId] : null;
    
    // Source panel state for reference system
    const [isSourcePanelOpen, setIsSourcePanelOpen] = useState(false);
    const [sourcePanelContent, setSourcePanelContent] = useState<ReferenceDetails | null>(null);
    const [highlightedNodeId, setHighlightedNodeId] = useState<string | null>(null);

    // Re-layout trigger - incremented on research completion to auto-organize graph
    const [reLayoutTrigger, setReLayoutTrigger] = useState(0);

    // Active research tracking helpers (localStorage)
    const ACTIVE_RESEARCH_KEY = 'bark_active_research';

    const setActiveResearch = (projectId: string) => {
        try {
            localStorage.setItem(ACTIVE_RESEARCH_KEY, JSON.stringify({
                projectId,
                startedAt: Date.now()
            }));
        } catch (e) {
            console.warn('Failed to save active research to localStorage:', e);
        }
    };

    const clearActiveResearch = () => {
        try {
            localStorage.removeItem(ACTIVE_RESEARCH_KEY);
        } catch (e) {
            console.warn('Failed to clear active research from localStorage:', e);
        }
    };

    const getActiveResearch = (): { projectId: string; startedAt: number } | null => {
        try {
            const data = localStorage.getItem(ACTIVE_RESEARCH_KEY);
            if (data) {
                const parsed = JSON.parse(data);
                // Expire after 30 minutes (research shouldn't take longer)
                if (Date.now() - parsed.startedAt < 30 * 60 * 1000) {
                    return parsed;
                } else {
                    localStorage.removeItem(ACTIVE_RESEARCH_KEY);
                }
            }
        } catch (e) {
            console.warn('Failed to get active research from localStorage:', e);
        }
        return null;
    };

    // Project store for chat history persistence
    const { activeProject, addChatMessage, addChatMessageLocal, getChatHistory, addLocalEvent, applyWorkspaceOperations, productionState, activeWorkspace, workspaceMode, setWorkspaceMode, reloadProjectEvents, dictWorkspace } = useProjectStore();

    // Handle plan/institution workspace update operations from WebSocket
    const handlePlanningWorkspaceUpdate = useCallback((operation: { type: string; id: string; payload: any }, expectedProjectId?: string) => {
        const { type, id, payload } = operation;
        // BAR-53: like applyWorkspaceOperations (F4), drop plan/institution
        // writes tagged for a project the user has switched away from. No-op
        // when expectedProjectId is omitted (preserves any existing caller).
        if (expectedProjectId && useProjectStore.getState().activeProject?.project_id !== expectedProjectId) {
            console.log('[App] Dropping stale plan/institution update for', expectedProjectId);
            return;
        }
        const store = useProjectStore.getState();

        // Plan workspace primitives
        if (type === 'objective') {
            store.updatePlan({ objective: payload.objective });
        } else if (type === 'plan_section') {
            const current = store.plan;
            const existingIdx = current.sections.findIndex(s => s.section_id === payload.section_id);
            const section = { ...payload, items: existingIdx >= 0 ? current.sections[existingIdx].items : [] };
            const sections = existingIdx >= 0
                ? current.sections.map((s, i) => i === existingIdx ? section : s)
                : [...current.sections, section];
            store.updatePlan({ sections });
        } else if (type === 'plan_item' || type === 'plan_edge') {
            // Plan nodes/edges are now LogicNodes/Edges on standard Compartment (#89)
            // Debounce: reload workspace state after a short delay so all events settle
            // BAR-53: this is a useCallback([]) — its `activeProject` and
            // `userData` closures are frozen at first render (activeProject is
            // null then), which silently disabled this debounced reload. Read
            // both live: project from the store, email from the userData ref.
            const liveProject = useProjectStore.getState().activeProject;
            const liveEmail = userDataRef.current?.email;
            if (liveProject?.project_id && liveEmail) {
                const timerProjectId = liveProject.project_id;
                if ((window as any).__planReloadTimer) clearTimeout((window as any).__planReloadTimer);
                (window as any).__planReloadTimer = setTimeout(() => {
                    // Guard: only reload if still on the same project (#89)
                    const currentProjectId = useProjectStore.getState().activeProject?.project_id;
                    if (currentProjectId !== timerProjectId) return;
                    store.loadWorkspaceState(timerProjectId, userDataRef.current?.email || liveEmail);
                }, 1500);
            }
            // Also update legacy plan state for backward compat
            if (type === 'plan_item') {
            const current = store.plan;
            const sectionId = payload.section_id;
            const sections = current.sections.map(s => {
                if (s.section_id !== sectionId) return s;
                const existingIdx = s.items.findIndex(i => i.item_id === payload.item_id);
                const items = existingIdx >= 0
                    ? s.items.map((item, i) => i === existingIdx ? payload : item)
                    : [...s.items, payload];
                return { ...s, items };
            });
            store.updatePlan({ sections });
            }
        } else if (type === 'plan_item_removed') {
            const current = store.plan;
            const sections = current.sections.map(s => ({
                ...s,
                items: s.items.filter(i => i.item_id !== payload.item_id),
            }));
            store.updatePlan({ sections });
        } else if (type === 'plan_section_removed') {
            const current = store.plan;
            const sections = current.sections.filter(s => s.section_id !== id);
            store.updatePlan({ sections });
        } else if (type === 'document') {
            const current = store.plan;
            const existingIdx = current.documents.findIndex(d => d.document_id === payload.document_id);
            const documents = existingIdx >= 0
                ? current.documents.map((d, i) => i === existingIdx ? payload : d)
                : [...current.documents, payload];
            store.updatePlan({ documents });
        } else if (type === 'plan') {
            store.updatePlan({ is_ready: payload.is_ready });
        }

        // Institution context primitives
        else if (type === 'profile_field') {
            const current = store.institutionProfile;
            const existingIdx = current.profile_fields.findIndex(f => f.field_id === id);
            const profile_fields = existingIdx >= 0
                ? current.profile_fields.map((f, i) => i === existingIdx ? payload : f)
                : [...current.profile_fields, payload];
            store.updateInstitutionProfile({ profile_fields });
        } else if (type === 'regulatory_choice') {
            const current = store.institutionProfile;
            const existingIdx = current.regulatory_choices.findIndex(c => c.choice_id === id);
            const regulatory_choices = existingIdx >= 0
                ? current.regulatory_choices.map((c, i) => i === existingIdx ? payload : c)
                : [...current.regulatory_choices, payload];
            store.updateInstitutionProfile({ regulatory_choices });
        } else if (type === 'key_figure') {
            const current = store.institutionProfile;
            const existingIdx = current.key_figures.findIndex(f => f.figure_id === id);
            const key_figures = existingIdx >= 0
                ? current.key_figures.map((f, i) => i === existingIdx ? payload : f)
                : [...current.key_figures, payload];
            store.updateInstitutionProfile({ key_figures });
        } else if (type === 'open_item') {
            const current = store.institutionProfile;
            const existingIdx = current.open_items.findIndex(i => i.item_id === id);
            if (existingIdx >= 0) {
                const open_items = current.open_items.map((item, i) =>
                    i === existingIdx ? { ...item, ...payload } : item
                );
                store.updateInstitutionProfile({ open_items });
            } else {
                store.updateInstitutionProfile({ open_items: [...current.open_items, payload] });
            }
        } else if (type === 'institution_context') {
            store.updateInstitutionProfile({ is_ready: payload.is_ready });
        }
    }, []);

    // Chat-specific report management functions
    const setOpenReports = (reports: { [reportId: string]: any }) => {
        if (!activeChatId) return;
        
        setChats(prev => ({
            ...prev,
            [activeChatId]: {
                ...prev[activeChatId],
                openReports: reports
            }
        }));
    };
    
    const setActiveReportId = (reportId: string | null) => {
        if (!activeChatId) return;

        setChats(prev => ({
            ...prev,
            [activeChatId]: {
                ...prev[activeChatId],
                activeReportId: reportId
            }
        }));
    };

    // Handle restoring research progress when reopening a project
    // Called from MainApplicationUI where dispatchResearch is not in scope
    const handleRestoreResearch = async (projectId: string) => {
        try {
            const researchState = await api.getResearchProgress(projectId);
            if (researchState.active && researchState.progress) {
                console.log('[App] Active background research found, restoring progress:', researchState.progress);
                const restoreTrackingId = (researchState.progress as any).tracking_id || 'restored';
                const restoreMsgId = (researchState.progress as any).message_id;
                currentResearchTrackingIdRef.current = restoreTrackingId;
                dispatchResearch({ type: 'RESTORE', trackingId: restoreTrackingId, messageId: restoreMsgId, payload: mapServerProgress(researchState.progress, restoreTrackingId) });
            } else if (researchState.completed && researchState.progress) {
                console.log('[App] Background research completed while away, showing completed state');
                const restoreTrackingId = (researchState.progress as any).tracking_id || 'restored';
                const restoreMsgId = (researchState.progress as any).message_id;
                const mapped = mapServerProgress(researchState.progress, restoreTrackingId);
                dispatchResearch({ type: 'RESTORE', trackingId: restoreTrackingId, messageId: restoreMsgId, payload: { ...mapped, isActive: false, currentPhase: 'complete' } });
            }
        } catch (e) {
            console.warn('[App] Failed to restore research progress:', e);
        }
    };

    // Handle reconnection to an active stream when reopening a project
    const handleReconnectToStream = (projectId: string) => {
        console.log('[App] handleReconnectToStream called for project:', projectId);
        // BAR-40: proactively close any prior reconnect subscription so a fast
        // A->B(->A) switch can't leave an idle, stale subscription open.
        if (reconnectUnsubscribeRef.current) {
            reconnectUnsubscribeRef.current();
            reconnectUnsubscribeRef.current = null;
        }
        isReconnectingRef.current = true;  // Prevent useEffect subscription from interfering
        reconnectProjectIdRef.current = projectId;  // BAR-53: remember which project this reconnect is for
        // BAR-146 (C — duplicate answer): the reconnect subscription is taking
        // over event processing for this session via Redis. Tear down any
        // still-installed per-send streaming callback so the direct-WS path and
        // the reconnect subscription can't both finalize the same events. The
        // underlying WS (if any) keeps running and its promise still resolves —
        // setStreamingCallback only controls UI forwarding (apiService guards on
        // `if (this._streamingCallback)`), so this is safe defense-in-depth.
        api.setStreamingCallback(null);
        setIsLoading(true);
        setHasReconnectedSession(true);  // Show "BARK is working" indicator

        // BAR-40: guard the synchronous setup below so that if starting the
        // subscription throws, isReconnectingRef can't stay stuck true (which
        // would block this session's future subscriptions). The per-event
        // callback logic is intentionally NOT wrapped — it must keep the flag
        // true through catchup.
        try {

        // Track whether catchup is complete - only act on `complete` events AFTER catchup
        // This prevents historical `complete` events from closing the subscription prematurely
        let catchupComplete = false;

        // Create streaming message placeholder in the chat
        setChats(prevChats => {
            const chatIds = Object.keys(prevChats).map(Number).filter(id => !isNaN(id));
            const currentChatId = chatIds.length > 0 ? Math.max(...chatIds) : null;
            if (!currentChatId) return prevChats;

            const currentChat = prevChats[currentChatId];
            if (!currentChat) return prevChats;

            return {
                ...prevChats,
                [currentChatId]: {
                    ...currentChat,
                    chatHistory: [...currentChat.chatHistory, {
                        sender: 'bark',
                        message: '',
                        activityLog: [],
                        streamingThinking: '',
                        streamingResponse: '',
                        _isStreaming: true,
                        _isReconnect: true
                    }]
                }
            };
        });

        // Reset streaming refs
        streamingThinkingRef.current = '';
        streamingResponseRef.current = '';
        activityLogRef.current = [];
        activitySequenceRef.current = 0;  // Reset sequence counter for new session
        // BAR-146 (C — duplicate answer): do NOT clear the cross-path dedup set
        // on reconnect. A reconnect resumes the SAME session and the Redis
        // catchup is about to REPLAY events this client may already have
        // processed (via the live subscription or the per-send streaming
        // callback). Clearing here removed that guard and let the replayed final
        // answer render a second time. event_ids are unique and the set is
        // size-capped (trimmed below), so retaining it never wrongly skips a
        // genuinely new event.

        // Subscribe directly - process ALL events to show stream progress
        const unsubscribe = api.subscribeToProjectEvents(projectId, (event) => {
            console.log('[Reconnect] Event:', event.type, catchupComplete ? '(live)' : '(catchup)');

            // BAR-53: if the user switched projects since this reconnection
            // started, tear it down. Otherwise (a) isReconnectingRef stays true
            // and blocks the new project's subscription from mounting, and
            // (b) this old stream's events write into the now-active project.
            if (useProjectStore.getState().activeProject?.project_id !== projectId) {
                console.log('[Reconnect] Project changed mid-reconnect — tearing down stale reconnection for', projectId);
                isReconnectingRef.current = false;
                reconnectProjectIdRef.current = null;
                reconnectUnsubscribeRef.current = null;
                unsubscribe?.();
                return;
            }

            // Track when catchup is complete
            if (event.type === 'catchup_complete') {
                catchupComplete = true;
                console.log('[Reconnect] Catchup complete - now listening for live events');
                return;
            }

            // Only close on complete/error AFTER catchup is done (i.e., live events)
            if ((event.type === 'complete' || event.type === 'error') && catchupComplete) {
                setIsLoading(false);
                setHasReconnectedSession(false);  // Hide "BARK is working" indicator
                isReconnectingRef.current = false;  // Allow useEffect to handle future subscriptions
                reconnectProjectIdRef.current = null;
                reconnectUnsubscribeRef.current = null;
                unsubscribe();
                console.log('[Reconnect] Live stream complete, reloading from Firestore');
                // BAR-53: only reload if still on THIS reconnect's project. The
                // F1 live-guard in loadProject short-circuits when activeProject
                // is transiently null mid-switch, so guard explicitly here too.
                // And read the email from the ref — the closure userData can be
                // stale across a logout/login as a different user mid-reconnect.
                if (useProjectStore.getState().activeProject?.project_id === projectId) {
                    const { loadProject } = useProjectStore.getState();
                    loadProject(projectId, userDataRef.current?.email || '');
                }
                return;
            }

            // Ignore historical complete/error events during catchup
            if (event.type === 'complete' || event.type === 'error') {
                console.log('[Reconnect] Ignoring historical', event.type, 'event during catchup');
                return;
            }

            // Deduplicate events using event_id or sequence
            const eventId = event.event_id || `${event.type}-${event.sequence || Date.now()}`;
            if (processedEventIdsRef.current.has(eventId)) {
                console.log('[Reconnect] Skipping duplicate event:', eventId);
                return;
            }
            processedEventIdsRef.current.add(eventId);

            if (event.type === 'workspace_update' && event.data?.operation?.type === 'reload_project') {
                console.log('[Reconnect] reload_project — reloading workspace state from Report Library');
                // BAR-53: the top-of-callback guard guarantees we are still on
                // `projectId`; read the email from the ref (closure userData can
                // be stale across a mid-reconnect logout/login as another user).
                if (userDataRef.current?.email) {
                    useProjectStore.getState().loadWorkspaceState(projectId, userDataRef.current.email);
                }
                return;
            }

            // During catchup, do NOT append historical tokens/thinking/tool_calls
            // to the active streaming placeholder — they belong to past turns,
            // already persisted in Firestore and rendered from chat history.
            // Only LIVE events (post catchup_complete) feed the current turn's
            // streaming state. Prevents the "all past responses glued into one
            // giant blob" symptom on reconnect.
            if (!catchupComplete) {
                return;
            }

            if (event.type === 'token' || event.type === 'response' || event.type === 'response_stream') {
                const token = event.data?.token || event.data?.content || '';
                streamingResponseRef.current += token;
                // NOTE: requestAnimationFrame syncs ref to state at 60fps
            } else if (event.type === 'thinking' || event.type === 'thinking_block' || event.type === 'thinking_stream') {
                const token = event.data?.token || event.data?.content || '';
                streamingThinkingRef.current += token;
                // NOTE: requestAnimationFrame syncs ref to state at 60fps
            } else if (event.type === 'tool_call') {
                const toolName = event.data?.tool || 'tool';
                setActiveToolCall(toolName);
                activityLogRef.current = [...activityLogRef.current, {
                    type: 'tool_call',
                    content: `🔧 ${toolName}`,
                    timestamp: Date.now()
                }];
                setActivityLog([...activityLogRef.current]);
            } else if (event.type === 'tool_result') {
                setActiveToolCall(null);
            }
        });
        // BAR-40: remember this reconnect's unsubscribe so the next reconnect
        // (or teardown) can proactively close it.
        reconnectUnsubscribeRef.current = unsubscribe;
        // --- end of the try opened near the top of handleReconnectToStream;
        // it guards ONLY the synchronous subscription setup above (the async
        // per-event callback is not inside it). ---
        } catch (err) {
            console.error('[Reconnect] Failed to start reconnect subscription for', projectId, err);
            isReconnectingRef.current = false;
            reconnectProjectIdRef.current = null;
            reconnectUnsubscribeRef.current = null;
            setIsLoading(false);
            setHasReconnectedSession(false);
        }
    };

    // Report fetching functions
    const fetchReportFromLibrary = useCallback(async (reportId: string): Promise<GetReportResult> => {
        if (!userData?.email) {
            return {
                success: false,
                reason: 'unauthorized',
                error: 'Your account email is unavailable',
            };
        }

        return getReport(reportId, userData.email);
    }, [userData?.email]);

    // Keep showToast in the dependency list: the provider value may be
    // replaced even though the current provider normally remains root-mounted.
    const handleReportLinkClick = useCallback(async (reportId: string) => {
        try {
            const requestId = ++reportOpenRequestRef.current;
            console.log(`🔗 Attempting to open report with ID: "${reportId}"`);
            console.log(`🔗 Report ID type: ${typeof reportId}, starts with recent: ${reportId.startsWith('recent:')}`);
            
            // A report link belongs to the chat where it was clicked. Do not
            // fall back to a different chat if that source chat has gone away.
            // The request counter makes the newest click authoritative; the
            // post-fetch ref checks then require the same live source chat.
            const currentActiveChatId = activeChatIdRef.current;
            if (!currentActiveChatId || !chatsRef.current[currentActiveChatId]) {
                console.error('🔗 Report link clicked without a valid active chat');
                showToast({
                    variant: 'error',
                    message: 'This report link is no longer connected to an active chat.',
                });
                return;
            }
            
            const reportResult = await fetchReportFromLibrary(reportId);
            if (requestId !== reportOpenRequestRef.current) {
                console.log(`🔗 Ignoring report result after navigation: ${reportId}`);
                return;
            }
            if (
                activeChatIdRef.current !== currentActiveChatId
                || !chatsRef.current[currentActiveChatId]
            ) {
                console.log(`🔗 Ignoring report result after active chat changed: ${reportId}`);
                return;
            }
            if (reportResult.success) {
                const report = reportResult.report;
                setReportLibrary(prev => ({
                    ...prev,
                    [reportId]: report,
                }));
                // Add to chat's open reports collection and set as active
                const transformedReport = {
                    title: report.title,
                    content: report.report_content,
                    reportId,
                    references: report.references || []
                };
                
                // Update the chat state with the new report
                setChats(prev => {
                    const sourceChat = prev[currentActiveChatId];
                    if (!sourceChat) return prev;

                    return {
                        ...prev,
                        [currentActiveChatId]: {
                            ...sourceChat,
                            openReports: {
                                ...(sourceChat.openReports || {}),
                                [reportId]: transformedReport
                            },
                            activeReportId: reportId
                        }
                    };
                });
                
                setSelectedReportId(reportId);
                setIsCanvasMode(true);
                setActiveView('report');
                
                console.log(`Switched to report: ${report.title}`);
            } else {
                console.error(`Failed to load report ${reportId}:`, reportResult.reason);
                
                // Check if this might be an old cached report ID format
                if (
                    reportResult.reason === 'not_found'
                    && isLegacyReportLinkId(reportId)
                ) {
                    console.log(`🧹 Detected old report ID format (${reportId})`);
                    showToast({
                        variant: 'warning',
                        message: 'This report link is outdated. The report it pointed to can no longer be opened.',
                    });
                } else {
                    const retryable = reportResult.reason === 'network' || reportResult.reason === 'server';
                    showToast({
                        variant: 'error',
                        message: reportOpenFailureMessage(undefined, reportResult.reason),
                        action: retryable
                            ? {
                                label: 'Retry',
                                onClick: () => { void handleReportLinkClick(reportId); },
                            }
                            : undefined,
                    });
                }
            }
        } catch (error) {
            console.error(`Error handling report link click:`, error);
            showToast({
                variant: 'error',
                message: 'An error occurred while trying to load the report. Please try again.',
                action: {
                    label: 'Retry',
                    onClick: () => { void handleReportLinkClick(reportId); },
                },
            });
        }
    }, [fetchReportFromLibrary, showToast]);

    const handleSwitchToReport = (reportId: string) => {
        if (!activeChatId || !activeChat) return;
        
        setChats(prev => ({
            ...prev,
            [activeChatId]: {
                ...prev[activeChatId],
                activeReportId: reportId
            }
        }));
    };

    const handleDeleteChat = (chatId: number) => {
        // Remove the chat from chats state
        setChats(prev => {
            const updatedChats = { ...prev };
            delete updatedChats[chatId];
            return updatedChats;
        });

        // If this was the active chat, switch to the most recent remaining chat or clear
        if (chatId === activeChatId) {
            const remainingChatIds = Object.keys(chats)
                .map(id => parseInt(id))
                .filter(id => id !== chatId)
                .sort((a, b) => b - a);
                
            if (remainingChatIds.length > 0) {
                setActiveChatId(remainingChatIds[0]);
            } else {
                setActiveChatId(null);
                setActiveView('welcome');
                setIsCanvasMode(false);
            }
        }

        console.log(`🗑️ Deleted chat ${chatId}`);
        
        // Save user state after deletion
        if (userData?.email) {
            setTimeout(() => saveUserState(userData.email), 100);
        }
    };
    
    const handleRenameChat = (chatId: number, newTitle: string) => {
        setChats(prev => ({
            ...prev,
            [chatId]: {
                ...prev[chatId],
                title: newTitle
            }
        }));
        
        console.log(`✏️ Renamed chat ${chatId} to: ${newTitle}`);
        
        // Update user state to persist the rename
        if (userData?.email) {
            setTimeout(() => saveUserState(userData.email), 100);
        }
    };

    const handleCloseReport = (reportId?: string) => {
        const idToClose = reportId || activeReportId;
        if (!idToClose || !activeChatId) return;
        
        // Remove from chat's open reports and update active report
        const updatedOpenReports = { ...openReports };
        delete updatedOpenReports[idToClose];
        
        let newActiveReportId = activeReportId;
        
        // If closing the active report, switch to another or clear
        if (idToClose === activeReportId) {
            const remainingReportIds = Object.keys(updatedOpenReports);
            if (remainingReportIds.length > 0) {
                // Switch to the most recently opened report
                newActiveReportId = remainingReportIds[remainingReportIds.length - 1];
                setActiveView('report');
            } else {
                // No reports left open: back to the workspace view.
                newActiveReportId = null;
                setSelectedReportId(null);
                setActiveView('welcome');
            }
        }
        
        // Update the chat state
        setChats(prev => ({
            ...prev,
            [activeChatId]: {
                ...prev[activeChatId],
                openReports: updatedOpenReports,
                activeReportId: newActiveReportId,
                selectedReportId: newActiveReportId || null
            }
        }));
        
        console.log(`📝 Closed report ${idToClose} from chat ${activeChatId}`);
    };

    // Load research mission when project loads
    useEffect(() => {
        if (activeProject && activeProject.research_mission) {
            setResearchMission(activeProject.research_mission as ResearchMission);
            console.log('[Project Load] Research mission loaded:', activeProject.research_mission.query);
        } else if (activeProject && !activeProject.research_mission) {
            // Project loaded but has no research mission
            setResearchMission(null);
        }
    }, [activeProject]);

    // (window.handleHandoffClick registration removed with the retired
    // HandoffChecklist panel — chat handoff chips degrade to
    // non-interactive spans, which ChatMessageMarkdown supports.)

    // 60fps streaming display - uses requestAnimationFrame for smooth rendering
    // Tokens update refs instantly, this effect syncs to state at 60fps
    useEffect(() => {
        if (!isLoading) {
            rafScheduledRef.current = false;
            return;
        }

        let animationFrameId: number;
        const syncToState = () => {
            setStreamingThinking(streamingThinkingRef.current);
            setStreamingResponse(streamingResponseRef.current);
            animationFrameId = requestAnimationFrame(syncToState);
        };

        animationFrameId = requestAnimationFrame(syncToState);
        rafScheduledRef.current = true;

        return () => {
            cancelAnimationFrame(animationFrameId);
            rafScheduledRef.current = false;
        };
    }, [isLoading]);

    // isLoading watchdog — polls checkActiveSession while isLoading is true.
    // Only resets isLoading if BOTH conditions are met:
    //   1. Backend reports no active session (Redis key expired or deleted)
    //   2. No subscription events received in the last 30 seconds
    // This prevents premature reset when the active_session TTL hasn't been refreshed yet
    // but events are still flowing (the agent is still working).
    // When resetting, we only clear interaction state (spinners, input lock) — NOT accumulated
    // data (activityLog, researchProgress). Those are valuable and should persist until the
    // next user message or project reload.
    useEffect(() => {
        if (!isLoading) return;

        const projectId = activeProject?.project_id;
        if (!projectId) return;

        let intervalId: ReturnType<typeof setInterval> | null = null;
        let cancelled = false;

        const poll = async () => {
            if (cancelled) return;

            // If subscription events are still flowing, the agent is still active
            const timeSinceLastEvent = Date.now() - lastSubscriptionEventRef.current;
            if (timeSinceLastEvent < 30_000) {
                console.log('[Watchdog] Subscription active (last event %ds ago) — skipping check', Math.round(timeSinceLastEvent / 1000));
                return;
            }

            // Suppress the reset while a command request is genuinely in flight,
            // but bound it: if the ref has been stuck active for >5min (request
            // threw before clearing it, or the component unmounted mid-flight),
            // ignore it so the watchdog can't be permanently disabled.
            const requestActiveMs = Date.now() - activeWorkflowRequestStartRef.current;
            if (activeWorkflowRequestRef.current && requestActiveMs < MAX_WORKFLOW_REQUEST_DURATION_MS) {
                console.log('[Watchdog] Command WebSocket request still active (last subscription event %ds ago) — skipping reset', Math.round(timeSinceLastEvent / 1000));
                return;
            }

            try {
                const session = await api.checkActiveSession(projectId);
                if (cancelled) return;  // Effect cleaned up while awaiting
                if (!session.active) {
                    console.log('[Watchdog] Backend reports no active session (last event %ds ago) — resetting isLoading', Math.round(timeSinceLastEvent / 1000));
                    // Reset interaction state only — preserve accumulated data
                    setStreamingStatus(null);
                    setActiveToolCall(null);
                    setActiveWorkers({});
                    setWorkflowPhase(null);
                    setWorkflowError(null);
                    setHasReconnectedSession(false);
                    isReconnectingRef.current = false;
                    setIsLoading(false);

                    // Reload events from Firestore to get the final authoritative state
                    const { reloadProjectEvents } = useProjectStore.getState();
                    reloadProjectEvents(projectId, userData?.email || 'ui-test-user');

                    // Stop polling after reset
                    if (intervalId) clearInterval(intervalId);
                }
            } catch (e) {
                console.warn('[Watchdog] Failed to check active session:', e);
                // Don't reset on network errors — the session might still be active
            }
        };

        // Wait 10 seconds before first poll (backend needs time to register the session)
        // then poll every 5 seconds (less aggressive than before)
        const delayId = setTimeout(() => {
            if (cancelled) return;
            poll();  // First check
            intervalId = setInterval(poll, 5000);
        }, 10000);

        return () => {
            cancelled = true;
            clearTimeout(delayId);
            if (intervalId) clearInterval(intervalId);
        };
    }, [isLoading, activeProject?.project_id, userData?.email]);

    // Global function for reference link clicks (opens SourcePanel)
    useEffect(() => {
        (window as any).handleReferenceClick = async (markerId: string) => {
            try {
                // Get workspace ID and user ID from active project
                const projectId = activeProject?.project_id;
                const userId = userData?.email;
                if (!projectId || !userId) {
                    console.warn('[Reference] No active project or user, cannot fetch reference details');
                    return;
                }

                // Workspace ID format is ws_{project_id}
                const workspaceId = `ws_${projectId}`;
                console.log('[Reference] Fetching details for marker:', markerId);

                // Fetch reference details from backend
                const details = await api.getReferenceDetails(workspaceId, markerId);

                // Highlight the corresponding node in Evidence Board
                if (details.node_ids?.length > 0) {
                    setHighlightedNodeId(details.node_ids[0]);
                    // Auto-clear highlight after 3 seconds
                    setTimeout(() => setHighlightedNodeId(null), 3000);
                }

                // Open source panel with finding details
                setSourcePanelContent(details);
                setIsSourcePanelOpen(true);

                console.log('[Reference] Opened source panel:', details);
            } catch (error) {
                console.error('[Reference] Error fetching reference details:', error);
            }
        };

        return () => {
            delete (window as any).handleReferenceClick;
        };
    }, [activeProject?.project_id, userData?.email]);

    // =========================================================================
    // PROJECT EVENT SUBSCRIPTION - Single Source of Truth from Redis
    // =========================================================================
    // Subscribe to project events when a project is active
    // Events flow: Backend -> Redis -> This subscription -> activityLog
    // This is the ONLY source for streaming events - no mixing!
    useEffect(() => {
        if (!activeProject?.project_id) {
            return;
        }

        // BAR-53: if a reconnection is flagged but belongs to a DIFFERENT
        // project than the one we're subscribing for now, the user switched
        // projects mid-reconnect. Clear the stale flag so this project gets its
        // own subscription instead of being wedged out. (The reconnect handler
        // clears the flag on its own events, but if the old stream goes silent
        // after the switch, this is the only thing that unwedges it.)
        if (isReconnectingRef.current && reconnectProjectIdRef.current && reconnectProjectIdRef.current !== activeProject.project_id) {
            console.log('[Subscription] Clearing stale reconnection flag from previous project:', reconnectProjectIdRef.current);
            isReconnectingRef.current = false;
            reconnectProjectIdRef.current = null;
        }

        // Skip if handleReconnectToStream is managing the subscription
        if (isReconnectingRef.current) {
            console.log('[Subscription] Skipping - reconnection in progress');
            return;
        }

        const projectId = activeProject.project_id;
        console.log('[Subscription] Subscribing to project events:', projectId);
        // New subscription — clear any stale live-stream bootstrap flag from a
        // previous project so this project can create its own placeholder on
        // first live event.
        liveStreamPlaceholderRef.current = false;

        // Track whether historical event catchup is complete
        // Before catchup_complete: events are historical (already in Firestore) - skip them
        // After catchup_complete: events are live - process them
        let catchupComplete = false;
        let unsubscribe: (() => void) | null = null;

        // Clear event deduplication set for new subscription
        processedEventIdsRef.current = new Set();

        // Ensure API is initialized before subscribing
        const initAndSubscribe = async () => {
            try {
                await api.initialize();
            } catch (e) {
                console.error('[Subscription] Failed to initialize API:', e);
                return;
            }

            // Re-check after async initialization - handleReconnectToStream may have started
            if (isReconnectingRef.current) {
                console.log('[Subscription] Skipping after init - reconnection in progress');
                return;
            }

            // Subscribe to project events from Redis via WebSocket
            unsubscribe = api.subscribeToProjectEvents(projectId, (event) => {
            // Guard: only process events for the currently active project.
            // Prevents leakage when old subscription hasn't closed yet after project switch.
            const currentProjectId = useProjectStore.getState().activeProject?.project_id;
            if (currentProjectId && event.project_id && event.project_id !== currentProjectId) {
                console.log('[Subscription] Ignoring event for different project:', event.project_id, 'current:', currentProjectId);
                return;
            }

            console.log('[Subscription] Event received:', event.type, event.data);

            // Handle catchup_complete marker - after this, events are live
            if (event.type === 'catchup_complete') {
                catchupComplete = true;
                console.log('[Subscription] Catchup complete, now processing live events');
                // After catchup, check if research is still running and restore progress box
                const restoreProjectId = useProjectStore.getState().activeProject?.project_id;  // BAR-53: live read, not the subscription closure
                if (restoreProjectId) {
                    api.getResearchProgress(restoreProjectId).then((researchState: any) => {
                        if (researchState.active && researchState.progress) {
                            console.log('[Subscription] Active research detected after catchup, restoring progress box');
                            const trackingId = researchState.progress.tracking_id || 'restored';
                            currentResearchTrackingIdRef.current = trackingId;
                            dispatchResearch({ type: 'RESTORE', trackingId, payload: mapServerProgress(researchState.progress, trackingId) });
                        }
                    }).catch(() => {});
                }
                return;
            }

            // Skip historical events (already in Firestore with correct activityLog)
            // EXCEPT research progress events — we need these to rebuild the progress box
            if (!catchupComplete) {
                const researchEventTypes = ['research_started', 'phase_change', 'finding', 'research_wave_start',
                    'coverage_metrics', 'research_gap_analysis', 'premise_result', 'research_complete'];
                if (!researchEventTypes.includes(event.type)) {
                    return;
                }
            }

            // Live-stream bootstrap: when post-catchup events from an ongoing
            // agent turn arrive, but no streaming chat entry exists (because
            // checkActiveSession returned false and handleReconnectToStream
            // was never called), create a placeholder now. Without it, the
            // refs we populate below have no chat row to paint into — tokens
            // pile up invisibly. Run only for "agent is talking" event types,
            // and only once per subscription (idempotent).
            const STREAMING_EVENT_TYPES = new Set([
                'remote_message_start', 'thinking', 'token', 'tool_call',
                'tool_result', 'response', 'response_stream',
            ]);
            if (catchupComplete && STREAMING_EVENT_TYPES.has(event.type) && !liveStreamPlaceholderRef.current) {
                liveStreamPlaceholderRef.current = true;
                console.log('[Subscription] Bootstrapping live stream placeholder (no active-session but events flowing)');
                // Reset streaming refs so the placeholder starts clean
                streamingThinkingRef.current = '';
                streamingResponseRef.current = '';
                activityLogRef.current = [];
                activitySequenceRef.current = 0;
                setStreamingThinking('');
                setStreamingResponse('');
                setActivityLog([]);
                setIsLoading(true);
                setHasReconnectedSession(true);
                // Add a streaming placeholder to the current chat
                setChats(prevChats => {
                    const chatIds = Object.keys(prevChats).map(Number).filter(id => !isNaN(id));
                    const currentChatId = chatIds.length > 0 ? Math.max(...chatIds) : null;
                    if (!currentChatId) return prevChats;
                    const currentChat = prevChats[currentChatId];
                    if (!currentChat) return prevChats;
                    // Don't add a second placeholder if the last entry is already streaming
                    const last = currentChat.chatHistory?.[currentChat.chatHistory.length - 1];
                    if (last?._isStreaming) return prevChats;
                    return {
                        ...prevChats,
                        [currentChatId]: {
                            ...currentChat,
                            chatHistory: [...currentChat.chatHistory, {
                                sender: 'bark',
                                message: '',
                                activityLog: [],
                                streamingThinking: '',
                                streamingResponse: '',
                                _isStreaming: true,
                                _isReconnect: true,
                            }],
                        },
                    };
                });
            }

            // Deduplicate events using event_id or sequence to prevent duplicate processing
            const eventId = event.event_id || `${event.type}-${event.sequence || Date.now()}`;
            if (processedEventIdsRef.current.has(eventId)) {
                console.log('[Subscription] Skipping duplicate event:', eventId);
                return;
            }
            processedEventIdsRef.current.add(eventId);
            // Limit set size to prevent memory leaks (keep last 1000 events)
            if (processedEventIdsRef.current.size > 1000) {
                const entries = Array.from(processedEventIdsRef.current);
                processedEventIdsRef.current = new Set(entries.slice(-500));
            }

            // Record activity timestamp — watchdog uses this to avoid premature reset
            lastSubscriptionEventRef.current = Date.now();

            // Extract tracking_id from event for per-research correlation (fall back to message_id for backward compat)
            const eventTrackingId = event.tracking_id || event.message_id || currentResearchTrackingIdRef.current || 'unknown';

            // Map backend event types to frontend activity log format
            // Use sequence counter to ensure unique timestamps for sorting
            // Use backend sequence number for ordering (authoritative), with local counter as tiebreaker
            const getNextTimestamp = () => {
                const seq = event.sequence || 0;
                activitySequenceRef.current++;
                // sequence * 1000 ensures backend order is primary, local counter breaks ties
                return seq * 1000 + activitySequenceRef.current;
            };

            switch (event.type) {
                case 'thinking':
                    // Accumulate thinking tokens - direct setState (fast string append)
                    const thinkingToken = event.data?.token || '';
                    streamingThinkingRef.current += thinkingToken;
                    setStreamingStatus('Thinking...');
                    // NOTE: requestAnimationFrame syncs ref to state at 60fps
                    break;

                case 'token':
                    // Accumulate response tokens (ref only - requestAnimationFrame syncs to state)
                    const responseToken = event.data?.token || '';
                    streamingResponseRef.current += responseToken;
                    break;

                case 'tool_call':
                    // Capture accumulated response as block before tool call (for chronological ordering)
                    if (streamingResponseRef.current.trim().length > 0) {
                        activityLogRef.current = activityLogRef.current.filter(e => e.type !== 'response_stream');
                        activityLogRef.current = [...activityLogRef.current, {
                            type: 'response',
                            content: streamingResponseRef.current.trim(),
                            timestamp: getNextTimestamp()
                        }];
                        streamingResponseRef.current = '';
                        setStreamingResponse('');
                    }

                    // Capture thinking as block before tool call (for chronological ordering)
                    if (streamingThinkingRef.current.trim().length > 0) {
                        activityLogRef.current = activityLogRef.current.filter(e => e.type !== 'thinking_stream');
                        activityLogRef.current = [...activityLogRef.current, {
                            type: 'thinking_block',
                            content: streamingThinkingRef.current.trim(),
                            timestamp: getNextTimestamp()
                        }];
                        streamingThinkingRef.current = '';
                        setStreamingThinking('');
                    }

                    // Add tool call to activity log
                    const toolName = event.data?.tool || 'tool';
                    setActiveToolCall(toolName);
                    activityLogRef.current = [...activityLogRef.current, {
                        type: 'tool_call',
                        content: `🔧 ${toolName}`,
                        timestamp: getNextTimestamp()
                    }];
                    setActivityLog([...activityLogRef.current]);
                    setStreamingStatus(`Using ${toolName}...`);
                    break;

                case 'tool_result': {
                    setActiveToolCall(null);
                    const resultToolName = event.data?.tool || 'tool';
                    // A tool may say what it concluded ("5 approved · 2 flagged").
                    // Prefer that over completed/failed: validate_work reports
                    // success=false for any flagged node, which used to print
                    // "validate_work failed" and read as a crash.
                    const resultOutcome = describeToolOutcome(resultToolName, event.data);
                    activityLogRef.current = [...activityLogRef.current, {
                        type: event.data?.success ? 'tool_result' : 'tool_error',
                        content: resultOutcome ?? `${resultToolName} ${event.data?.success ? 'completed' : 'failed'}`,
                        timestamp: getNextTimestamp()
                    }];
                    setActivityLog([...activityLogRef.current]);
                    setStreamingStatus(resultOutcome ?? (event.data?.success ? `${resultToolName} complete` : `${resultToolName} failed`));
                    break;
                }

                case 'phase_change':
                    const newPhase = event.data?.new_phase || event.data?.to || 'unknown';
                    setWorkflowPhase(newPhase);
                    setStreamingStatus(`Phase: ${newPhase}`);
                    handleResearchEvent(eventTrackingId, 'phase_change', event.data || {});
                    activityLogRef.current = [...activityLogRef.current, {
                        type: 'phase',
                        content: `Entering ${newPhase} phase`,
                        timestamp: getNextTimestamp()
                    }];
                    setActivityLog([...activityLogRef.current]);
                    break;

                case 'research_started': {
                    const subTrackingId = event.data?.tracking_id || eventTrackingId;
                    currentResearchTrackingIdRef.current = subTrackingId;
                    dispatchResearch({ type: 'START', trackingId: subTrackingId, messageId: event.message_id });

                    // Persist research_progress placeholder to local event store
                    // so the progress box renders from getDisplayHistory() after streaming ends
                    const subStartProjectId = useProjectStore.getState().activeProject?.project_id;
                    if (subStartProjectId) {
                        const { addLocalEvent } = useProjectStore.getState();
                        const initialSnapshot = JSON.stringify({
                            isActive: true,
                            trackingId: subTrackingId,
                            currentPhase: 'idle',
                            phases: [],
                            waves: [],
                            findings: [],
                            coverage: null,
                            gaps: null,
                            premiseCorrections: 0,
                            totalPremises: 0,
                            startedAt: Date.now(),
                        });
                        addLocalEvent({
                            project_id: subStartProjectId,
                            type: 'research_progress',
                            content: initialSnapshot,
                            timestamp: new Date().toISOString(),
                            message_id: event.message_id || subTrackingId,
                        });
                        console.log('[Subscription] Added research_progress local event for', subTrackingId);
                    }
                    break;
                }

                case 'research_wave_start':
                case 'coverage_metrics':
                case 'research_gap_analysis':
                case 'premise_result':
                case 'research_complete':
                    handleResearchEvent(eventTrackingId, event.type, event.data || {});
                    break;

                case 'workspace_update':
                    // Handle handoff events (legacy chat-chip context only)
                    if (event.data?.primitive_type === 'handoff') {
                        setHandoffPackage(event.data.payload as HandoffPackage);
                    }
                    // Apply workspace operation in real-time
                    if (event.data?.operation) {
                        console.log('[Subscription] Applying workspace operation:', event.data.operation.type);
                        if (event.data.operation.type === 'reload_project') {
                            console.log('[Subscription] reload_project — reloading workspace state from Report Library');
                            const liveReloadPid = useProjectStore.getState().activeProject?.project_id;  // BAR-53: live, not closure
                            const liveReloadEmail = userDataRef.current?.email;  // BAR-53: live email, not the closure
                            if (liveReloadPid && liveReloadEmail) {
                                useProjectStore.getState().loadWorkspaceState(liveReloadPid, liveReloadEmail);
                            }
                        } else if (event.data.operation.type === 'workspace_switch') {
                            const switchData = event.data.operation.data;
                            // BAR-53 (F5): only move the shared workspace indicator if we're
                            // still on the project this subscription was created for — an
                            // untagged switch op on a not-yet-closed socket must not retarget
                            // the new project's indicator.
                            if (switchData?.to && useProjectStore.getState().activeProject?.project_id === projectId) {
                                const target = switchData.to as string;
                                const validModes = ['plan', 'institution_context', 'research', 'production', 'output_package'];
                                const targetMode = validModes.includes(target) ? target as any : 'research';
                                const targetNodeName = switchData.target_node_name || '';
                                console.log('[Subscription] BARK switched workspace to:', target, targetNodeName ? `(${targetNodeName})` : '');
                                useProjectStore.getState().setBarkWorkspace(targetMode, projectId);
                                useProjectStore.getState().setBarkWorkspaceName(targetNodeName, projectId);
                            }
                        } else if (
                            event.data.operation.type === 'requirements_updated' ||
                            event.data.operation.type === 'validation_complete'
                        ) {
                            // Bump the overlay's refresh signal so it re-fetches
                            // requirements + validation state. No-op when the
                            // overlay is closed; cheap when open.
                            setWorkspaceDetailsRefreshSignal((n) => n + 1);
                            // Also route through the store so the Spec panel's
                            // requirementsRefreshSignal bumps on THIS channel too
                            // (the streaming handler already does; the store
                            // strips these ops safely — no dict payload).
                            useProjectStore.getState().applyWorkspaceOperations([event.data.operation]);
                        } else {
                            // Scope guard (#89): drop workspace_update ops whose
                            // workspace_id does not match the currently-viewed
                            // workspace. Otherwise e.g. add_finding events from
                            // the research workspace leak into the plan view's
                            // dict. Backend emits event.data.workspace_id on
                            // every per-workspace op; when absent we fall back
                            // to the legacy behaviour (apply).
                            const opWsId = event.data?.workspace_id
                                || event.data?.operation?.data?.workspace_id
                                || event.data?.payload?.workspace_id;
                            const currentWsId = useProjectStore.getState().dictWorkspace?.workspace_id;
                            if (opWsId && currentWsId && opWsId !== currentWsId) {
                                console.log(
                                    '[Subscription] Dropping cross-workspace op:',
                                    event.data.operation.type,
                                    'op_ws=', opWsId,
                                    'current_ws=', currentWsId,
                                );
                            } else {
                                handlePlanningWorkspaceUpdate(event.data.operation, event.project_id ?? projectId);
                                // BAR-53: tag ops with the event's source project so a late op
                                // on a not-yet-closed socket can't write into another project.
                                applyWorkspaceOperations([event.data.operation], event.project_id ?? projectId);
                            }
                        }
                    }
                    break;

                case 'user_question':
                    // Flush any accumulated response before the question card
                    if (streamingResponseRef.current.trim().length > 0) {
                        activityLogRef.current = activityLogRef.current.filter(e => e.type !== 'response_stream');
                        activityLogRef.current = [...activityLogRef.current, {
                            type: 'response',
                            content: streamingResponseRef.current.trim(),
                            timestamp: getNextTimestamp()
                        }];
                        streamingResponseRef.current = '';
                        setStreamingResponse('');
                    }
                    // Add user_question to activity log with full data as JSON
                    const uqEntry = {
                        type: 'user_question',
                        content: JSON.stringify(event.data || {}),
                        timestamp: getNextTimestamp()
                    };
                    activityLogRef.current = [...activityLogRef.current, uqEntry];
                    console.log('[USER_QUESTION] Added to activityLog:', uqEntry.content.substring(0, 100));
                    console.log('[USER_QUESTION] activityLog types:', activityLogRef.current.map(e => e.type));
                    setActivityLog([...activityLogRef.current]);
                    // Clear the active tool call indicator and show question in input area
                    setActiveToolCall(null);
                    setPendingQuestion(event.data || null);
                    setStreamingStatus('Waiting for your answer...');
                    break;

                case 'error':
                    const errorMsg = event.data?.error || 'Unknown error';
                    setStreamingStatus(`Error: ${errorMsg}`);
                    setWorkflowError(errorMsg);
                    break;

                case 'token_usage': {
                    // CURRENT context window usage — not cumulative; it is the
                    // latest API call's prompt size.
                    //
                    // `input_tokens` alone is NOT the context. Anthropic reports
                    // cached prompt tokens separately, so once prompt caching is
                    // on (re-enabled 2026-09-20) `input_tokens` is only the
                    // UNCACHED remainder. Reading it alone under-reported the
                    // bar by however much was served from cache — a 55k reading
                    // against a real ~1.7M-token-read turn. The model still sees
                    // every one of these tokens; caching changes what they cost,
                    // not how much context is consumed.
                    //
                    // The emitter has always sent all three (emitter.py:471-477);
                    // this case just never summed them.
                    const billedInput = event.data?.input_tokens || 0;
                    const cacheRead = event.data?.cache_read_input_tokens || 0;
                    const cacheWrite = event.data?.cache_creation_input_tokens || 0;
                    const contextUsed = billedInput + cacheRead + cacheWrite;
                    const currentOutputTokens = event.data?.output_tokens || 0;
                    console.log(
                        '[TOKEN_USAGE] Context window usage:', contextUsed, '/ 200k',
                        `(billed ${billedInput}, cache read ${cacheRead}, cache write ${cacheWrite})`
                    );
                    setTokenUsage({
                        inputTokens: contextUsed,
                        outputTokens: currentOutputTokens,
                        totalTokens: contextUsed + currentOutputTokens
                    });
                    break;
                }

                case 'complete':
                    // Finalize any remaining thinking/response as blocks
                    if (streamingThinkingRef.current.trim().length > 0) {
                        activityLogRef.current = activityLogRef.current.filter(e => e.type !== 'thinking_stream');
                        activityLogRef.current = [...activityLogRef.current, {
                            type: 'thinking_block',
                            content: streamingThinkingRef.current.trim(),
                            timestamp: getNextTimestamp()
                        }];
                    }
                    if (streamingResponseRef.current.trim().length > 0) {
                        activityLogRef.current = activityLogRef.current.filter(e => e.type !== 'response_stream');
                        activityLogRef.current = [...activityLogRef.current, {
                            type: 'response',
                            content: streamingResponseRef.current.trim(),
                            timestamp: getNextTimestamp()
                        }];
                    }
                    // Clear debounce timeout and flush final state immediately
                    if (activityLogUpdateTimeoutRef.current) {
                        clearTimeout(activityLogUpdateTimeoutRef.current);
                        activityLogUpdateTimeoutRef.current = null;
                    }
                    setActivityLog([...activityLogRef.current]);

                    // Persist research progress snapshot to chat history (if research ran)
                    const subCompleteProgress = researchProgressMapRef.current.get(eventTrackingId);
                    const subProjectId = useProjectStore.getState().activeProject?.project_id;
                    if (subCompleteProgress && subCompleteProgress.phases.length > 0 && subProjectId) {
                        const { addLocalEvent } = useProjectStore.getState();
                        const completedSnapshot = {
                            ...subCompleteProgress,
                            isActive: false,
                            currentPhase: 'complete',
                            phases: subCompleteProgress.phases.map((p: any) => ({ ...p, status: 'completed' })),
                        };
                        addLocalEvent({
                            project_id: subProjectId,
                            type: 'research_progress',
                            content: JSON.stringify(completedSnapshot),
                            timestamp: new Date().toISOString(),
                            message_id: event.message_id || eventTrackingId,
                        });
                        console.log('[Chat] Persisted research progress snapshot to chat history');
                    }

                    // Subscription is the source of truth for completion.
                    // Wait briefly for backend to flush to Firestore, then reload.
                    setActiveToolCall(null);
                    setStreamingStatus(null);
                    // Do NOT clear pendingQuestion on 'complete'. When the
                    // agent calls ask_user_question, the streaming response
                    // for that turn finishes (firing 'complete' on this
                    // subscription) before the user answers — backend is
                    // blocked on _pending_question_event for up to 300s.
                    // Clearing pendingQuestion here hides the question form,
                    // wedging the conversation until the backend timeout
                    // fires (PR-45 round 4 "ask_user_question UI render"
                    // bug). Clearing is owned by the answer callback (the
                    // GeminiChatArea onAnswerQuestion path) and by the next
                    // user_question event overwriting the prior.
                    setResearchFindings([]);
                    setActiveWorkers({});
                    setWorkflowPhase(null);
                    setWorkflowError(null);
                    dispatchResearch({ type: 'COMPLETE', trackingId: eventTrackingId });
                    clearActiveResearch();

                    // Trigger evidence graph re-layout on completion
                    setReLayoutTrigger(prev => prev + 1);

                    // Set loading false after a brief delay for Firestore flush
                    setTimeout(() => {
                        setIsLoading(false);
                        setHasReconnectedSession(false);
                        // Allow the next live turn to bootstrap its own placeholder.
                        liveStreamPlaceholderRef.current = false;
                        // Reload project events from Firestore.
                        // BAR-53: read the live project and only reload if we're
                        // still on the project this subscription was created for
                        // (`projectId`) — this 2s timer easily outlives a switch,
                        // and loadProject(stalePid) would clobber the new project.
                        const pid = useProjectStore.getState().activeProject?.project_id;
                        if (pid && pid === projectId && userDataRef.current?.email) {
                            const { loadProject } = useProjectStore.getState();
                            loadProject(pid, userDataRef.current.email);
                        }
                    }, 2000);

                    // NOTE: Do NOT reload events here. The WebSocket 'complete' event
                    // fires before the backend has finished writing to Firestore, causing
                    // a race condition where the reload fetches truncated content.
                    // The awaited reload in the api.sendMessage() response handler
                    // (line ~3877) is the safe reload point — the HTTP response only
                    // returns after the backend has flushed all events to Firestore.

                    // Refresh DAG for build projects (bypasses loadFirstModel guard)
                    {
                        const project = useProjectStore.getState().activeProject;
                        if (project?.project_type === 'build' && subProjectId && authToken) {
                            useProcessViewerStore.getState().refreshModel(subProjectId, authToken);
                        }
                    }
                    break;

                case 'finding':
                    // Research finding - add to both progress panel and workspace
                    const subscriptionFindingSummary = event.data?.summary || event.data?.content || '';
                    if (subscriptionFindingSummary) {
                        const findingTimestamp = getNextTimestamp();
                        const subscriptionFindingId = event.data?.finding_id || event.data?.id || `finding-${Date.now()}`;

                        handleResearchEvent(eventTrackingId, 'finding', event.data || {});
                        setResearchFindings(prev => [...prev, {
                            id: subscriptionFindingId,
                            summary: subscriptionFindingSummary,
                            source: event.data?.source || event.data?.source_id || event.data?.document,
                            confidence: event.data?.confidence,
                            timestamp: findingTimestamp
                        }]);

                        // Add to activity log
                        activityLogRef.current = [...activityLogRef.current, {
                            type: 'finding',
                            content: subscriptionFindingSummary.length > 80 ? subscriptionFindingSummary.substring(0, 80) + '...' : subscriptionFindingSummary,
                            timestamp: findingTimestamp
                        }];
                        setActivityLog([...activityLogRef.current]);

                        // Convert finding to workspace operation.
                        // Scope guard (#89): ONLY apply when the current view is
                        // the workspace where the finding actually belongs.
                        // Research findings come from research/build worker
                        // activity — they don't belong on the Plan graph. And
                        // during catchup, the authoritative state already has
                        // them from the snapshot (loadWorkspaceState), so
                        // replaying here duplicates them.
                        const liveSubFindingProject = useProjectStore.getState().activeProject;  // BAR-53: live, not the subscription closure
                        const subFindingPlanWsId = liveSubFindingProject ? `ws_${liveSubFindingProject.project_id}` : '';
                        const subFindingCurrentWsId = useProjectStore.getState().dictWorkspace?.workspace_id || '';
                        const subFindingIsPlanView = !subFindingCurrentWsId || subFindingCurrentWsId === subFindingPlanWsId;
                        if (liveSubFindingProject && !subFindingIsPlanView && catchupComplete && projectId === liveSubFindingProject.project_id) {
                            const workspaceOp = {
                                type: 'add_evidence' as const,
                                data: {
                                    node_id: crypto.randomUUID(),
                                    node_type: 'finding' as const,
                                    label: subscriptionFindingSummary.length > 80 ? subscriptionFindingSummary.substring(0, 77) + '...' : subscriptionFindingSummary,
                                    relevance_note: `Found by ${event.data?.worker_id || 'research worker'} using ${event.data?.tool_used || 'graph tools'}`,
                                    finding_id: subscriptionFindingId,
                                    content: event.data?.content || subscriptionFindingSummary,
                                    detail: event.data?.metadata ? JSON.stringify(event.data.metadata) : undefined,
                                    reference_ids: [],
                                    finding: {
                                        finding_id: subscriptionFindingId,
                                        worker_id: event.data?.worker_id || 'unknown',
                                        source_type: event.data?.source_type || 'content_search',
                                        source_id: event.data?.source_id || event.data?.source || 'unknown',
                                        content: event.data?.content || subscriptionFindingSummary,
                                        pillar: event.data?.pillar || 'primary_regulation',
                                        confidence: event.data?.confidence || 0.7,
                                        completeness: event.data?.completeness || 0.5,
                                        tool_used: event.data?.tool_used || 'unknown',
                                        timestamp: event.data?.timestamp || new Date(findingTimestamp).toISOString(),
                                        related_concepts: event.data?.related_concepts || [],
                                        related_findings: event.data?.related_findings || [],
                                        metadata: event.data?.metadata || {}
                                    }
                                }
                            };

                            console.log('[Subscription] Converting finding to workspace operation:', workspaceOp);
                            applyWorkspaceOperations([workspaceOp], event.project_id ?? projectId);  // BAR-53: scope ops to source project
                        }
                    }
                    break;

                case 'worker_update':
                    // Track individual worker status
                    const subscriptionWorkerId = event.data?.worker_id || event.data?.id;
                    const subscriptionWorkerStatus = event.data?.status || 'working';
                    const subscriptionWorkerArea = event.data?.area || event.data?.research_area;
                    if (subscriptionWorkerId) {
                        setActiveWorkers(prev => ({
                            ...prev,
                            [subscriptionWorkerId]: {
                                status: subscriptionWorkerStatus,
                                area: subscriptionWorkerArea,
                                progress: event.data?.progress
                            }
                        }));
                        // Only log significant worker events (started, completed)
                        if (subscriptionWorkerStatus === 'started' || subscriptionWorkerStatus === 'completed') {
                            activityLogRef.current = [...activityLogRef.current, {
                                type: 'worker',
                                content: `Worker ${subscriptionWorkerId} ${subscriptionWorkerStatus}${subscriptionWorkerArea ? ` (${subscriptionWorkerArea})` : ''}`,
                                timestamp: getNextTimestamp()
                            }];
                            setActivityLog([...activityLogRef.current]);
                        }
                    }
                    break;

                case 'research_mission':
                    // Research mission set or updated
                    if (event.data) {
                        setResearchMission(event.data as ResearchMission);
                        console.log('[Subscription] Research mission updated:', event.data.query);
                    }
                    break;

                case 'chat_message_updated':
                    // Chat message saved to Firestore - reload to display
                    console.log('[Subscription] Chat updated, reloading project...');
                    {
                        // BAR-53: read the live project (not the subscription
                        // closure) and reload IT. loadProject's own guard bails
                        // if it no longer matches, so a stale event can't reload
                        // a project the user has switched away from.
                        const liveChatProject = useProjectStore.getState().activeProject;
                        if (liveChatProject?.project_id && liveChatProject?.user_id) {
                            const { loadProject: loadProjectFn } = useProjectStore.getState();
                            loadProjectFn(liveChatProject.project_id, liveChatProject.user_id);
                            console.log('[Subscription] Reload triggered for user:', liveChatProject.user_id);
                        } else {
                            console.warn('[Subscription] Cannot reload - no user_id in activeProject');
                        }
                    }
                    break;

                // Subscription connection management events
                case 'subscription_connected':
                    console.log('[Subscription] Connected to project:', event.data?.project_id);
                    break;

                case 'subscription_reconnecting':
                    console.log('[Subscription] Reconnecting...', event.data);
                    setStreamingStatus(`Reconnecting to stream (attempt ${event.data?.attempt})...`);
                    // Reset catchupComplete so the reconnected WebSocket's historical events
                    // are skipped (same as initial connect). Without this, all historical events
                    // replay as live — causing duplicate activity log entries, duplicate workspace
                    // nodes, and unnecessary API calls (e.g., refreshModel on every past 'complete').
                    catchupComplete = false;
                    break;

                case 'subscription_failed':
                    console.error('[Subscription] Failed to reconnect:', event.data?.reason);
                    if (event.data?.reason === 'authentication_required') {
                        setStreamingStatus('Connection authentication failed - please sign in again');
                    } else {
                        setStreamingStatus('Connection lost - please refresh');
                    }
                    break;

                case 'subscription_error':
                    console.error('[Subscription] Error:', event.data?.error);
                    break;

                case 'subscription_closed':
                    console.log('[Subscription] Closed:', event.data?.project_id);
                    break;

                case 'system': {
                    // System events carry subtypes in data.type (research_detail, heartbeat, turn_complete, etc.)
                    const systemSubtype = event.data?.type;
                    if (systemSubtype === 'research_detail') {
                        // Granular progress message from v3 pipeline — dispatch to research progress reducer
                        const detailTrackingId = event.tracking_id || currentResearchTrackingIdRef.current || 'unknown';
                        dispatchResearch({ type: 'DETAIL', trackingId: detailTrackingId, payload: { message: event.data.message || '' } });
                    } else if (systemSubtype === 'turn_complete') {
                        // Agent completed a turn and will continue with fresh context
                        setStreamingStatus('Continuing with fresh context...');
                    }
                    // Ignore heartbeat, connected, and other system subtypes
                    break;
                }

                default:
                    // Log unknown events for debugging
                    console.log('[Subscription] Unknown event type:', event.type);
            }
            });
        };

        // Call the async initialization
        initAndSubscribe();

        // Cleanup: unsubscribe when project changes or component unmounts
        return () => {
            console.log('[Subscription] Unsubscribing from project events:', projectId);
            if (unsubscribe) {
                unsubscribe();
            }
        };
        // authToken and the `api` singleton are intentionally NOT dependencies:
        // getStoredAuthToken() reads the fresh token at (re)connect time (so the
        // subscription doesn't tear down + replay on every ~50min token refresh),
        // and `api` (realApi) is a stable module-level object (BAR-266 review).
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeProject?.project_id, applyWorkspaceOperations]);

    // User state persistence functions
    const getUserStateKey = (userId: string) => `bark_user_state_${userId}`;
    
    const saveUserState = (userId: string) => {
        try {
            const userState = {
                chats,
                activeChatId,
                selectedReportId,
                regulatorySettings,
                reportLibrary,
                // BAR-53: `currentReport` intentionally NOT persisted. It is a
                // derived value (activeReportId -> openReports[id]) that
                // loadUserState already ignores; persisting it only risked
                // carrying a stale report across a re-login. openReports still
                // live per-chat inside `chats`.
                activeView,
                lastSaved: new Date().toISOString()
            };
            
            const key = getUserStateKey(userId);
            localStorage.setItem(key, JSON.stringify(userState));
            console.log(`💾 Saved user state for ${userId}`, {
                key,
                chatCount: Object.keys(chats).length,
                activeChatId: activeChatId || 'null',
                chats: Object.keys(chats)
            });
        } catch (error) {
            console.error('Error saving user state:', error);
        }
    };
    
    const loadUserState = (userId: string) => {
        try {
            const key = getUserStateKey(userId);
            const savedState = localStorage.getItem(key);
            console.log(`🔄 Loading user state for ${userId}`, { key, hasSavedState: !!savedState });
            
            if (savedState) {
                const userState = JSON.parse(savedState);
                console.log(`📂 Found saved state:`, {
                    chatCount: Object.keys(userState.chats || {}).length,
                    activeChatId: userState.activeChatId,
                    lastSaved: userState.lastSaved
                });
                
                // Clean up old report data that might have incompatible IDs
                let cleanedReportLibrary = userState.reportLibrary || {};
                let cleanedSelectedReportId = userState.selectedReportId;
                let needsCleanup = false;
                
                // Check for old numeric report IDs and remove them
                Object.keys(cleanedReportLibrary).forEach(reportId => {
                    if (!reportId.startsWith('report_')) {
                        console.log(`🧹 Removing old report ID from cache: ${reportId}`);
                        delete cleanedReportLibrary[reportId];
                        needsCleanup = true;
                    }
                });
                
                // Clear selected report if it's an old format
                if (cleanedSelectedReportId && !cleanedSelectedReportId.startsWith('report_')) {
                    console.log(`🧹 Clearing old selected report ID: ${cleanedSelectedReportId}`);
                    cleanedSelectedReportId = null;
                    needsCleanup = true;
                }
                
                // Restore state
                const loadedChats = userState.chats || {};
                const loadedActiveChatId = userState.activeChatId;
                
                console.log(`🔄 Restoring chats:`, {
                    loadedChatCount: Object.keys(loadedChats).length,
                    loadedActiveChatId,
                    chatIds: Object.keys(loadedChats)
                });
                
                setChats(loadedChats);
                // Don't set activeChatId - user starts fresh with welcome screen
                setSelectedReportId(cleanedSelectedReportId);
                setRegulatorySettings(userState.regulatorySettings || {});
                setReportLibrary(cleanedReportLibrary);
                // Migration: Skip old currentReport state - use new openReports system
                // setCurrentReport(userState.currentReport);
                setActiveView(userState.activeView || 'welcome');
                
                // Restore chats (but don't auto-activate any chat - user starts fresh)
                if (Object.keys(loadedChats).length > 0) {
                    console.log(`✅ Restored ${Object.keys(loadedChats).length} chats to history`);
                    // Don't set activeChatId - user starts with welcome screen
                } else {
                    console.log('📝 No previous chats found - user will start fresh');
                }
                
                if (needsCleanup) {
                    console.log(`🧹 Cleaned up old cached data for ${userId}`);
                    // Save the cleaned state immediately
                    saveUserState(userId);
                }
                
                console.log(`📂 Loaded user state for ${userId} (saved: ${userState.lastSaved})`);
                return true;
            }
            // First time user - no chat history, starts with welcome screen
            console.log('👤 First time user - will start with welcome screen');
            return false;
        } catch (error) {
            console.error('Error loading user state:', error);
            return false;
        }
    };
    
    const clearUserState = (userId: string) => {
        try {
            localStorage.removeItem(getUserStateKey(userId));
            console.log(`🗑️ Cleared user state for ${userId}`);
        } catch (error) {
            console.error('Error clearing user state:', error);
        }
    };

    // Auto-save user state when important data changes
    useEffect(() => {
        if (isAuthenticated && userData?.email) {
            console.log(`⏰ Auto-save triggered for ${userData.email}`, {
                chatCount: Object.keys(chats).length,
                activeChatId
            });
            const saveTimeout = setTimeout(() => {
                saveUserState(userData.email);
            }, 1000); // Debounce saves
            
            return () => clearTimeout(saveTimeout);
        }
    }, [chats, activeChatId, selectedReportId, currentReport, activeView, isAuthenticated, userData]);

    // Load user state when authenticated (but don't auto-create chat)
    useEffect(() => {
        if (isAuthenticated && userData?.email) {
            console.log(`🔑 About to load user state for: ${userData.email}`);
            loadUserState(userData.email);
            // Note: No auto-creation of chat - user starts with welcome screen
            // New chat will be created when user actually sends their first message
        }
    }, [isAuthenticated, userData?.email]);

    // Single source of truth for backend token refresh (BAR-41). Called by
    // initializeApp (startup, below), the 50-min proactive interval,
    // api.setTokenRefreshCallback (all mounted API refreshes and reactive WS
    // close 4001 recovery), and handleSendMessage's pre-turn check
    // (resolveTurnToken). Must stay declared above the consumer effects
    // below — their dep arrays reference it.
    //
    // Inflight-promise singleton dedups any race between the startup refresh,
    // the 50-min interval, a pre-turn refresh, and a 4001-triggered reactive
    // refresh. An exchange can return a rotated token, so parallel requests
    // could persist different pairs and strand a consumer on the losing result.
    const inflightRefresh = useRef<Promise<string> | null>(null);
    const refreshAccessToken = useCallback(async (): Promise<string> => {
        if (inflightRefresh.current) return inflightRefresh.current;
        const doRefresh = async (): Promise<string> => {
            // BAR-1564: an impersonating tab has no refresh token (D5). The one
            // in localStorage is the admin's: refreshing it would put the admin
            // back in this tab behind the impersonation banner.
            if (isImpersonatingTab()) throw terminalAuthRefreshError('An impersonation session cannot be refreshed');
            // localStorage can be newer if a refresh completed during the
            // narrow interval before this callback effect registered.
            const rt = localStorage.getItem('bark_refresh_token') || refreshTokenRef.current;
            if (!rt) throw terminalAuthRefreshError('No refresh token available');
            const result = await api.refreshToken(rt);
            setAuthToken(result.access_token);
            localStorage.setItem('bark_auth_token', result.access_token);
            if (result.refresh_token) {
                refreshTokenRef.current = result.refresh_token;
                localStorage.setItem('bark_refresh_token', result.refresh_token);
            }
            return result.access_token;
        };
        // .finally clears the singleton on both success AND rejection so a
        // failed refresh doesn't permanently block subsequent attempts.
        inflightRefresh.current = doRefresh().finally(() => { inflightRefresh.current = null; });
        return inflightRefresh.current;
        // stable deps: useRef, module singleton, useState setter
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // BAR-1564: who signs people in. config.json decides; until it is read the
    // sign-in page shows neither form, and without it the app stays on Firebase.
    useEffect(() => {
        let cancelled = false;
        api.initialize()
            .then(() => { if (!cancelled) setAuthMode(isKeycloakMode() ? 'keycloak' : 'firebase'); })
            .catch(() => { if (!cancelled) setAuthMode('firebase'); });
        return () => { cancelled = true; };
    }, []);

    // BAR-1564: open the page a Keycloak sign-in was started from. A waiting
    // connector hand-off (`?connect=…`) goes back into state, as on a first
    // load, instead of into the address bar.
    const openReturnPath = (returnTo: string) => {
        const url = new URL(returnTo, window.location.origin);
        const connect = url.searchParams.get('connect');
        if (connect) {
            setConnectState(connect);
            setConnectGatewayOrigin(url.searchParams.get('gateway_origin'));
            url.searchParams.delete('connect');
            url.searchParams.delete('gateway_origin');
        }
        navigate(url.pathname + url.search + url.hash, { replace: true });
    };

    // BAR-1564: Keycloak sent the browser back to /auth/callback. Exchange the
    // code, then finish either a sign-in (BARK's account rules through
    // /session/provision, plus a refresh when they changed the claims) or the
    // password change Keycloak's own form just made (kc_action), and open the
    // page the person came for. True when this decided the session; false when
    // the ordinary restore of the saved session should run.
    const completeOidcCallback = async (): Promise<boolean> => {
        try { await api.initialize(); } catch { /* no config: not Keycloak */ }
        if (!isKeycloakMode()) {
            navigate('/', { replace: true });
            return false;
        }
        const savedUserRaw = localStorage.getItem('bark_user_data');
        const hasSavedSession = Boolean(localStorage.getItem('bark_auth_token') && savedUserRaw);
        let result;
        try {
            result = await completeKeycloakLogin();
        } catch (error) {
            console.error('[Auth] Keycloak sign-in could not be completed:', error);
            navigate('/', { replace: true });
            // Back from an abandoned password change: the saved session still holds.
            if (hasSavedSession) return false;
            setLoginNotice({ text: 'Sign-in could not be completed. Please try again.', tone: 'error' });
            return true;
        }
        const { accessToken, state, kcActionStatus } = result;

        if (state.action === 'UPDATE_PASSWORD' && savedUserRaw) {
            let savedUser = null;
            try { savedUser = JSON.parse(savedUserRaw); } catch { savedUser = null; }
            if (savedUser) {
                let token = accessToken;
                if (kcActionStatus === 'success') {
                    try {
                        // Retires every token issued before now, other devices' and this one.
                        await api.notifyPasswordChanged(accessToken);
                        token = (await refreshKeycloakTokens()).access_token;
                        showToast({ variant: 'success', message: CHANGE_PASSWORD_SUCCESS_USER_MESSAGE });
                    } catch (error) {
                        console.warn('[Auth] Password changed; retiring older tokens failed:', error);
                        showToast({ variant: 'warning', message: 'Your password was changed. Other devices may stay signed in for up to an hour.' });
                    }
                }
                handleLoginSuccess(token, savedUser, OIDC_SESSION_MARKER);
                openReturnPath(state.returnTo);
                return true;
            }
        }

        try {
            const session = await api.provisionSession(accessToken, state.signupConsentVersion);
            let token = accessToken;
            // company or role was just written: the token's claims are stale.
            if (session.claims_changed) token = (await refreshKeycloakTokens()).access_token;
            setLoginNotice(null);
            setForceLoginPrompt(false);
            handleLoginSuccess(token, session.user, OIDC_SESSION_MARKER);
        } catch (error) {
            console.error('[Auth] Keycloak sign-in refused:', error);
            await clearKeycloakSession();
            // Started as Google or Microsoft on our page: today's SSO words.
            setLoginNotice({ text: signInRefusalMessage(error, { sso: Boolean(state.idpHint) }), tone: 'error' });
            // A refusal asks for credentials next time, so another account can be
            // picked. Any other failure leaves that as it was: only a sign-in BARK
            // accepts ends it (an earlier failed sign-out may have set it).
            if (error?.status === 403) setForceLoginPrompt(true);
        }
        openReturnPath(state.returnTo);
        return true;
    };

    // Initialize authentication and data from localStorage on app start
    useEffect(() => {
        const initializeApp = async () => {
            // BAR-1564: an impersonation lives in its tab only. An impersonation
            // token in localStorage was left there by the first design (or by a
            // tab still running it): drop it, so no tab acts as the target
            // without the banner. Keycloak then signs the admin in again.
            if (isImpersonationToken(localStorage.getItem('bark_auth_token'))) {
                localStorage.removeItem('bark_auth_token');
                localStorage.removeItem('bark_user_data');
            }
            // A partial set, or the first design's admin stash, is no
            // impersonation; nor is one whose admin signed out while this tab
            // was away (it missed the storage event below).
            if (!isImpersonatingTab() || !localStorage.getItem('bark_auth_token')) clearImpersonation();
            // BAR-1564: back from Keycloak. That decides the session first.
            if (typeof window !== 'undefined' && isOidcCallbackPath(window.location.pathname)) {
                const decided = await completeOidcCallback();
                setOidcCallbackPending(false);
                if (decided) return;
            }
            // Clean up old global localStorage keys that might interfere
            const oldKeys = ['bark_chats', 'bark_active_chat_id'];
            oldKeys.forEach(key => {
                if (localStorage.getItem(key)) {
                    console.log(`🧹 Cleaning up old localStorage key: ${key}`);
                    localStorage.removeItem(key);
                }
            });
            
            // Check for existing auth token (this tab's impersonation first)
            const savedToken = readAuthToken();
            const savedUser = readUserData();

            if (savedToken && savedUser) {
                // An impersonation has no refresh token (D5); the one in
                // localStorage is the admin's.
                refreshTokenRef.current = isImpersonatingTab() ? null : localStorage.getItem('bark_refresh_token');

                // Eagerly refresh on app startup. The saved access_token
                // might already be expired (page closed for hours, then
                // reopened); without a startup refresh, the very first
                // API call falls through to anonymous on the backend
                // and the user sees an empty workspace with no signal
                // that they're authed-but-stale. Goes through the shared
                // refreshAccessToken so the inflight-promise singleton
                // dedups against any race with the 50-min interval.
                let activeToken = savedToken;
                if (refreshTokenRef.current) {
                    try {
                        activeToken = await refreshAccessToken();
                        console.log('[Auth] Refreshed token on startup');
                    } catch (e) {
                        if (shouldClearAuthAfterRefreshFailure(e)) {
                            console.warn('[Auth] Startup refresh rejected; signing out');
                            localStorage.removeItem('bark_auth_token');
                            localStorage.removeItem('bark_user_data');
                            localStorage.removeItem('bark_refresh_token');
                            return;
                        }
                        // Keep the saved pair during a transport/provider blip.
                        // Mounted API calls share the same owner and can retry.
                        console.warn('[Auth] Startup refresh unavailable; retaining saved session');
                    }
                }

                setAuthToken(activeToken);
                setIsAuthenticated(true);
                const cachedUser = JSON.parse(savedUser);
                setUserData(cachedUser);

                // Entitlements are a property of the ORGANISATION and change
                // without the person doing anything, so the login payload
                // cached here goes stale on its own. Before this, a restored
                // session replayed `disabled_modules` from whenever they last
                // signed in — the admin panel promises "applies within ~60
                // seconds, no re-login needed", and for an open tab that was
                // simply untrue in both directions: a newly granted module
                // stayed Locked, and a revoked one kept its entry points while
                // the backend refused them.
                //
                // Re-read /me and keep only what the server is authoritative
                // for. Fail-safe: any error leaves the cached payload in place
                // (a Firestore blip must not lock a paying customer out of
                // their own UI), and the token refresh above already handles
                // the genuinely-signed-out case.
                void (async () => {
                    try {
                        const fresh: any = await api.getCurrentUser(activeToken);
                        if (!fresh || typeof fresh !== 'object') return;
                        const merged = {
                            ...cachedUser,
                            ...fresh,
                            plan: fresh.plan ?? cachedUser?.plan,
                            disabled_modules: Array.isArray(fresh.disabled_modules)
                                ? fresh.disabled_modules
                                : (cachedUser?.disabled_modules ?? []),
                        };
                        setUserData(merged);
                        storeUserData(merged);
                    } catch (e: any) {
                        // BAR-135: an impersonation that ended on the server
                        // (Exit in another tab, a revoke) answers 401 here.
                        // Exit it, as a 401 on any other call does; before,
                        // the banner stayed until that call or the ceiling.
                        if (e?.status === 401 && isImpersonatingTab()) {
                            void exitImpersonationRef.current?.('auth_failure');
                            return;
                        }
                        console.warn('[Auth] /me refresh unavailable; keeping cached entitlements', e);
                    }
                })();

                // BAR-135: restore an in-progress impersonation session so the
                // banner + refresh-suppression survive a reload. If the ceiling
                // already passed, the expiry-timer effect below fires an
                // immediate auto-exit-restore of the real admin.
                const savedImp = readImpersonationMeta();
                if (savedImp) setImpersonation(savedImp);

                // Note: User state will be loaded by the useEffect when isAuthenticated and userData are set

                // Load saved regulatory settings
                const savedSettings = localStorage.getItem('bark_regulatory_settings');
                if (savedSettings) {
                    try {
                        setRegulatorySettings(JSON.parse(savedSettings));
                    } catch (e) {
                        console.warn('Failed to parse saved settings:', e);
                    }
                }
            }
        };
        
        // BAR-1564: every branch of initializeApp (the /auth/callback path, a
        // rejected startup refresh, nothing saved) ends the restore here; only
        // then may the sign-in page's automatic Keycloak redirect start.
        initializeApp().finally(() => setSessionRestorePending(false));
        // refreshAccessToken is a stable useCallback (empty deps) — listing
        // it here satisfies exhaustive-deps without re-running on every render.
        // completeOidcCallback runs once, at mount, like the rest of this effect.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshAccessToken]);

    // Auto-refresh Firebase token every 50 minutes (expires after 60).
    // BAR-135: SUPPRESSED while impersonating — an impersonated session has no
    // refresh token (D5), so there is nothing to refresh; the credential dies at
    // the 60-min ceiling and the expiry timer below auto-exits.
    useEffect(() => {
        if (!isAuthenticated || impersonation) return;
        const interval = setInterval(async () => {
            try {
                await refreshAccessToken();
                console.log('[Auth] Token refreshed (proactive)');
            } catch (e) {
                console.warn('[Auth] Token refresh failed:', e);
            }
        }, 50 * 60 * 1000); // 50 minutes
        return () => clearInterval(interval);
    }, [isAuthenticated, impersonation, refreshAccessToken]);

    // Make App the single mounted refresh owner so rotating credentials update
    // React state, the ref, and localStorage together. This also lets an
    // in-flight WebSocket closed with code 4001 refresh before reconnecting
    // instead of looping with the dead value captured in closure. BAR-41.
    //
    // BAR-135: while impersonating, a 4001 means the TARGET session is dead
    // (revoked / demoted / expired) and there is no refresh token to recover
    // with — so instead of refreshing, the callback triggers an auto-exit-
    // restore of the real admin and rejects (stopping the WS from reconnecting
    // with a dead target token).
    useEffect(() => {
        if (!isAuthenticated) {
            api.setTokenRefreshCallback(null);
            return;
        }
        if (impersonation) {
            api.setTokenRefreshCallback(async () => {
                void exitImpersonationRef.current?.('auth_failure');
                throw terminalAuthRefreshError('Impersonation session ended');
            });
            return () => api.setTokenRefreshCallback(null);
        }
        api.setTokenRefreshCallback(refreshAccessToken);
        return () => api.setTokenRefreshCallback(null);
    }, [isAuthenticated, impersonation, refreshAccessToken]);

    // BAR-962: once signed in with a pending connector hand-off, load what the
    // connector is asking for so the signed-in owner can approve it (consent
    // is given HERE, by the account owner — never on an anonymous page).
    // Runs at most once per hand-off; on failure the person stays signed in
    // and sees the message.
    useEffect(() => {
        if (!connectState || !isAuthenticated || !authToken || connectInFlightRef.current) return;
        if (impersonation) {
            setConnectError('Exit impersonation before connecting an external client.');
            setConnectState(null);
            return;
        }
        connectInFlightRef.current = true;
        const state = connectState;
        (async () => {
            try {
                setConnectContext(await api.getConnectorContext(state, connectGatewayOrigin));
            } catch (error: any) {
                console.error('[Auth] Connector sign-in failed:', error);
                setConnectError(error?.message || 'Connector sign-in failed');
                setConnectState(null);
                connectInFlightRef.current = false;
            }
        })();
    }, [connectState, connectGatewayOrigin, isAuthenticated, authToken, impersonation]);

    // The person approved: hand the EXISTING session to the gateway (which
    // verifies it with auth_service /me) and follow the connector's redirect.
    const handleConnectorApprove = useCallback(async () => {
        if (!connectState || !authToken || connectSubmitting || connectSwitching) return;
        // Checked here too: after Use another account the consent reopens
        // from the cached context, past the effect that refuses impersonation.
        if (impersonation) {
            setConnectError('Exit impersonation before connecting an external client.');
            setConnectContext(null);
            setConnectState(null);
            connectInFlightRef.current = false;
            return;
        }
        setConnectSubmitting(true);
        try {
            const rt = refreshTokenRef.current || localStorage.getItem('bark_refresh_token');
            const { redirect_url } = await api.completeConnectorSignIn(
                connectState, authToken, rt, connectGatewayOrigin,
            );
            window.location.replace(redirect_url);
        } catch (error: any) {
            console.error('[Auth] Connector sign-in failed:', error);
            setConnectError(error?.message || 'Connector sign-in failed');
            setConnectSubmitting(false);
            setConnectContext(null);
            setConnectState(null);
            connectInFlightRef.current = false;
        }
    }, [connectState, connectGatewayOrigin, authToken, connectSubmitting, connectSwitching, impersonation]);

    const handleConnectorCancel = useCallback(() => {
        setConnectContext(null);
        setConnectState(null);
        connectInFlightRef.current = false;
    }, []);

    // BAR-135: keep the global REST handler scoped to impersonated sessions.
    // Ordinary change-password expiry is handled by that modal's explicit
    // callback so unrelated REST/refresh failures cannot log a user out.
    useEffect(() => {
        if (impersonation) {
            api.setGlobalAuthFailureHandler(() => {
                setIsChangePasswordOpen(false);
                void exitImpersonationRef.current?.('auth_failure');
            });
            return () => api.setGlobalAuthFailureHandler(null);
        }
        api.setGlobalAuthFailureHandler(null);
    }, [impersonation]);

    // BAR-135: hard-stop at the impersonation ceiling. When expires_at is
    // reached (or is already past on a stale reload), auto-exit-restore.
    useEffect(() => {
        if (!impersonation) return;
        const msLeft = new Date(impersonation.expires_at).getTime() - Date.now();
        if (Number.isNaN(msLeft) || msLeft <= 0) {
            void exitImpersonationRef.current?.('expired');
            return;
        }
        const t = setTimeout(() => { void exitImpersonationRef.current?.('expired'); }, msLeft);
        return () => clearTimeout(t);
    }, [impersonation]);

    // BAR-1564: the admin's own session left localStorage (a sign-out in
    // another tab, or a tab that found it dead). End this tab's impersonation
    // too: the target's identity never outlives the admin's session.
    useEffect(() => {
        if (!impersonation) return;
        const onStorage = (event: StorageEvent) => {
            if (event.storageArea !== window.localStorage) return;
            // key null: localStorage.clear() in another tab.
            if (event.key === null || (event.key === 'bark_auth_token' && event.newValue === null)) {
                void exitImpersonationRef.current?.('auth_failure');
            }
        };
        window.addEventListener('storage', onStorage);
        return () => window.removeEventListener('storage', onStorage);
    }, [impersonation]);

    // Save regulatory settings to localStorage whenever they change
    useEffect(() => {
        localStorage.setItem('bark_regulatory_settings', JSON.stringify(regulatorySettings));
    }, [regulatorySettings]);
    
    const getNewChat = (id: number, serviceType: string) => ({
        id: id,
        title: 'New query',
        chatHistory: [{ 
            sender: 'bark', 
            message: serviceType === 'regulatory_expert' 
                ? 'Welcome to BARK Regulatory Expert. Ask me anything about EU banking regulations.' 
                : 'Welcome to BARK Banking Assistant. I can help with complex regulatory analysis and strategic planning.'
        }],
        report: { title: '', content: '' },
        references: [],
        session_id: null, // Add session_id for UniversalRequest/UniversalResponse contract
        service_type: serviceType, // Track which service this chat is using
        clarification_context: null, // For regulatory expert clarifications
        // NEW CONVERSATIONAL FIELDS:
        reports: {}, // Store multiple reports by ID
        lastResearchId: null, // Track last research session for follow-ups
        research_limitations: false, // Track if research hit processing limits
    });

    const handleLoginSuccess = (token: string, userData?: any, refreshToken?: string) => {
        // A sign-in replaces an impersonation in this tab (BAR-1564).
        clearImpersonation();
        setImpersonation(null);
        setAuthToken(token);
        setIsAuthenticated(true);
        setUserData(userData);

        // Save authentication data to localStorage
        localStorage.setItem('bark_auth_token', token);
        if (userData) {
            localStorage.setItem('bark_user_data', JSON.stringify(userData));
        }
        if (refreshToken) {
            refreshTokenRef.current = refreshToken;
            localStorage.setItem('bark_refresh_token', refreshToken);
        }
    };
    
    // BAR-53: full teardown of the previous project's ephemeral, in-memory UI
    // state. Called whenever we leave a project context — project switch (via
    // the resetEphemeralProjectState prop), "new query", logout, and delete-active-
    // project. Anything project-scoped that lives in App state / refs / sibling
    // Zustand singletons must be reset here so it cannot bleed into the next
    // project. (MainApplicationUI-local panels — referencesRef, selectedEvidence,
    // linkedReport — are reset separately inside MainApplicationUI.)
    const resetEphemeralProjectState = () => {
        // streaming / activity timeline
        setStreamingStatus(null);
        setStreamingResponse('');
        setStreamingThinking('');
        streamingThinkingRef.current = '';
        streamingResponseRef.current = '';
        setActivityLog([]);
        activityLogRef.current = [];
        activitySequenceRef.current = 0;
        setActiveToolCall(null);
        setActiveWorkers({});
        setWorkflowPhase(null);
        setWorkflowError(null);
        setTokenUsage({ ...DEFAULT_TOKEN_USAGE });
        // research-progress map (useReducer) + dedup / tracking refs
        dispatchResearch({ type: 'RESET_ALL' });
        currentResearchTrackingIdRef.current = null;
        processedEventIdsRef.current = new Set();
        lastSubscriptionEventRef.current = 0;
        // project-scoped App state the streaming subset above did NOT cover
        setPendingQuestion(null);
        setResearchFindings([]);
        setResearchMission(null);
        setHandoffPackage(null);
        setSourcePanelContent(null);
        setIsSourcePanelOpen(false);
        setHighlightedNodeId(null);
        // reconnect / "BARK is working" indicator — the standalone indicator is
        // gated on hasReconnectedSession (NOT isLoading), so a mid-reconnect
        // switch would otherwise leave the new project showing the old project's
        // spinner until the user sends a message. Clear it + the reconnect refs.
        setHasReconnectedSession(false);
        isReconnectingRef.current = false;
        reconnectProjectIdRef.current = null;
        // a mid-stream switch doesn't go through the subscription `complete`
        // handler that normally resets this, so clear it here — otherwise the
        // new project's subscription skips creating its streaming placeholder.
        liveStreamPlaceholderRef.current = false;
        // tear down the live agent WS session bound to the previous project so
        // its in-flight stream / queued commands can't land on the next one
        api.closeActiveSession();
        // sibling Zustand stores (session-global singletons) — Build process
        // viewer + Quick Research (full reset, not just results: a stale
        // activeTool / isLoading would otherwise survive the switch)
        useProcessViewerStore.getState().resetStore();
        useQuickSearchStore.getState().resetStore();
    };

    const handleNewQuery = async () => {
        if (!(await useProjectStore.getState().clearActiveProject())) {
            return;
        }
        // Don't create chat immediately - user starts with welcome screen
        // Chat will be created when user sends their first message
        setActiveChatId(null);

        // CRITICAL: Clear active project to prevent chat history from old project bleeding through
        // Without this, getChatHistory() returns the old project's messages
        // BAR-53: use the fully-qualified store action. handleNewQuery runs in
        // the App component, which — unlike MainApplicationUI — does NOT
        // destructure clearActiveProject, so the bare call was a latent
        // ReferenceError that aborted New Query before any reset ran.
        // BAR-53: handleNewQuery used to reset almost nothing — clear the full
        // ephemeral set so streaming/handoff/research UI from the project we
        // just left doesn't linger on the welcome screen or the next project.
        resetEphemeralProjectState();

        // Clear any current report and reset to welcome view
        setSelectedReportId(null);
        setActiveView('welcome');
        setIsCanvasMode(false);

        console.log('🆕 Starting new query - cleared project context, will create chat when user sends message');
    };
    
    const handleServiceSelection = (serviceType: string) => {
        // Service selection is no longer used - always default to regulatory_expert
        console.log(`🎯 Service selection called but ignored: ${serviceType}`);
    };

    const handleSettingsChange = (newSettings: RegulatorySettings) => {
        setRegulatorySettings(newSettings);
        // Persist to localStorage
        localStorage.setItem('regulatorySettings', JSON.stringify(newSettings));
    };

    // Load settings from localStorage on app start
    useEffect(() => {
        const savedSettings = localStorage.getItem('regulatorySettings');
        if (savedSettings) {
            try {
                setRegulatorySettings(JSON.parse(savedSettings));
            } catch (error) {
                console.error('Error loading saved settings:', error);
            }
        }
    }, []);
    
    const handleSelectQuery = (chatId: number) => { setActiveChatId(chatId); };
    
    // BAR-1564: in keycloak mode a sign-out by the person also ends the Keycloak
    // session (the browser goes to Keycloak and back to `afterLogoutPath`) and
    // holds the sign-in page (signingOut) so the automatic Keycloak sign-in
    // cannot cancel it; an expired session only drops what this browser stored.
    const handleLogout = async (
        reason: 'user' | 'session_expired' = 'user',
        warningAlreadyShown: boolean = false,
        afterLogoutPath?: string,
    ) => {
        const keycloak = isKeycloakMode();
        // Set even when another sign-out already runs (it then owns the
        // transition): after a click on Sign out, the automatic Keycloak
        // sign-in never signs the person back in.
        if (keycloak && reason === 'user') setSigningOut(true);
        // Read before the cleanup removes the session: with it Keycloak signs
        // out without asking for a confirmation click.
        const idTokenHint = keycloak ? await currentKeycloakIdToken() : null;
        const finished = await runAppLogoutTransition(reason, warningAlreadyShown);
        if (finished && keycloak) {
            if (reason === 'user') {
                try {
                    await endKeycloakSession({ idTokenHint, postLogoutPath: afterLogoutPath });
                } catch (error) {
                    console.warn('[Auth] Keycloak sign-out failed; signed out of BARK only', error);
                    await clearKeycloakSession();
                    setLoginNotice({ text: SIGNED_OUT_OF_BARK_NOTICE, tone: 'info' });
                    setForceLoginPrompt(true);
                }
            } else {
                await clearKeycloakSession();
            }
        }
        return finished;
    };

    const runAppLogoutTransition = (
        reason: 'user' | 'session_expired',
        warningAlreadyShown: boolean,
    ) => runLogoutTransition({
        inFlightRef: logoutInFlightRef,
        reason,
        warningAlreadyShown,
        clearActiveProject: options => useProjectStore.getState().clearActiveProject(options),
        showWarning: message => showToast({ variant: 'warning', message }),
        saveUserState: () => {
            if (userData?.email) saveUserState(userData.email);
        },
        remoteLogout: async () => {
            if (authToken) await api.logout(authToken);
        },
        // A rejected preparation step must never leave live credentials behind
        // a signed-out React shell.
        clearReactState: () => {
            setIsAuthenticated(false);
            setAuthToken(null);
            setUserData(null);
            // BAR-1564: a sign-out ends this tab's impersonation too; it must
            // not come back with the next sign-in or reload in this tab.
            clearImpersonation();
            setImpersonation(null);
            // The ref outlived the logout, so a next sign-in that returned no
            // refresh token handed on the previous account's (BAR-1528).
            refreshTokenRef.current = null;
            setChats({});
            setActiveChatId(null);
            setRegulatorySettings({});
            setReportLibrary({});
            setActiveView('welcome');
            setSelectedReportId(null);
        },
        clearPlanReloadTimer: () => {
            if ((window as any).__planReloadTimer) {
                clearTimeout((window as any).__planReloadTimer);
                (window as any).__planReloadTimer = null;
            }
        },
        // BAR-53: wipe ephemeral App UI state + sibling Zustand singletons so
        // the next user never sees the previous user's residue for a paint.
        resetEphemeralState: resetEphemeralProjectState,
        storage: localStorage,
        // BAR-40: prevent the next user from inheriting a stale deep link.
        navigateHome: () => navigate('/', { replace: true }),
    });

    // BAR-1528: the way out when the consent names the wrong account. The
    // pending hand-off survives the sign-out, so the next sign-in returns here.
    const handleConnectorUseAnotherAccount = async () => {
        if (connectSwitching) return;
        setConnectSwitching(true);
        try {
            // Keycloak mode: the sign-out leaves the page, and comes back here.
            if (await handleLogout('user', false, connectReturnPath(connectState, connectGatewayOrigin) ?? undefined)) {
                showToast({
                    variant: 'info',
                    message: 'Sign in with the account you want to connect. The connection request is still waiting.',
                });
            }
        } finally {
            setConnectSwitching(false);
        }
    };

    const handleChangePasswordSessionExpired = (
        message: string = SESSION_EXPIRED_USER_MESSAGE,
    ) => {
        setIsChangePasswordOpen(false);
        // The impersonation-only global handler owns restore of the real admin,
        // but this modal still owns explaining why it disappeared.
        if (impersonation) {
            showToast({ variant: 'warning', message });
            return;
        }
        showToast({ variant: 'warning', message });
        setIsAuthenticated(false);
        setAuthToken(null);
        setUserData(null);
        void handleLogout('session_expired', true);
    };

    // ---- BAR-135: super-admin impersonation (start / exit) ----
    // The target's token, user and session metadata live in this tab's
    // sessionStorage only (BAR-1564, auth/impersonationTab.ts). localStorage
    // keeps the admin's session, which the exit refreshes and restores. See
    // plan §6.4.

    const startImpersonation = async (targetUid: string, reason?: string): Promise<void> => {
        const adminToken = authToken || readAuthToken();
        if (!adminToken) throw new Error('Not authenticated');
        if (!(await useProjectStore.getState().clearActiveProject())) {
            throw new Error('Could not save the current project. Retry impersonation after the save succeeds.');
        }
        // Mint the target's short-lived token (NO refresh token — D5).
        const res = await api.impersonateUser(adminToken, targetUid, reason);

        const meta: ImpersonationMeta = {
            session_id: res.impersonation.session_id,
            expires_at: res.impersonation.expires_at,
            actor_email: res.impersonation.actor_email,
            target_email: res.user?.email || '',
        };
        // Swap THIS TAB to the target (state, authedHeaders, subscribe-WS token
        // reads, userData.email advisory ids). The admin's other tabs, and new
        // ones, stay the admin.
        storeImpersonation(res.access_token, res.user, meta);
        setImpersonation(meta);
        setAuthToken(res.access_token);
        setIsAuthenticated(true);
        setUserData(res.user);
        // The impersonated session has no refresh token (D5).
        refreshTokenRef.current = null;

        // BAR-53 teardown subset: drop the admin's ephemeral project state +
        // live sockets before rendering the target's workspace.
        try { if (typeof resetEphemeralProjectState === 'function') resetEphemeralProjectState(); } catch {}
        try { api.closeActiveSession(); } catch {}

        navigate('/', { replace: true });
    };

    const exitImpersonation = async (reason?: string): Promise<void> => {
        if (impersonationExitInFlightRef.current) return;
        impersonationExitInFlightRef.current = true;
        // The ceiling or a 401 ended the session, not the admin's Exit.
        const forcedIdentityExit = reason === 'expired' || reason === 'auth_failure';
        let endNoticeShown = false;
        try {
            const projectCleared = await useProjectStore.getState().clearActiveProject();
            if (!projectCleared) {
                if (!forcedIdentityExit) {
                    return;
                }
                showToast({
                    variant: 'warning',
                    message: 'The impersonation session ended before the current project could be saved.',
                });
                endNoticeShown = true;
                await useProjectStore.getState().clearActiveProject({ discardPendingChanges: true });
            }
            const meta = impersonationRef.current || readImpersonationMeta();
            // The admin's own session: localStorage kept it the whole time (and
            // the admin's other tabs may have refreshed it there).
            const adminRefresh = localStorage.getItem('bark_refresh_token');
            const adminUserRaw = localStorage.getItem('bark_user_data');

            if (!adminRefresh || !adminUserRaw) {
                // The admin signed out meanwhile (in another tab, or a tab found
                // the session dead): nothing to restore, and no session of theirs
                // left here to end. Sign this tab out as an expired session does;
                // the server expires the impersonation at its ceiling (§6.4).
                clearImpersonation();
                setImpersonation(null);
                await handleLogout('session_expired');
                return;
            }

            let adminUser: any = null;
            try { adminUser = JSON.parse(adminUserRaw); } catch { adminUser = null; }

            try {
                // Refresh the admin FIRST: the access token in localStorage may
                // have expired during a ≤60-min session. Restore the latest
                // returned pair.
                const refreshed = await api.refreshToken(adminRefresh);
                const freshAdminToken = refreshed.access_token;
                const freshAdminRefresh = refreshed.refresh_token || adminRefresh;

                // End the session server-side with the FRESH admin token. Client-
                // side best-effort: the server also auto-expires at the ceiling.
                if (meta?.session_id) {
                    try { await api.endImpersonation(freshAdminToken, meta.session_id); }
                    catch (e) { console.warn('[Impersonation] end call failed (session still expires server-side):', e); }
                }

                clearImpersonation();
                setImpersonation(null);
                // Teardown the TARGET's live state before restoring the admin.
                try { if (typeof resetEphemeralProjectState === 'function') resetEphemeralProjectState(); } catch {}
                try { api.closeActiveSession(); } catch {}

                // Restore the real admin (token + user + rotated refresh).
                handleLoginSuccess(freshAdminToken, adminUser, freshAdminRefresh);
                navigate('/', { replace: true });
                // Without this, a forced exit swaps the identity silently.
                if (forcedIdentityExit && !endNoticeShown) {
                    showToast({
                        variant: 'info',
                        message: adminUser?.email
                            ? `The impersonation session ended. You are signed in as ${adminUser.email} again.`
                            : 'The impersonation session ended.',
                    });
                }
            } catch (e) {
                // The admin's refresh failed (session ended, or rotated-token
                // loss): degrade to logout-to-login — harmless, the server
                // marks the session expired without an /end call (§6.4).
                console.warn('[Impersonation] admin refresh failed; degrading to logout:', e);
                clearImpersonation();
                setImpersonation(null);
                await handleLogout();
            }
        } finally {
            impersonationExitInFlightRef.current = false;
        }
    };

    // Keep the late-bound ref current so the auth effects (declared above these
    // handlers) can trigger auto-exit without a stale closure.
    useEffect(() => { exitImpersonationRef.current = exitImpersonation; });

    // Reset chat state when switching projects - creates fresh chat session.
    // If projectId is provided, checks for active research and restores
    // loading state.
    //
    // INVARIANT: This function DOES NOT touch prefillChatInput or
    // autoSendPrefill. The new-project onCreated handler relies on calling
    // resetChatState FIRST, then setting prefill state, then switching
    // routeView to the workspace — if this function ever started clearing
    // prefill state, the seed would be wiped before GeminiChatArea mounts
    // and the auto-send would silently no-op. Any future edit that adds
    // prefill manipulation here must be mirrored at that call site.
    const resetChatState = (projectId?: string) => {
        // Check if there's active research for this project
        const activeResearch = getActiveResearch();
        const hasActiveResearch = projectId && activeResearch?.projectId === projectId;

        setIsLoading(hasActiveResearch);
        if (hasActiveResearch) {
            console.log('[Chat] Restored loading state for active research on project:', projectId);
        }

        const newChatId = reserveChatId();
        setChats({
            [newChatId]: {
                id: newChatId,
                title: "New chat",
                service_type: 'regulatory_expert',
                chatHistory: [],
                report: null,
                references: [],
                lastResearchId: null,
                clarification_context: null,
                openReports: {},
                activeReportId: null
            }
        });
        setActiveChatId(newChatId);
        setSelectedReportId(null);  // Clear any stale report selection
        setResearchMission(null);  // Clear research mission from previous project
        // Note: prefillChatInput and autoSendPrefill are intentionally NOT
        // reset here — see the INVARIANT in the docstring above.
    };

    const handleSendMessage = async (
        messageText: string,
        researchDepth: string,
        useMultiStageValidation: boolean = false,
        options: any = {},
    ) => {
        const displayMessage = options.displayMessage || messageText;
        const uploadedFiles = Array.isArray(options.uploadedFiles) ? options.uploadedFiles : [];
        // BAR-146: always have a stable client message_id. It tags the
        // optimistic event, the durable persist-on-send write, and the payload
        // to the orchestrator — all three share it so the user turn is one
        // idempotent document that survives a dropped/idle WS or a hard refresh.
        const clientMessageId: string = typeof options.clientMessageId === 'string'
            ? options.clientMessageId
            : crypto.randomUUID();
        let currentActiveChatId = activeChatId;
        let activeChat = activeChatId ? chats[activeChatId] : null;
        
        // Create new chat if none exists (lazy creation)
        if (!currentActiveChatId || !activeChat) {
            console.log('💬 Creating new chat for first message');
            const newChatId = reserveChatId();
            const newChat = {
                id: newChatId,
                title: "New chat",
                service_type: 'regulatory_expert',
                chatHistory: [],
                report: null,
                references: [],
                lastResearchId: null,
                clarification_context: null,
                openReports: {},
                activeReportId: null
            };
            
            setChats(prev => ({ ...prev, [newChatId]: newChat }));
            setActiveChatId(newChatId);
            
            // Use the new chat for this message
            currentActiveChatId = newChatId;
            activeChat = newChat;
        }
        
        // Service type is now always 'regulatory_expert', so no need to check
        const newUserMessage = {
            sender: 'user',
            message: displayMessage,
            uploadedFiles,
        };
        
        // Check if this is the first user message BEFORE adding it to chat history
        const isFirstUserMessage = activeChat.chatHistory.filter((m: any) => m.sender === 'user').length === 0;
        const titleWords = displayMessage.split(' ');
        const updatedTitle = isFirstUserMessage
            ? titleWords.slice(0, 5).join(' ') + (titleWords.length > 5 ? '...' : '')
            : activeChat.title;
        
        // Add user message to chat history immediately
        setChats(prevChats => {
            const currentChat = prevChats[currentActiveChatId];
            return {
            ...prevChats,
            [currentActiveChatId]: {
                    ...currentChat,
                title: updatedTitle,
                    chatHistory: [...currentChat.chatHistory, newUserMessage]
            }
            };
        });

        setIsLoading(true);
        setStreamingResponse('');  // Clear previous streaming response
        setStreamingThinking('');  // Clear previous thinking
        streamingThinkingRef.current = '';  // Reset ref for new message
        streamingResponseRef.current = '';  // Reset response ref for new message
        // (reload_project tracking removed — always save locally now)
        setActivityLog([]);  // Clear previous activity log
        activityLogRef.current = [];  // Reset ref for new message
        activitySequenceRef.current = 0;  // Reset sequence counter for new message
        setActiveToolCall(null);  // Clear any active tool call
        setActiveWorkers({});  // Clear workers
        setWorkflowPhase(null);  // Clear phase
        dispatchResearch({ type: 'RESET_ALL' });  // Clear stale research progress
        currentResearchTrackingIdRef.current = null;

        // IMMEDIATELY add a BARK message to history - will be updated in real-time as events come in
        // This ensures NO LOSS of data if user cancels mid-stream
        const streamingMessageIndex = activeChat.chatHistory.length;  // Index of the new message (after user message)
        setChats(prevChats => {
            const currentChat = prevChats[currentActiveChatId];
            return {
                ...prevChats,
                [currentActiveChatId]: {
                    ...currentChat,
                    chatHistory: [...currentChat.chatHistory, {
                        sender: 'bark',
                        message: '',  // Empty - content is in activityLog
                        activityLog: [],  // Will be updated in real-time
                        streamingThinking: '',  // Thinking tokens - persisted in real-time
                        streamingResponse: '',  // Response tokens - persisted in real-time
                        _isStreaming: true  // Marker for streaming message
                    }]
                }
            };
        });

        // Track active research for this project (for loading state restoration)
        if (activeProject?.project_id) {
            setActiveResearch(activeProject.project_id);
        }

        // Transform regulatory settings before sending to API
        const transformedSettings = Object.keys(regulatorySettings).length > 0 ? transformRegulatorySettings(regulatorySettings) : {};

        // No conversation history is sent. The orchestrator loads the project's
        // earlier turns itself from the chat event log (BAR-1551): the chat pane
        // only holds the page of events on screen, so a history built here
        // shrank with the pane and BARK forgot everything above the fold.

        // Now save user message to local events (AFTER getting conversation history)
        // This prevents the current message from being included in the API's conversation history
        // IMPORTANT: Use addLocalEvent for immediate display in pure event-based architecture
        // The backend will save the user_message event to Firestore subcollection
        console.log('[Chat] Checking activeProject for user message:', activeProject?.project_id || 'null');
        if (activeProject) {
            const userMessageMetadata: Record<string, any> = {
                user_id: userData?.email || 'ui-test-user',
                user_name: userData?.name || 'User',
                user_workspace: workspaceMode,
                ...(uploadedFiles.length > 0 ? { uploaded_files: uploadedFiles } : {}),
            };
            // Show the user's message immediately. Use a deterministic event_id
            // tied to the client message_id so the optimistic copy and the
            // backend-persisted copy are the SAME document — no duplicate turn.
            addLocalEvent({
                project_id: activeProject.project_id,
                event_id: `usermsg-${clientMessageId}`,
                message_id: clientMessageId,
                type: 'user_message',
                content: displayMessage,
                timestamp: new Date().toISOString(),
                metadata: userMessageMetadata,
            });
            // BAR-146 (persist-on-send): durably persist the user turn NOW, at
            // send time, independent of the agent/WebSocket. The orchestrator
            // also persists it when the turn starts; both use the same client
            // message_id and report-library collapses them into one
            // deterministic `usermsg-{message_id}` document. This is what makes
            // the prompt survive a dropped/idle connection or a hard refresh
            // even when the agent turn never starts. Fire-and-forget — the
            // optimistic copy above already renders it; we only need the durable
            // write to land (the orchestrator write is the fallback).
            void persistEvent(
                activeProject.project_id,
                userData?.email || 'ui-test-user',
                'user_message',
                displayMessage,
                userMessageMetadata,
                undefined,
                clientMessageId,
            ).then(res => {
                if (!res.success) {
                    console.error('[Chat] persist-on-send failed for user message:', res.error);
                }
            }).catch(err => console.error('[Chat] persist-on-send threw:', err));
        }

        // Get active artifact info for context injection
        const activeArtifact = productionState?.activeArtifactId
            ? productionState.artifacts.find(a => a.artifact_id === productionState.activeArtifactId)
            : null;

        // Construct enhanced payload with conversational support
        const payload = {
            query: messageText,
            project_id: activeProject?.project_id || null,
            context: {
                research_depth: researchDepth.toLowerCase(),
                available_reports: activeChat.reports || {},
                selected_report_context: null,  // Backend will fetch report using selected_report_id
                user_name: userData?.name || "User",
                user_id: userData?.email || "ui-test-user",  // Move user_id to context
                client_message_id: clientMessageId,  // BAR-146: orchestrator reuses this id so its user-message write is idempotent with persist-on-send
                use_multi_stage_synthesis: useMultiStageValidation,  // Multi-stage validation flag
                user_workspace: workspaceMode,  // User's viewport — tells BARK where user is looking (not routing)
                repair_dispatch: !!options.repair,  // BAR-192: Fix/Find-source button → backend done-gate (no text sniffing)
                ...(uploadedFiles.length > 0 && {
                    uploaded_files: uploadedFiles,
                    uploaded_file_ids: uploadedFiles.map((file: any) => file.upload_id).filter(Boolean),
                }),
                // Active artifact for context injection (Google Doc/Sheet open in iframe)
                ...(activeArtifact && {
                    active_artifact: {
                        artifact_id: activeArtifact.artifact_id,
                        document_id: activeArtifact.document_id,
                        type: activeArtifact.type,
                        title: activeArtifact.title,
                    }
                }),
                ...(Object.keys(transformedSettings).length > 0 && { regulatory_settings: transformedSettings })
            },
            // NEW CONVERSATIONAL FIELDS:
            use_conversational_flow: true,  // Explicitly request conversational flow
            session_id: currentActiveChatId?.toString() || null,
            parent_research_id: activeChat.lastResearchId || null,
            selected_report_id: (activeView === 'report' && isCanvasMode && selectedReportId) ? selectedReportId : null,
            skip_query_refinement: false,
            // Legacy fields:
            research_id: isFirstUserMessage ? null : (activeChat.clarification_context?.research_id || null)
        };
        
        // Helper function to add event to activity log AND update chat history in real-time
        const addActivityEvent = (entry: { type: string; content: string; timestamp: number }) => {
            // Update ref immediately
            activityLogRef.current = [...activityLogRef.current, entry];
            // Update state for streaming display
            setActivityLog(prev => [...prev, entry]);
            // Update chat history in real-time (so events persist if cancelled)
            setChats(prevChats => {
                const currentChat = prevChats[currentActiveChatId];
                if (!currentChat) return prevChats;
                const updatedHistory = [...currentChat.chatHistory];
                // Find the last BARK message (the streaming one)
                for (let i = updatedHistory.length - 1; i >= 0; i--) {
                    if (updatedHistory[i].sender === 'bark' && updatedHistory[i]._isStreaming) {
                        updatedHistory[i] = {
                            ...updatedHistory[i],
                            activityLog: [...activityLogRef.current]
                        };
                        break;
                    }
                }
                return {
                    ...prevChats,
                    [currentActiveChatId]: {
                        ...currentChat,
                        chatHistory: updatedHistory
                    }
                };
            });
        };

        try {
            // Mark the workflow request active up front — before the streaming
            // callback is registered — so the watchdog is suppressed for the
            // whole in-flight request, not just from a late point inside it.
            activeWorkflowRequestRef.current = true;
            activeWorkflowRequestStartRef.current = Date.now();

            // Debug report context logic
            console.log('[UI] Report context debug:', {
                activeView,
                isCanvasMode,
                selectedReportId,
                shouldIncludeReport: (activeView === 'report' && isCanvasMode && selectedReportId),
                finalReportId: (activeView === 'report' && isCanvasMode && selectedReportId) ? selectedReportId : null
            });
            console.log('[UI] About to send payload:', payload);

            // BAR-53: capture the project this turn was sent for. The streaming
            // callback stays installed for the whole turn; an untagged late op
            // must be scoped to THIS turn's project, not whatever is live now.
            const turnProjectId = activeProject?.project_id;
            // Set up streaming callback for real-time progress updates
            // NOTE: Redis subscription is preferred when available (production), but
            // we ALWAYS set up the callback as fallback for local dev (Redis may not run)
            api.setStreamingCallback((event) => {
                console.log('[Streaming] Event received:', event.type, event.data);

                // BAR-53: this callback stays installed for the whole agent turn.
                // If the user switches projects mid-stream, the previous project's
                // events must not mutate the now-active project's UI. Drop any
                // event explicitly tagged for a different project. (Untagged frames
                // like heartbeats carry no project state and fall through.)
                const liveStreamPid = useProjectStore.getState().activeProject?.project_id;
                if (event.project_id && liveStreamPid && event.project_id !== liveStreamPid) {
                    return;
                }

                // Deduplicate structural events against the Redis subscription path.
                // Both the direct WebSocket and the Redis subscription deliver the same
                // events. For high-frequency events (token, thinking) only the subscription
                // accumulates — the callback just breaks. For structural events (tool_call,
                // tool_result, etc.) both paths historically processed them, causing duplicates
                // in the activity log. Now we use processedEventIdsRef as a shared dedup set:
                // whichever path fires first claims the event, the other skips it.
                if (STRUCTURAL_EVENTS.has(event.type)) {
                    const eventId = event.event_id || `${event.type}-${event.sequence || Date.now()}`;
                    if (processedEventIdsRef.current.has(eventId)) {
                        console.log('[Streaming] Skipping duplicate (subscription already processed):', eventId);
                        return;
                    }
                    processedEventIdsRef.current.add(eventId);
                    // Cap the set size to prevent memory leaks
                    if (processedEventIdsRef.current.size > 1000) {
                        const entries = Array.from(processedEventIdsRef.current);
                        processedEventIdsRef.current = new Set(entries.slice(-500));
                    }
                }

                // Update streaming status based on event type
                switch (event.type) {
                    case 'system':
                        // Heartbeat events keep WS alive during long tools — ignore in UI
                        if (event.data?.type === 'heartbeat') break;
                        // Research detail events — granular progress from v3 pipeline
                        if (event.data?.type === 'research_detail') {
                            const detailTrackingId = event.tracking_id || currentResearchTrackingIdRef.current || 'unknown';
                            dispatchResearch({ type: 'DETAIL', trackingId: detailTrackingId, payload: { message: event.data.message || '' } });
                            break;
                        }
                        if (event.data?.message === 'connected') {
                            setStreamingStatus('Connected to workflow service...');
                        } else if (event.data?.message === 'planning_complete') {
                            setStreamingStatus(`Planning complete. ${formatCountOrFallback(
                                event.data?.worker_count,
                                { one: 'research worker', other: 'research workers' },
                                'Multiple',
                            )} assigned.`);
                        } else if (event.data?.message === 'execution_started') {
                            setStreamingStatus(`Executing research (${event.data?.mode || 'parallel'} mode)...`);
                        } else if (event.data?.message === 'phase_started') {
                            setStreamingStatus(`Phase ${event.data?.phase_number || '?'}: ${formatCountOrFallback(
                                event.data?.worker_count,
                                { one: 'worker', other: 'workers' },
                                '?',
                            )} active`);
                        } else if (event.data?.message === 'execution_completed') {
                            setStreamingStatus('Synthesizing findings...');
                        } else if (event.data?.event === 'merge_complete') {
                            setStreamingStatus(`Merged ${formatCountOrFallback(
                                event.data?.total_findings,
                                { one: 'finding', other: 'findings' },
                                '?',
                            )} from ${formatCountOrFallback(
                                event.data?.workers,
                                { one: 'worker', other: 'workers' },
                                '?',
                            )}`);
                        } else if (event.data?.event === 'critique_complete') {
                            setStreamingStatus('Validating findings...');
                        } else if (event.data?.event === 'report_complete') {
                            setStreamingStatus('Generating final report...');
                        } else {
                            setStreamingStatus(event.data?.message || 'Processing...');
                        }
                        break;
                    case 'phase_change': {
                        // Track workflow phase changes
                        const newPhase = event.data?.new_phase || event.data?.to || 'unknown';
                        const streamTrackingId = event.tracking_id || currentResearchTrackingIdRef.current || 'unknown';
                        setWorkflowPhase(newPhase);
                        setStreamingStatus(`Phase: ${newPhase}`);
                        handleResearchEvent(streamTrackingId, 'phase_change', event.data || {});
                        addActivityEvent({
                            type: 'phase',
                            content: `Entering ${newPhase} phase`,
                            timestamp: Date.now()
                        });
                        break;
                    }
                    case 'research_wave_start':
                    case 'coverage_metrics':
                    case 'research_gap_analysis':
                    case 'premise_result': {
                        const streamTrackingId2 = event.tracking_id || currentResearchTrackingIdRef.current || 'unknown';
                        handleResearchEvent(streamTrackingId2, event.type, event.data || {});
                        break;
                    }
                    case 'workspace_update':
                        // Handle handoff events (legacy chat-chip context only)
                        if (event.data?.primitive_type === 'handoff') {
                            setHandoffPackage(event.data.payload as HandoffPackage);
                        }
                        // Apply workspace operation in real-time
                        if (event.data?.operation) {
                            console.log('[Workspace] Applying real-time operation:', event.data.operation.type);
                            if (event.data.operation.type === 'reload_project') {
                                console.log('[Workspace] reload_project — reloading workspace state from Report Library');
                                const liveReloadPid = useProjectStore.getState().activeProject?.project_id;  // BAR-53: live, not closure
                                const liveReloadEmail = userDataRef.current?.email;  // BAR-53: live email, not the closure
                                if (liveReloadPid && liveReloadEmail) {
                                    useProjectStore.getState().loadWorkspaceState(liveReloadPid, liveReloadEmail);
                                }
                            } else if (event.data.operation.type === 'workspace_switch') {
                                const switchData = event.data.operation.data;
                                // BAR-53 (F5): only retarget the shared workspace indicator if
                                // we're still on this turn's project.
                                if (switchData?.to && useProjectStore.getState().activeProject?.project_id === turnProjectId) {
                                    const target = switchData.to as string;
                                    const from = (switchData.from as string) || useProjectStore.getState().barkWorkspace;
                                    const modeLabels: Record<string, string> = { plan: 'Plan', institution_context: 'Institution context', research: 'Research', production: 'Build' };
                                    const validModes = ['plan', 'institution_context', 'research', 'production', 'output_package'];
                                    const targetMode = validModes.includes(target) ? target as any : 'research';
                                    const targetNodeName = switchData.target_node_name || '';
                                    console.log('[Workspace] BARK switched workspace:', from, '->', target, targetNodeName ? `(${targetNodeName})` : '');
                                    // Update shared BARK workspace indicator (visible to all users via tree icon)
                                    // User's viewport is NOT moved — they stay where they are
                                    useProjectStore.getState().setBarkWorkspace(targetMode, turnProjectId);
                                    useProjectStore.getState().setBarkWorkspaceName(targetNodeName, turnProjectId);
                                    const displayName = targetNodeName || modeLabels[target] || target;
                                    setStreamingStatus(`BARK moved to ${displayName}`);
                                    // Add to streaming activity log (with dedup guard)
                                    const alreadyInStreamLog = activityLogRef.current.some(e => {
                                        if (e.type !== 'workspace_switch') return false;
                                        try {
                                            const existing = JSON.parse(e.content);
                                            return existing.from === from && existing.to === target;
                                        } catch { return false; }
                                    });
                                    if (!alreadyInStreamLog) {
                                        addActivityEvent({
                                            type: 'workspace_switch',
                                            content: JSON.stringify({ from, to: target, targetNodeName, initiator: 'agent' }),
                                            timestamp: Date.now(),
                                        });
                                    }
                                }
                            } else {
                                // Scope guard (#89): drop workspace_update ops
                                // whose workspace_id does not match the current
                                // view. Prevents cross-workspace pollution when
                                // the agent is working in a different workspace
                                // than the one the user is looking at.
                                const opWsId = event.data?.workspace_id
                                    || event.data?.operation?.data?.workspace_id
                                    || event.data?.payload?.workspace_id;
                                const currentWsId = useProjectStore.getState().dictWorkspace?.workspace_id;
                                if (opWsId && currentWsId && opWsId !== currentWsId) {
                                    console.log(
                                        '[Workspace] Dropping cross-workspace op:',
                                        event.data.operation.type,
                                        'op_ws=', opWsId,
                                        'current_ws=', currentWsId,
                                    );
                                } else {
                                    handlePlanningWorkspaceUpdate(event.data.operation, event.project_id ?? turnProjectId);
                                    // BAR-53: scope ops to the event's source project (the turn's
                                    // project, not whatever is live now — an untagged late op after
                                    // a switch must drop rather than land on the new project).
                                    applyWorkspaceOperations([event.data.operation], event.project_id ?? turnProjectId);
                                }
                            }
                        }
                        break;
                    case 'worker_update':
                        // Track individual worker status
                        const workerId = event.data?.worker_id || event.data?.id;
                        const workerStatus = event.data?.status || 'working';
                        const workerArea = event.data?.area || event.data?.research_area;
                        if (workerId) {
                            setActiveWorkers(prev => ({
                                ...prev,
                                [workerId]: {
                                    status: workerStatus,
                                    area: workerArea,
                                    progress: event.data?.progress
                                }
                            }));
                            // Only log significant worker events (started, completed)
                            if (workerStatus === 'started' || workerStatus === 'completed') {
                                addActivityEvent({
                                    type: 'worker',
                                    content: `Worker ${workerId} ${workerStatus}${workerArea ? ` (${workerArea})` : ''}`,
                                    timestamp: Date.now()
                                });
                            }
                        }
                        setStreamingStatus(`Worker ${workerId || '?'}: ${workerStatus}...`);
                        break;
                    case 'finding': {
                        // Accumulate findings during research workflow
                        const findingSummary = event.data?.summary || event.data?.content || '';
                        if (findingSummary) {
                            const findingId = event.data?.finding_id || event.data?.id || `finding-${Date.now()}`;
                            const findingTrackingId = event.tracking_id || currentResearchTrackingIdRef.current || 'unknown';
                            handleResearchEvent(findingTrackingId, 'finding', event.data || {});
                            setResearchFindings(prev => [...prev, {
                                id: findingId,
                                summary: findingSummary,
                                source: event.data?.source || event.data?.source_id || event.data?.document,
                                confidence: event.data?.confidence,
                                timestamp: Date.now()
                            }]);

                            // Add to activity log
                            addActivityEvent({
                                type: 'finding',
                                content: findingSummary.length > 80 ? findingSummary.substring(0, 80) + '...' : findingSummary,
                                timestamp: Date.now()
                            });

                            // Convert finding to workspace operation to add to Evidence Board.
                            // Scope guard (#89): don't add to the Plan view —
                            // findings belong in the child research/build
                            // workspace, not the top-level plan.
                            const liveFindingProject = useProjectStore.getState().activeProject;  // BAR-53: live, not the streaming closure
                            const liveFindingPlanWsId = liveFindingProject ? `ws_${liveFindingProject.project_id}` : '';
                            const liveFindingCurrentWsId = useProjectStore.getState().dictWorkspace?.workspace_id || '';
                            const liveFindingIsPlanView = !liveFindingCurrentWsId || liveFindingCurrentWsId === liveFindingPlanWsId;
                            if (liveFindingProject && !liveFindingIsPlanView) {
                                const workspaceOp = {
                                    type: 'add_evidence' as const,
                                    data: {
                                        node_id: crypto.randomUUID(),
                                        node_type: 'finding' as const,
                                        label: findingSummary.length > 80 ? findingSummary.substring(0, 77) + '...' : findingSummary,
                                        relevance_note: `Found by ${event.data?.worker_id || 'research worker'} using ${event.data?.tool_used || 'graph tools'}`,
                                        finding_id: findingId,
                                        content: event.data?.content || findingSummary,
                                        detail: event.data?.metadata ? JSON.stringify(event.data.metadata) : undefined,
                                        reference_ids: [],
                                        finding: {
                                            finding_id: findingId,
                                            worker_id: event.data?.worker_id || 'unknown',
                                            source_type: event.data?.source_type || 'content_search',
                                            source_id: event.data?.source_id || event.data?.source || 'unknown',
                                            content: event.data?.content || findingSummary,
                                            pillar: event.data?.pillar || 'primary_regulation',
                                            confidence: event.data?.confidence || 0.7,
                                            completeness: event.data?.completeness || 0.5,
                                            tool_used: event.data?.tool_used || 'unknown',
                                            timestamp: event.data?.timestamp || new Date().toISOString(),
                                            related_concepts: event.data?.related_concepts || [],
                                            related_findings: event.data?.related_findings || [],
                                            metadata: event.data?.metadata || {}
                                        }
                                    }
                                };

                                console.log('[Finding] Converting to workspace operation:', workspaceOp);
                                applyWorkspaceOperations([workspaceOp], event.project_id ?? turnProjectId);  // BAR-53: scope ops to the turn's project
                            }
                        }
                        setStreamingStatus(`Found: ${findingSummary.substring(0, 60)}...`);
                        break;
                    }
                    case 'progress':
                        setStreamingStatus(event.data?.message || `Progress: ${event.data?.percent || '?'}%`);
                        break;
                    case 'error':
                        const errorMsg = event.data?.error || 'Unknown error';
                        setStreamingStatus(`Error: ${errorMsg}`);
                        setWorkflowError(errorMsg);
                        break;
                    // Conversational orchestrator events.
                    // NOTE: thinking/token/response accumulation is owned by the
                    // Redis subscription path (the single delivery path) — don't
                    // also accumulate here or the same tokens double-append
                    // (BAR-266 review).
                    case 'thinking':
                        setStreamingStatus('Thinking...');
                        break;
                    case 'token':
                        break;
                    case 'tool_call':
                        // Show which tool is being invoked
                        const toolName = event.data?.tool || null;
                        setActiveToolCall(toolName);
                        setStreamingStatus(`Using ${toolName}...`);

                        // Capture any accumulated RESPONSE as a "response" block before tool call
                        // This ensures: response1 → tool → response2 (not all merged)
                        if (streamingResponseRef.current.trim().length > 0) {
                            addActivityEvent({
                                type: 'response',
                                content: streamingResponseRef.current.trim(),
                                timestamp: Date.now() - 2  // Before thinking and tool
                            });
                            activityLogRef.current = activityLogRef.current.filter(e => e.type !== 'response_stream');
                            setActivityLog([...activityLogRef.current]);
                            streamingResponseRef.current = '';
                            setStreamingResponse('');
                        }

                        // Capture any accumulated thinking as a "thinking_block" before tool call
                        // This ensures chronological ordering: thinking → tool → thinking → tool...
                        if (streamingThinkingRef.current.trim().length > 0) {
                            addActivityEvent({
                                type: 'thinking_block',
                                content: streamingThinkingRef.current.trim(),
                                timestamp: Date.now() - 1  // Slightly before tool call
                            });
                            // IMPORTANT: Remove thinking_stream from activityLog since we captured it as thinking_block
                            // This prevents duplication and enables true chronological ordering
                            activityLogRef.current = activityLogRef.current.filter(e => e.type !== 'thinking_stream');
                            setActivityLog([...activityLogRef.current]);
                            // Clear thinking for next block
                            streamingThinkingRef.current = '';
                            setStreamingThinking('');
                        }

                        // Add tool call to activity log with clearer description
                        if (toolName) {
                            // Format tool name for better readability
                            const formattedTool = toolName
                                .replace(/_/g, ' ')
                                .replace(/^get /, 'Getting ')
                                .replace(/^read /, 'Reading ')
                                .replace(/^add /, 'Adding ')
                                .replace(/^set /, 'Setting ')
                                .replace(/^search /, 'Searching ');
                            addActivityEvent({
                                type: 'tool_call',
                                content: `🔧 ${formattedTool}`,
                                timestamp: Date.now()
                            });
                        }
                        // DON'T clear thinking - it persists as part of the timeline
                        if (toolName === 'research_workflow') {
                            setResearchFindings([]);
                            setActiveWorkers({});
                            setWorkflowPhase(null);
                            setWorkflowError(null);
                        }
                        break;
                    case 'research_started': {
                        // Backend emits this with the real tracking_id right before launch
                        const trackingId = event.data?.tracking_id || event.tracking_id || `stream-${Date.now()}`;
                        currentResearchTrackingIdRef.current = trackingId;
                        dispatchResearch({ type: 'START', trackingId, messageId: event.message_id });
                        break;
                    }
                    case 'tool_result': {
                        // Clear tool call indicator when complete
                        setActiveToolCall(null);
                        const toolResultName = event.data?.tool || 'tool';
                        // See the streaming handler above: a tool-supplied
                        // outcome ("5 approved · 2 flagged") beats completed/failed.
                        const toolOutcome = describeToolOutcome(toolResultName, event.data);
                        if (event.data?.success) {
                            setStreamingStatus(toolOutcome ?? `${toolResultName} complete`);
                            addActivityEvent({
                                type: 'tool_result',
                                content: toolOutcome ?? `${toolResultName} completed`,
                                timestamp: Date.now()
                            });
                        } else {
                            setStreamingStatus(toolOutcome ?? `${toolResultName} failed`);
                            addActivityEvent({
                                type: 'tool_error',
                                content: toolOutcome ?? `${toolResultName} failed`,
                                timestamp: Date.now()
                            });
                        }
                        break;
                    }
                    case 'user_question':
                        // Agent is asking the user a structured question — render as question card
                        // Flush any accumulated response first
                        if (streamingResponseRef.current.trim().length > 0) {
                            addActivityEvent({
                                type: 'response',
                                content: streamingResponseRef.current.trim(),
                                timestamp: Date.now() - 1
                            });
                            activityLogRef.current = activityLogRef.current.filter(e => e.type !== 'response_stream');
                            streamingResponseRef.current = '';
                            setStreamingResponse('');
                        }
                        addActivityEvent({
                            type: 'user_question',
                            content: JSON.stringify(event.data || {}),
                            timestamp: Date.now()
                        });
                        setActiveToolCall(null);
                        setPendingQuestion(event.data || null);
                        setStreamingStatus('Waiting for your answer...');
                        break;
                    case 'complete':
                        // Clear debounce timeout and flush final state
                        if (activityLogUpdateTimeoutRef.current) {
                            clearTimeout(activityLogUpdateTimeoutRef.current);
                            activityLogUpdateTimeoutRef.current = null;
                        }
                        setActivityLog([...activityLogRef.current]);

                        // NOTE: research_progress is now persisted by the backend
                        // (saved as a placeholder at tool_call time, PATCHed with results on completion)

                        // Conversation/workflow complete - clear streaming state
                        setActiveToolCall(null);
                        setStreamingStatus(null);
                        // Note: Don't COMPLETE research here — streaming 'complete' means the agent
                        // finished talking, NOT that research finished. Research continues in background
                        // and completes via the subscription handler's 'complete' event.
                        // Clear research state on completion
                        setResearchFindings([]);
                        setActiveWorkers({});
                        setWorkflowPhase(null);
                        setWorkflowError(null);  // Clear error on successful completion
                        break;
                    case 'research_mission':
                        // Research mission set or updated
                        if (event.data) {
                            setResearchMission(event.data as ResearchMission);
                            console.log('[Live] Research mission updated:', event.data.query);
                        }
                        break;
                    case 'workspace_switched': {
                        // Backend-initiated workspace switch (agent requested and auto-approved)
                        // BAR-53 (F5): scope to this turn's project so an untagged event
                        // can't retarget the shared indicator after a mid-stream switch.
                        if (event.data?.workspace && useProjectStore.getState().activeProject?.project_id === turnProjectId) {
                            const target = event.data.workspace as string;
                            const from = (event.data.from as string) || useProjectStore.getState().barkWorkspace;
                            const targetNodeName = (event.data.target_node_name as string) || '';
                            const modeLabels: Record<string, string> = { plan: 'Plan', institution_context: 'Institution context', research: 'Research', production: 'Build' };
                            const validModes = ['plan', 'institution_context', 'research', 'production', 'output_package'];
                            const targetMode = validModes.includes(target) ? target as any : 'research';
                            const displayName = targetNodeName || modeLabels[target] || target;
                            console.log('[Workspace] BARK switched workspace to:', target, targetNodeName ? `(${targetNodeName})` : '');
                            // Update shared BARK workspace indicator only — no viewport change
                            useProjectStore.getState().setBarkWorkspace(targetMode, turnProjectId);
                            useProjectStore.getState().setBarkWorkspaceName(targetNodeName, turnProjectId);
                            setStreamingStatus(`BARK moved to ${displayName}`);
                            // Add to streaming activity log so the banner renders in the timeline
                            const alreadyLogged = activityLogRef.current.some(e => {
                                if (e.type !== 'workspace_switch') return false;
                                try {
                                    const existing = JSON.parse(e.content);
                                    return existing.from === from && existing.to === target;
                                } catch { return false; }
                            });
                            if (!alreadyLogged) {
                                addActivityEvent({
                                    type: 'workspace_switch',
                                    content: JSON.stringify({ from, to: target, targetNodeName, initiator: 'agent' }),
                                    timestamp: Date.now(),
                                });
                            }
                        }
                        break;
                    }
                    case 'workspace_switch_proposal':
                        // Agent proposes a workspace switch for user approval
                        if (event.data) {
                            console.log('[Workspace] Switch proposal received:', event.data);
                            const { activeWorkspace: currentWorkspace, proposeWorkspaceSwitch: propose } = useProjectStore.getState();
                            propose({
                                from_workspace: event.data.from_workspace || currentWorkspace,
                                to_workspace: event.data.to_workspace,
                                reason: event.data.reason || 'Agent suggests switching workspace',
                                key_findings: event.data.key_findings,
                                recommended_structure: event.data.recommended_structure,
                            });
                        }
                        break;
                    case 'handoff_sync':
                        // Research workspace changes affected a handoff - show notification
                        if (event.data) {
                            console.log('[LiveSync] Handoff sync event received:', event.data);
                            const { setHandoffSyncNotification } = useProjectStore.getState();
                            if (setHandoffSyncNotification) {
                                setHandoffSyncNotification({
                                    handoff_id: event.data.handoff_id,
                                    summary: event.data.summary || 'Handoff updated',
                                    new_count: event.data.new_count || 0,
                                    updated_count: event.data.updated_count || 0,
                                    removed_count: event.data.removed_count || 0,
                                    changes: event.data.changes || {},
                                    timestamp: event.data.timestamp || new Date().toISOString(),
                                });
                            }
                            // Also update streaming status to show notification
                            setStreamingStatus(`Handoff updated: ${event.data.summary || 'Research changes detected'}`);
                        }
                        break;
                    default:
                        // Keep existing status for unknown events
                        break;
                }
            });

        // The orchestrator signs this whole turn with the token its WebSocket
        // opens with, so start the turn with one that outlives it (see
        // resolveTurnToken). Not while impersonating: that session has no
        // refresh token (D5), as with the 50-min interval above.
        const turnToken = impersonation
            ? (authToken || "")
            : await resolveTurnToken(authToken || "", refreshAccessToken);
        const response: TransformedResponse = await api.sendMessage(
            payload,
            turnToken,
            activeChat.service_type
        );

        // Add any remaining thinking as a final block (for chronological ordering)
        if (streamingThinkingRef.current.trim().length > 0) {
            addActivityEvent({
                type: 'thinking_block',
                content: streamingThinkingRef.current.trim(),
                timestamp: Date.now() - 1  // Slightly before response
            });
        }

        // Add the response as the final block in the unified timeline
        if (streamingResponseRef.current.trim().length > 0) {
            addActivityEvent({
                type: 'response',
                content: streamingResponseRef.current.trim(),
                timestamp: Date.now()
            });
        }

        // Capture activity log BEFORE clearing (needed for project store sync)
        const capturedActivityLog = [...activityLogRef.current];
        console.log('[Chat] About to finalize streaming message - activityLog count:', capturedActivityLog.length);

        // Clear streaming callback and status after completion
        api.setStreamingCallback(null);
        setStreamingStatus(null);
        setStreamingResponse('');  // Clear streaming response
        setStreamingThinking('');  // Clear thinking
        streamingThinkingRef.current = '';  // Clear ref
        streamingResponseRef.current = '';  // Clear response ref
        setActivityLog([]);  // Clear activity log
        activityLogRef.current = [];  // Clear ref
        activitySequenceRef.current = 0;  // Reset sequence counter
        setActiveToolCall(null);  // Clear tool call
        setActiveWorkers({});  // Clear workers
        setWorkflowPhase(null);  // Clear phase
        setWorkflowError(null);  // Clear error

        console.log('[UI] Full API response:', response);
        if (response.references) {
            console.log('[UI] References received:', response.references);
            response.references.forEach((ref, index) => {
                console.log(`[UI] Reference ${index}:`, {
                    id: ref.id,
                    document: ref.document,
                    document_reference: ref.document_reference,
                    title: ref.title,
                    summary: ref.summary,
                    logical_connections: ref.logical_connections,
                    connection_type: ref.connection_type,
                    logical_rationale: ref.logical_rationale
                });
            });
        }
        
        // NOTE: Do NOT set isLoading(false) here. The main WS promise resolving
        // does not mean the agent is done — the subscription WS handles that.
        // isLoading is set to false when the subscription receives 'complete'.
        // setIsLoading(false);  // REMOVED — subscription-driven
        clearActiveResearch();

        if (response.success) {
            // Track assistant message for project store - capture BEFORE setChats since React 18 batches updates
            let assistantMessageContent: string | null = null;
            if (response.response_type === 'clarification') {
                assistantMessageContent = response.content;
            } else if (response.response_type === 'conversation') {
                assistantMessageContent = response.content;
            } else if (response.response_type === 'report' && response.report) {
                // Report generated - content stored in chat, no link message needed
                assistantMessageContent = response.content || "Research complete.";
            }
            console.log('[Chat] Captured assistantMessageContent:', assistantMessageContent?.substring(0, 50));

            // Only update local chats state for NON-project chats
            // For project chats, we'll update project store directly
            if (!activeProject) {
                setChats(prevChats => {
                    console.log('[Chat] setChats callback - response_type:', response.response_type, 'chatId:', currentActiveChatId);
                    const currentChat = prevChats[currentActiveChatId];
                if (!currentChat) {
                    console.error('[Chat] ERROR: currentChat is undefined for id:', currentActiveChatId);
                    return prevChats;
                }

                // FINALIZE the streaming message - it's already in history from real-time updates
                // Find the streaming message and update it with final properties
                const updatedHistory = [...currentChat.chatHistory];
                let streamingMsgIndex = -1;
                for (let i = updatedHistory.length - 1; i >= 0; i--) {
                    if (updatedHistory[i].sender === 'bark' && updatedHistory[i]._isStreaming) {
                        streamingMsgIndex = i;
                        break;
                    }
                }

                if (streamingMsgIndex >= 0) {
                    // Remove _isStreaming flag and add any response-specific properties
                    const { _isStreaming, ...streamingMsg } = updatedHistory[streamingMsgIndex];
                    const finalizedMessage = {
                        ...streamingMsg,
                        activityLog: capturedActivityLog,  // Preserve activity log from streaming
                        // Add proposed_plan if present
                        ...(response.proposed_plan && { proposed_plan: response.proposed_plan })
                    };
                    updatedHistory[streamingMsgIndex] = finalizedMessage;
                    console.log('[Chat] Finalized streaming message at index:', streamingMsgIndex, 'activityLog count:', finalizedMessage.activityLog?.length);
                } else {
                    console.warn('[Chat] No streaming message found to finalize');
                }

                let updatedChat = {
                    ...currentChat,
                    title: updatedTitle,
                    chatHistory: updatedHistory
                };

                if (response.response_type === 'clarification') {
                    updatedChat.clarification_context = response.clarification_context;
                    assistantMessageContent = response.content;
                } else if (response.response_type === 'conversation') {
                    assistantMessageContent = response.content;
                    console.log('[Chat] Inside setChats - set assistantMessageContent:', response.content?.substring(0, 50));

                    // Check if we need to automatically continue research
                    if (response.continue_research && response.research_query) {
                        console.log('🔄 Automatically continuing research with query:', response.research_query);

                        // Save the current state first
                        setChats(prev => ({ ...prev, [currentActiveChatId]: updatedChat }));

                        // Continue research automatically
                        setTimeout(async () => {
                            try {
                                console.log('🚀 Starting automatic research continuation');
                                const researchResponse = await fetch(`${api.getWorkflowOrchestratorUrl()}/research`, {
                                    method: 'POST',
                                    headers: {
                                        'Content-Type': 'application/json',
                                        ...(authToken && { 'Authorization': `Bearer ${authToken}` })
                                    },
                                    body: JSON.stringify({
                                        query: response.research_query,
                                        user_id: userData?.email,
                                        use_conversational_flow: false, // Direct research, no more coordination
                                        ...(response.report_instruction && { report_instruction: response.report_instruction })
                                    })
                                });

                                if (researchResponse.ok) {
                                    const researchResult = await researchResponse.json();
                                    console.log('📊 Research continuation completed:', researchResult);

                                    // Update the chat with the research results (no message added, just store report)
                                    if (researchResult.report) {
                                        setChats(prev => {
                                            const currentChat = prev[currentActiveChatId];
                                            if (currentChat) {
                                                const updatedChatWithReport = {
                                                    ...currentChat,
                                                    report: researchResult.report,
                                                    lastResearchId: researchResult.session_id
                                                };

                                                // Store report in reports collection
                                                if (!updatedChatWithReport.reports) updatedChatWithReport.reports = {};
                                                updatedChatWithReport.reports[researchResult.session_id] = {
                                                    report: researchResult.report,
                                                    references: researchResult.references || [],
                                                    research_id: researchResult.session_id,
                                                    created_at: new Date().toISOString()
                                                };

                                                return { ...prev, [currentActiveChatId]: updatedChatWithReport };
                                            }
                                            return prev;
                                        });
                                    }
                                } else {
                                    console.error('❌ Research continuation failed:', researchResponse.status);
                                }
                            } catch (error) {
                                console.error('❌ Error during research continuation:', error);
                            }
                        }, 100); // Small delay to ensure UI state is updated

                        // Return early to avoid duplicate chat updates
                        return;
                    }

                    // Update report and references if provided
                    if (response.report) {
                        // Store in both legacy format and new reports structure
                        updatedChat.report = response.report;
                        updatedChat.lastResearchId = response.session_id;

                        // Store in reports collection with research_id as key
                        if (!updatedChat.reports) updatedChat.reports = {};
                        updatedChat.reports[response.session_id] = {
                            report: response.report,
                            references: response.references || [],
                            research_id: response.session_id,
                            created_at: new Date().toISOString()
                        };
                    }
                    if (response.references) {
                        updatedChat.references = response.references;
                    }

                    // Handle research limitations
                    if (response.conversation_context?.research_limitations) {
                        updatedChat.research_limitations = true;
                    }

                    updatedChat.clarification_context = null;
                } else if (response.response_type === 'report') {
                    // Report generated - store data but don't add link message
                    const reportId = response.report_id || response.session_id;
                    assistantMessageContent = response.content;

                    // Store report data in proper structure (but don't auto-open)
                    if (response.report && reportId) {
                        const transformedReport = {
                            title: response.report.title,
                            content: response.report,
                            structured_sections: response.report.structured_sections || [],
                            reportId: reportId,
                            references: response.references || []
                        };

                        // Add to chat's open reports collection (available but not active)
                        updatedChat = {
                            ...updatedChat,
                            openReports: {
                                ...updatedChat.openReports,
                                [reportId]: transformedReport
                            }
                        };
                    }

                    // Keep legacy format for backward compatibility
                    updatedChat.report = response.report;
                    updatedChat.references = response.references;
                    updatedChat.clarification_context = null;
                }

                return { ...prevChats, [currentActiveChatId]: updatedChat };
            });
            } // End if (!activeProject)

            // Apply workspace operations if present (from conversational agent).
            // BAR-53: only apply if we're still on the project this message was
            // sent for. A deep-research response can resolve minutes later, after
            // the user has switched projects, and these ops + the safety-net
            // reload would otherwise land on the now-active project.
            const liveOpsPid = useProjectStore.getState().activeProject?.project_id;
            const messageOpsPid = activeProject?.project_id;
            if (messageOpsPid && liveOpsPid === messageOpsPid && response.workspace_operations && response.workspace_operations.length > 0) {
                console.log('[App] Applying workspace operations:', response.workspace_operations.length);
                const incrementalOps = response.workspace_operations.filter((op: any) => op.type !== 'reload_project');
                if (incrementalOps.length > 0) {
                    applyWorkspaceOperations(incrementalOps, messageOpsPid);
                }
                // If reload_project is present, fetch full workspace state as safety net
                const hasReload = response.workspace_operations.some((op: any) => op.type === 'reload_project');
                if (hasReload && userData?.email) {
                    console.log('[App] reload_project in response — reloading workspace state from Report Library');
                    useProjectStore.getState().loadWorkspaceState(messageOpsPid, userData.email);
                }
            }

            // Reload events from backend (the single source of truth).
            // The backend has already flushed all events (thinking, tool_call,
            // tool_result, research_progress, response) to Firestore by this point.
            //
            // BAR-53: read the live project id from the store rather than the
            // closure's `activeProject`. Deep-research responses can take
            // minutes; if the user has switched projects since the message
            // was sent, the closure still points to the previous project and
            // reloading its events would clobber the new project's chat.
            const liveProjectIdSuccess = useProjectStore.getState().activeProject?.project_id;
            const messageProjectId = activeProject?.project_id;
            if (liveProjectIdSuccess && messageProjectId && liveProjectIdSuccess === messageProjectId) {
                const { reloadProjectEvents } = useProjectStore.getState();
                console.log('[Chat] Reloading events from backend for project:', liveProjectIdSuccess);
                await reloadProjectEvents(liveProjectIdSuccess, userData?.email || 'ui-test-user');

                // Clear streaming messages from local chats state (project store is source of truth)
                setChats(prevChats => {
                    const currentChat = prevChats[currentActiveChatId];
                    if (!currentChat) return prevChats;
                    const cleanedHistory = currentChat.chatHistory.filter(
                        (msg: any) => msg.sender !== 'bark'
                    );
                    return {
                        ...prevChats,
                        [currentActiveChatId]: {
                            ...currentChat,
                            chatHistory: cleanedHistory
                        }
                    };
                });
            } else if (messageProjectId && liveProjectIdSuccess !== messageProjectId) {
                console.log('[Chat] Skipping reload — user switched projects mid-response (was:', messageProjectId, 'now:', liveProjectIdSuccess, ')');
            }
        } else {
            // Response failed - but we might still have useful streamed content
            // Use streamingResponse as fallback content if available
            const errorText = `Error: ${response.error || 'Unknown error occurred'}`;
            const fallbackContent = streamingResponse && streamingResponse.trim().length > 0
                ? `${streamingResponse}\n\n---\n*${errorText}*`
                : errorText;

            // BAR-53: same live-id check as the success path above — don't
            // overwrite the chat slice if the user has navigated away.
            const liveProjectIdError = useProjectStore.getState().activeProject?.project_id;
            const messageProjectIdError = activeProject?.project_id;
            if (messageProjectIdError && liveProjectIdError === messageProjectIdError) {
                // Reload events from backend (may have partial events from streaming)
                const { reloadProjectEvents } = useProjectStore.getState();
                console.log('[Chat] Error path: reloading events from backend for project:', liveProjectIdError);
                await reloadProjectEvents(liveProjectIdError, userData?.email || 'ui-test-user');

                // Clear streaming messages from local state
                setChats(prevChats => {
                    const currentChat = prevChats[currentActiveChatId];
                    if (!currentChat) return prevChats;
                    const cleanedHistory = currentChat.chatHistory.filter((msg: any) => msg.sender !== 'bark');
                    return {
                        ...prevChats,
                        [currentActiveChatId]: { ...currentChat, chatHistory: cleanedHistory }
                    };
                });
            } else if (messageProjectIdError && liveProjectIdError !== messageProjectIdError) {
                console.log('[Chat] Error path: skipping reload — user switched projects mid-response (was:', messageProjectIdError, 'now:', liveProjectIdError, ')');
            } else if (!messageProjectIdError) {
                // Use old chats state for non-project chat
                setChats(prevChats => {
                    const currentChat = prevChats[currentActiveChatId];
                    const errorMessage = { sender: 'bark', message: fallbackContent };
                    return { ...prevChats, [currentActiveChatId]: { ...currentChat, chatHistory: [...currentChat.chatHistory, errorMessage] } };
                });
            }
        }
        } catch (error) {
            console.error('[UI] Error in handleSendMessage:', error);
            api.setStreamingCallback(null);
            setStreamingStatus(null);
            setStreamingResponse('');
            setStreamingThinking('');
            setActiveToolCall(null);
            setActiveWorkers({});
            setWorkflowPhase(null);
            setIsLoading(false);
            clearActiveResearch();

            const errorMessage = `UI Error: ${error.message || 'Unknown error occurred'}`;

            // BAR-53: only write the UI error into the store if we're still on
            // the project this message was sent for — addChatMessage writes to
            // the store's CURRENT project, so a stale catch must not append the
            // previous project's error to a project the user switched to.
            const liveCatchPid = useProjectStore.getState().activeProject?.project_id;
            const messageCatchPid = activeProject?.project_id;
            if (messageCatchPid && liveCatchPid === messageCatchPid) {
                // Save error to project store
                const { addChatMessage: addChatMessageStore } = useProjectStore.getState();
                console.log('[Chat] Saving UI error to project store:', errorMessage);
                addChatMessageStore('assistant', errorMessage);

                // Clear streaming messages from local state
                setChats(prevChats => {
                    const currentChat = prevChats[currentActiveChatId];
                    if (!currentChat) return prevChats;
                    const cleanedHistory = currentChat.chatHistory.filter((msg: any) => msg.sender !== 'bark');
                    return {
                        ...prevChats,
                        [currentActiveChatId]: { ...currentChat, chatHistory: cleanedHistory }
                    };
                });
            } else if (messageCatchPid && liveCatchPid !== messageCatchPid) {
                // BAR-53: user switched projects before this error surfaced —
                // don't append the old project's error to the new project.
                console.log('[Chat] Catch: skipping error write — project switched mid-request (was:', messageCatchPid, 'now:', liveCatchPid, ')');
            } else {
                // Use old chats state for non-project chat
                setChats(prevChats => {
                    const currentChat = prevChats[currentActiveChatId];
                    return { ...prevChats, [currentActiveChatId]: { ...currentChat, chatHistory: [...currentChat.chatHistory, { sender: 'bark', message: errorMessage }] } };
                });
            }
        } finally {
            // Always clear the active-request flag so a throw on any path can't
            // permanently suppress the subscription watchdog (BAR-266 review).
            activeWorkflowRequestRef.current = false;
        }
    };

    // BAR-40 S1: expose App-owned auth/session state to the route tree via
    // context. Providers are inert until views consume them (S3+); App keeps
    // owning the state. MainApplicationUI-owned bits (handleSelectProject,
    // prefill, isProjectOpening) are populated in S4/S5 as it is decomposed.
    const __authStateValue = {
        isAuthenticated, authToken, userData, resetToken, inviteToken, signupToken, impersonation,
        // BAR-962: while a connector sign-in waits, Home keeps a free account
        // where it is instead of sending it to /connect.
        connectPending: Boolean(connectState),
        authMode,
        connectReturnPath: connectReturnPath(connectState, connectGatewayOrigin),
        loginNotice: loginNotice?.text ?? null,
        // BAR-1564: a failed sign-out is news, not a fault — shown as information.
        loginNoticeTone: loginNotice?.tone ?? 'error',
        forceLoginPrompt,
        sessionRestorePending,
        signingOut,
    };
    // Lookup calls read this to pick their base URL (see lookupApi); set here,
    // in render, so a child's first request already sees the current account.
    // Only the plan: lookupApi checks the chat-free URL per call, after config.json loads.
    const chatFreeActive = userUsesChatFree(userData);
    setLookupUsesChatFree(userData?.plan === 'free');
    const __authActionsValue = {
        handleLoginSuccess, handleLogout, setResetToken, setInviteToken, setSignupToken,
        handleSignOutLate,
        isModuleDisabled: (moduleId: string) =>
            Array.isArray(userData?.disabled_modules) && userData.disabled_modules.includes(moduleId),
        usesChatFree: chatFreeActive,
        startImpersonation, exitImpersonation,
    };
    const __sessionStateValue = {
        chats, activeChatId, streamingStatus, streamingResponse, streamingThinking,
        activityLog, isLoading, activeToolCall, pendingQuestion, activeWorkers,
        workflowPhase, workflowError, hasReconnectedSession, tokenUsage,
        openReports, activeReportId, currentReport, activeView, regulatorySettings,
        researchMission, researchProgress, activeResearchTrackingId: currentResearchTrackingIdRef.current,
        // handoffPackage kept as legacy chat-chip context only; showHandoffPanel/
        // isHandoffOpen were retired with the HandoffPackagePanel UI (D7).
        handoffPackage,
    };
    const __sessionActionsValue = {
        handleSendMessage, handleNewQuery, handleReconnectToStream, handleRestoreResearch,
        resetEphemeralProjectState, resetChatState,
        handleCloseReport, handleSwitchToReport, handleDeleteChat, handleRenameChat,
        handleSelectQuery, handleSettingsChange,
        // S3a: remaining MainApplicationUI handlers/setters (named App sources)
        onServiceSelect: handleServiceSelection,
        getResearchProgress: getProgress,
        setIsLoading, setStreamingResponse, setStreamingThinking,
        setHandoffPackage, setActiveView,
    };

    // BAR-40 S2: the logged-out render now lives in <RequireAuth> (built from
    // auth context). App builds only the authed view as an element (its ~90
    // props reference App's closure, so it must be constructed here, verbatim)
    // and hands it to <AppRoutes> under path='*'. S3-S5 carve real routes out
    // of MainApplicationUI's ternary; S6 deletes this legacy element.
    const __legacyAuthedView = (
        <>
            <GlobalStyles />
            <ErrorBoundary>
            <MainApplicationUI
                activeChat={activeChat}
                allChats={chats}
                onNewQuery={handleNewQuery}
                onSelectQuery={handleSelectQuery}
                onSendMessage={handleSendMessage}
                isLoading={isLoading}
                setIsLoading={setIsLoading}
                streamingStatus={streamingStatus}
                resetEphemeralProjectState={resetEphemeralProjectState}
                streamingResponse={streamingResponse}
                setStreamingResponse={setStreamingResponse}
                streamingThinking={streamingThinking}
                setStreamingThinking={setStreamingThinking}
                streamingActivityLog={activityLog}
                activeToolCall={activeToolCall}
                pendingQuestion={pendingQuestion}
                onAnswerQuestion={(answer: string) => {
                    setPendingQuestion(null);
                    // Send answer on the EXISTING WebSocket — the WS handler routes it to the blocking tool
                    const projectId = useProjectStore.getState().activeProject?.project_id || '';
                    const sent = api.sendQuestionAnswer(answer, projectId, {
                        user_id: userData?.email || '',
                        user_name: userData?.name || '',
                    });
                    if (!sent) {
                        console.warn('[Question] Failed to send answer on active WS, falling back to new message');
                        handleSendMessage(answer, 'standard', false);
                    }
                }}
                onCancelWorkflow={() => {
                    // Send cancel command via WebSocket
                    api.sendCancelCommand();

                    // FINALIZE the streaming message (it's already in history from real-time updates)
                    if (activeChatId) {
                        // Add any remaining thinking to activity log
                        if (streamingThinkingRef.current.trim().length > 0) {
                            activityLogRef.current = [...activityLogRef.current, {
                                type: 'thinking_block',
                                content: streamingThinkingRef.current.trim(),
                                timestamp: Date.now() - 1
                            }];
                        }

                        // Add any accumulated response to activity log
                        if (streamingResponseRef.current.trim().length > 0) {
                            activityLogRef.current = [...activityLogRef.current, {
                                type: 'response',
                                content: streamingResponseRef.current.trim(),
                                timestamp: Date.now()
                            }];
                        }

                        // Add cancelled marker
                        activityLogRef.current = [...activityLogRef.current, {
                            type: 'system',
                            content: 'Cancelled by user',
                            timestamp: Date.now() + 1
                        }];

                        // CAPTURE the activity log NOW, before clearing refs (fixes race condition)
                        const finalActivityLog = [...activityLogRef.current];
                        console.log('[Cancel] Captured finalActivityLog count:', finalActivityLog.length);

                        // FINALIZE the streaming message - find it and remove _isStreaming flag
                        setChats(prev => {
                            const currentChat = prev[activeChatId];
                            if (!currentChat) return prev;

                            const updatedHistory = [...currentChat.chatHistory];
                            // Find the streaming message
                            for (let i = updatedHistory.length - 1; i >= 0; i--) {
                                if (updatedHistory[i].sender === 'bark' && updatedHistory[i]._isStreaming) {
                                    // Finalize: remove flag, update activityLog with captured content
                                    const { _isStreaming, ...streamingMsg } = updatedHistory[i];
                                    updatedHistory[i] = {
                                        ...streamingMsg,
                                        activityLog: finalActivityLog  // Use captured copy, not ref
                                    };
                                    console.log('[Cancel] Finalized streaming message at index:', i, 'activityLog count:', finalActivityLog.length);
                                    break;
                                }
                            }

                            return {
                                ...prev,
                                [activeChatId]: {
                                    ...currentChat,
                                    chatHistory: updatedHistory
                                }
                            };
                        });
                    }

                    // Clear all refs AFTER capturing (order matters!)
                    streamingThinkingRef.current = '';
                    streamingResponseRef.current = '';
                    activityLogRef.current = [];
                    activitySequenceRef.current = 0;  // Reset sequence counter

                    // Clear local state
                    setIsLoading(false);
                    setActiveToolCall(null);
                    setWorkflowError('Cancelled by user');
                    setStreamingStatus('Cancelled');
                    setStreamingResponse('');
                    setStreamingThinking('');
                    setActivityLog([]);
                    setResearchFindings([]);
                    setActiveWorkers({});
                    setWorkflowPhase(null);
                    dispatchResearch({ type: 'RESET_ALL' });
                    currentResearchTrackingIdRef.current = null;

                    // Reload workspace to get latest state from backend after cancellation
                    if (activeProject?.project_id && userData?.email) {
                        const { loadProject } = useProjectStore.getState();
                        loadProject(activeProject.project_id, userData.email);
                    }
                }}
                onSendComment={(text: string) => {
                    // Send comment/intervention via WebSocket
                    api.sendCommentCommand(text);
                }}
                onLogout={handleLogout}
                regulatorySettings={regulatorySettings}
                onSettingsChange={handleSettingsChange}
                onServiceSelect={handleServiceSelection}
                currentReport={currentReport}
                activeView={activeView}
                setActiveView={setActiveView}
                setSelectedReportId={setSelectedReportId}
                onReportLinkClick={handleReportLinkClick}
                isSettingsCollapsed={isSettingsCollapsed}
                setIsSettingsCollapsed={setIsSettingsCollapsed}
                onCloseReport={handleCloseReport}
                onSwitchToReport={handleSwitchToReport}
                userData={userData}
                isCanvasMode={isCanvasMode}
                setIsCanvasMode={setIsCanvasMode}
                openReports={openReports}
                activeReportId={activeReportId}
                onDeleteChat={handleDeleteChat}
                onRenameChat={handleRenameChat}
                resetChatState={resetChatState}
                highlightedNodeId={highlightedNodeId}
                setHighlightedNodeId={setHighlightedNodeId}
                isSourcePanelOpen={isSourcePanelOpen}
                setIsSourcePanelOpen={setIsSourcePanelOpen}
                sourcePanelContent={sourcePanelContent}
                setSourcePanelContent={setSourcePanelContent}
                researchMission={researchMission}
                onReconnectToStream={handleReconnectToStream}
                onRestoreResearch={handleRestoreResearch}
                hasReconnectedSession={hasReconnectedSession}
                reLayoutTrigger={reLayoutTrigger}
                tokenUsage={tokenUsage}
                researchProgress={researchProgress}
                getResearchProgress={getProgress}
                activeResearchTrackingId={currentResearchTrackingIdRef.current}
                handoffPackage={handoffPackage}
                setHandoffPackage={setHandoffPackage}
                authToken={authToken}
                pendingQuestion={pendingQuestion}
                onAnswerQuestion={(answer: string) => {
                    setPendingQuestion(null);
                    const projectId = useProjectStore.getState().activeProject?.project_id || '';
                    const sent = api.sendQuestionAnswer(answer, projectId, {
                        user_id: userData?.email || '',
                        user_name: userData?.name || '',
                    });
                    if (!sent) {
                        console.warn('[Question] Failed to send answer on active WS, falling back');
                        handleSendMessage(answer, 'standard', false);
                    }
                }}
                onOpenWorkspaceDetails={() => setIsWorkspaceDetailsOpen(true)}
                onOpenChangePassword={() => setIsChangePasswordOpen(true)}
            />

            {/* Workspace Details Overlay (#89) — opened from the top-left
                validation pill in any workspace except Plan. Rendered here
                so its state (useState in App) is visible at render time. */}
            {isWorkspaceDetailsOpen
                && workspaceMode !== 'plan'
                && authToken
                && activeProject?.project_id
                && dictWorkspace?.workspace_id && (
                <WorkspaceDetailsOverlay
                    workspaceType={workspaceMode as WorkspaceDetailsType}
                    projectId={activeProject.project_id}
                    workspaceId={dictWorkspace.workspace_id}
                    token={authToken}
                    reportLibraryUrl={api.getReportLibraryUrl()}
                    userId={userData?.email || ''}
                    refreshSignal={workspaceDetailsRefreshSignal}
                    onClose={() => setIsWorkspaceDetailsOpen(false)}
                />
            )}
            </ErrorBoundary>
            {/* Match RequireAuth: an invitation takes precedence when a
                hand-built URL contains both recovery parameters, then a
                free-signup activation (BAR-1528), then a reset. */}
            {inviteToken ? (
                <ForgotPasswordModal
                    key={`invite:${inviteToken}`}
                    initialToken={inviteToken}
                    mode="invite"
                    onClose={() => setInviteToken(null)}
                />
            ) : signupToken ? (
                // Someone is already signed in here, so the activation never
                // replaces that session (no onLoginSuccess); its success view
                // links the connect page of this app instead.
                <ForgotPasswordModal
                    key={`signup:${signupToken}`}
                    initialToken={signupToken}
                    mode="signup"
                    onClose={() => setSignupToken(null)}
                    onOpenConnectGuide={() => {
                        setSignupToken(null);
                        navigate(ROUTE_PATTERNS.connect);
                    }}
                />
            ) : resetToken ? (
                <ForgotPasswordModal
                    key={`reset:${resetToken}`}
                    initialToken={resetToken}
                    mode="reset"
                    onClose={() => setResetToken(null)}
                />
            ) : null}
            {isChangePasswordOpen && authToken && (
                <ChangePasswordModal
                    authToken={authToken}
                    keycloak={authMode === 'keycloak'}
                    onClose={() => setIsChangePasswordOpen(false)}
                    onSessionExpired={handleChangePasswordSessionExpired}
                />
            )}
        </>
    );

    // BAR-40 S2: providers wrap the route tree. RequireAuth gates auth from
    // context; the authed shell renders via AppRoutes' path='*' for now.
    return (
        <AuthProvider state={__authStateValue} actions={__authActionsValue}>
            <SessionProvider state={__sessionStateValue} actions={__sessionActionsValue}>
                {oidcCallbackPending ? (
                    <div role="status" aria-live="polite" className="flex min-h-screen items-center justify-center bg-surface text-fg">
                        <p className="text-sm">Signing you in…</p>
                    </div>
                ) : (
                    <AppRoutes legacyAuthedView={__legacyAuthedView} />
                )}
            </SessionProvider>
            {/* BAR-962: MCP connector hand-off in progress / failed. Rendered
                above the whole tree so it shows on the ordinary app, not a
                separate connector view. */}
            {connectState && isAuthenticated && !connectContext && (
                <div role="status" aria-live="polite" className="fixed inset-0 z-50 flex items-center justify-center bg-surface text-fg">
                    <p className="text-sm">
                        Preparing connector…
                        <button type="button" className="ml-3 inline-flex min-h-8 items-center px-1 underline" onClick={handleConnectorCancel}>Cancel</button>
                    </p>
                </div>
            )}
            <ConfirmDialog
                open={Boolean(connectState && isAuthenticated && connectContext)}
                onCancel={handleConnectorCancel}
                onConfirm={handleConnectorApprove}
                title={`Connect ${connectContext?.client_name ?? 'this connector'} to your BARK account?`}
                description={
                    <>
                        {userData?.email && (
                            <>You are signed in as <strong>{userData.email}</strong>. </>
                        )}
                        <button
                            type="button"
                            className="inline-flex min-h-8 items-center underline"
                            onClick={handleConnectorUseAnotherAccount}
                            disabled={connectSubmitting || connectSwitching}
                        >
                            Use another account
                        </button>
                        {' '}
                        The connector will use {userData?.email ? 'this' : 'your'} BARK account with these permissions.
                        {connectContext?.redirect_origin && (
                            <> After you connect, BARK returns you to <strong>{connectContext.redirect_origin}</strong>.</>
                        )}
                        {connectContext?.preview_gateway && (
                            <> This sign-in goes to a <strong>test gateway</strong> ({connectContext.preview_gateway}), not the regular BARK gateway. It only gets a short-lived sign-in, never your saved session, so the connection lasts up to an hour before you connect again.</>
                        )}
                    </>
                }
                items={connectContext?.scopes ?? []}
                confirmLabel="Connect"
                cancelLabel="Cancel"
                submitting={connectSubmitting || connectSwitching}
            />
            {connectError && (
                <div role="alert" className="fixed inset-x-0 top-0 z-50 flex justify-center p-3">
                    <p className="rounded-md border border-border-default bg-surface px-4 py-2 text-sm text-danger">
                        {connectError}
                        <button type="button" className="ml-3 inline-flex min-h-8 items-center px-1 underline" onClick={() => setConnectError(null)}>Dismiss</button>
                    </p>
                </div>
            )}
        </AuthProvider>
    );
}
